import En from '@/common/classes/En'
import {
  DEFAULT_TYPEID_FALLBACK,
  convertRowDataToFieldsForScript,
  resolveDocumentTypeId
} from '@/common/utils/rowDataUtils'
import {
  filterLiveOverridesToCurrentTilesRowPrefix,
  overlayTilesRowIdentityOnFieldMap,
} from '@/common/utils/tilesRowPanelData'
import { pushBreadcrumb, reportError } from '@/common/utils/errorReporter'
import { createPlatformActionParams } from '@/common/utils/requestSanitizer'
import {
  MAIN_FRAME_SLOT_ID,
  buildMainFramePageDataFromActionResponse,
} from '@/services/mainFrame/mainFrameMount'
import { coreService, createSafeClientData } from '@/services/core/core.service'
import { useDialog } from '@/common/composables/useDialog'
import { confirmPlatformAction } from '@/services/action/confirmPlatformAction'
import { purgeOrphanBodyDialogMasks } from '@/services/messages/dialog-manager'
import apiService from '@/services/api.service'
import { scriptService } from '@/services/script/script.service'
import {
  DOCUMENT_TRANSFER_TYPES,
  INITIAL_JS_RESULT_PROPERTIES,
  RETURN_TRANSFER_TYPE_CODES,
  type ActionContext,
  type ActionDependencies,
  type ExchangeData,
  type JsResultProperties,
  type StateData,
} from './types'
import {
  shouldContinueAfterPerformPreAction,
  shouldExecuteProcedureLoop,
} from './procedureLoopPolicy'
import { resolveActionPlatformSessionId } from './resolveActionPlatformSessionId'
import { shouldTriggerStage3DialogClose, shouldCloseDialogDespiteSetContextAbort } from './stage3DialogClosePolicy'
import {
  attachDialogCloseConfigToPageData,
  isDialogSlot,
  planDialogClosePhases,
  resolveDialogCloseConfigForSlot,
  resolveDialogCloseReloadFormOverlay,
  type DialogCloseListener,
} from './dialogCloseSlotContext'
import {
  invokeDialogCloseFromProperties,
  invokeCloseRootsFromProperties,
  emptyDialogCloseProperties,
  stampCloseRootsOpenedSlotId,
  readCloseRootsOpenedSlotId,
  type DialogCloseSlotResult,
} from './invokeDialogCloseFromProperties'
import { mapEnRuntimePropertiesToJsResult } from './mapEnRuntimePropertiesToJsResult'
import { normalizeProcedureExchangeData } from './normalizeProcedureExchangeData'
import {
  isEmptyActionScript,
  isNoPlatformTransferAction,
  resolveActionLayoutTextId,
  buildDialogClosedNReturnCloseOverrides,
  resolveOnCloseDialogWebClientExec,
  shouldExecuteActionScriptOnClient,
  shouldExecuteChainCalledOnClient,
  shouldExecuteOnClickOnClient,
} from './actionScriptRuntime'
import { computeExchangeFieldDelta } from './exchangeFieldDelta'
import { extractScriptRegistryElementNames } from '@/common/utils/formRootOnLoad'
import { MiddleExecutionRequiredError } from '@/services/parse/MiddleExecutionRequiredError'
import {
  extractActionTextValue,
  hasNormalizedActionTransfer,
  isUnsetActionTransferType,
  normalizeActionTransferValue,
} from './normalizeActionTransfer'
import type { ScriptData } from '@/components/platform/types'
import { isProxy, nextTick, ref, toRaw } from 'vue'
import { type ActionItem, useActions } from '@/common/composables'
import {
  fieldEntryHasPresentableValue,
  fieldEntryLooksEmptyIncoming,
  fieldHasRefillOrEmptyState,
  shouldPreserveFieldEntryOnEmptyIncomingForStates,
} from '@/common/utils/changedDataFieldMerge'
import {
  fieldParticipatesInTreeTableSearchCriteria,
  resolveTreeTableFieldNameFromPageFields,
} from '@/common/utils/treeTableSearch'
import { extractReloadElementsInstructions } from '@/common/utils/applyReloadElementsInstruction'
import { isElementScopedFieldKey } from '@/common/utils/elementScopedUniData'
import {
  resolveLayoutScopedClientFields,
  filterLayoutScopedFieldsToPanels,
  collectPagePanelIds,
  collectLayoutFieldNames,
} from '@/common/utils/layoutScopedClientFields'
import {
  collectPanelIdentificatorFieldNames,
  collectPanelIdentificatorGetDataFields,
  filterPanelGetDataSnapshotFields,
  pickIdentificatorFieldsFromSources,
  applyFormIdentificatorAndListboxGetDataToFields,
} from '@/common/utils/panelGetDataCollect'
import { mergePageDataFieldSnapshot, resolveJsResultChangedDataFieldMap } from '@/common/utils/resolveClientDataActualFields'
import { buildEnPageDataWithParentFormFunctions, readRootFormFunctions } from '@/common/utils/formRootOnLoad'
import {
  isDialogPageSlot,
  shouldDropStaleDialogOpenAfterClose,
  shouldSkipDocumentNavigationForSlot,
} from '@/common/utils/dialogPageSlot'
import {
  resolvePagesDataStoreKey,
} from '@/common/utils/resolvePageStoreEntry'
import {
  resolveSameWindowAuthoritativeFormId,
  resolveSameWindowOpenedFormId,
  resolveDialogSlotRegistryFormId,
  sameWindowResponseNeedsFullFormRefetch,
  sameWindowSchemaUnchangedOnPage,
} from '@/common/utils/sameWindowFormRefetch'
import {
  dispatchSameWindowReplace,
  resolveNewWindowPageSlotId,
  resolveSameWindowPageSlotId,
} from '@/common/utils/createWindowSlotId'
import {
  collectDescendantPageSlotIds,
  isIsolatedPageSlotId,
  resolveStableParentPageSlotId,
} from '@/common/utils/registryFormIdFromSlotId'
import { capturePageSlotSnapshotBeforeChildWindow } from '@/common/utils/pageSlotFieldSnapshot'
import { shouldUseAdjacentSplitPreview } from '@/common/utils/adjacentSplitView'
import { isDistinctPlatformSessionChange } from '@/common/utils/pageDataFieldMerge'
import {
  buildBoundDocumentIdentityFromRow,
  documentIdentityChangedAfterPreAction,
  documentIdentityChangedOnPage,
  overlayBoundDocumentIdentityOnFieldsUnlessPreserved,
  readExchangeDocumentIdentity,
  readPageBoundIdentityFieldMap,
  replaceDocumentIdentityFieldsFromSource,
  resolveBoundDocumentIdentityFromPage,
  resolveDocumentIdentityFromPageSnapshot,
  restoreDocumentIdentityFieldsAfterProcedureMerge,
  type BoundDocumentIdentity,
  type PreservePayloadDocumentIdentityContext,
} from '@/common/utils/documentTransferIdentity'
import { isDocumentContextAction } from '@/services/action/procedureLoopPolicy'
import { useDocumentTabsStore } from '@/stores/documentTabs'
import { mergePageFieldMaps, overlayAuthoritativeFieldKeys } from '@/common/utils/mergePageFieldMaps'
import { overlayRegistrySyncOptions } from '@/common/utils/formOverlayPageScope'
import { pickBooleanLiveFieldOverrides } from '@/common/utils/liveFormFieldOverrides'
import { applyFormErrorFromPayload } from '@/common/utils/formErrorStore'
import { ensureGetFileDataPayload } from './action.service.tmp'
import actionScript, {
  extractReloadElementNamesFromScript,
  scriptReloadsEntireForm,
} from '@/services/action/actionScript.ts'
import { applyWebClientFormOnLoadBeforeBuild } from '@/services/action/webClientFormOnLoadBeforeBuild'
import { applyExchangeDataToAppStoreSlot } from '@/services/action/applyChangedDataHelpers.ts'
import { applyIncomingExchangeStates } from '@/services/action/commitExchangeResult'
import { runStartupServerRootOnLoadIfNeeded } from '@/services/startup/runStartupServerRootOnLoad'
import { shouldRunRootFormOnLoadOnClientBeforeBuild } from '@/common/utils/formRootOnLoad'
import { normalizeExchangeStates } from '@/common/utils/normalizeExchangeStates'
import { overlayMiddleScriptUniDataOnClientDataFields } from '@/services/action/overlayMiddleScriptUniData.ts'
import { applySynthActionTableLeafCollect } from '@/common/utils/applyPutFormDataSynthCollect'
import { resolveTableLeafMetasFromElements } from '@/common/utils/tableLeafGetData'
import {
  coerceExchangeTypedFieldForPayload,
  mergeFieldMaps,
  serializeTypedFieldForExchange,
} from '@/common/utils/jsonUniData.ts'
import { resolveClientDataActualFields } from '@/common/utils/resolveClientDataActualFields'
import {
  getKnownTableSelections,
  sameRows,
  sanitizeCheckboxSelectionChangedData,
  stripInitialActionPayloadFields,
} from '@/services/action/actionUtils.ts'
import {
  collectCheckboxSelectionPayloadFieldNames,
  overlaySelectionPayloadFieldsOnExchangeData,
  preserveOnClickDocumentIdentity,
  preserveOnClickTableFieldsNotInSelectionPayload,
  preserveUploadedFileAttachmentsOnExchangeData,
  resolveTableElementIdForFieldName,
  shouldApplyCheckboxTableSelectionForTableField,
} from '@/services/action/checkboxTablePayloadPolicy.ts'
import { overlayFileAttachmentsPutFullDataOnExchangeData } from '@/services/action/collectFileAttachmentsPutFullData.ts'
import { isActionXmlVerifyEnabled } from '@/common/utils/actionXmlVerify'
import { verifyFormElements, resolveActionLayoutVerifyScope } from '@/common/utils/verifyHelper'
import { getPageIdentityAliases } from '@/common/utils/resolvePageIdentity'
import {
  augmentDialogCloseConfigMergeScope,
  buildDialogCloseConfigForStore,
  flushDialogClosePendingFieldOnChanges,
  inferDialogCloseMergeFieldKeysFromScript,
  inferDialogCloseSourceTableFieldsFromScript,
  isDialogCloseReloadOnlyScript,
  shouldSkipDialogCloseVisibilityReplay,
  extractDialogCloseSeedFieldMap,
  extractIncomingDataFieldMap,
  bindDialogCloseEnVisibilityControls,
  pickDialogClosedDispatchedVisibilities,
  resolveDialogCloseSourceFieldOverlay,
  stripEnDataToDialogCloseMergeAllowlist,
  stripDialogCloseSideEffectFieldsFromMergeResult,
} from '@/common/utils/dialogCloseParentMergeScope'
import {
  isolateAllSharedTableFieldValues,
  isolateSharedTableFieldAliases,
  normalizeDialogPageUniDataFieldAliases,
} from '@/common/utils/cloneTableValue'
import type { DialogClosePendingFieldOnChange } from '@/common/utils/dialogCloseParentMergeScope'
import {
  getDialogClosedScriptText,
} from '@/common/utils/dialogClosedScriptMeta'
import {
  findElementForDialogCloseParent,
  resolveDialogCloseParentContext,
  resolveParentSlotAtDialogOpen,
} from '@/common/utils/resolveDialogCloseParentStoreKey'
import {
  applyMultiSelectToClientDataFieldsFromRegistry,
  toMultiSelectTypedValue,
} from '@/common/utils/normalizeMultiselectTableValue'
import type { AppStore } from '@/stores/app.ts'
import { ScriptName, ScriptLevel } from '@/services/websocket/types.ts'
import { applyComboBoxSwingToClientDataFieldsFromRegistry, applyListboxWireTypesFromListboxFields } from '@/common/utils/comboBoxSwingFields'
import { enrichEditableTableUrlFieldsInClientDataFields } from '@/common/utils/editableTableUrlEnrichment'
import { collectLiveEditableTableOverrides } from '@/common/utils/editableTableLiveCollect'
import {
  fillEmptyUrlCellsFromSelfStringLists,
  sanitizeOutboundActionClientDataFields,
} from '@/common/utils/editableTableOutboundSanitize'
import { applyFileAttachmentsToClientDataFieldsFromRegistry, collectFileAttachmentFieldNames } from '@/common/utils/fileAttachmentsUniData'
import { applyCheckboxToClientDataFieldsFromRegistry, applyDateRangeToClientDataFieldsFromRegistry } from '@/common/utils/elementGetDataExport'
import { startWorkflowMeasure } from '@/common/utils/performanceTrace'
import { tGlobal } from '@/common/composables/useI18n'
import { progressBarService } from '@/services/progress/progress.service'
import {
  markTreeTableSearchActionSubmitted,
  shouldSubmitTreeTableSearch,
  hasPendingTreeTableSearchApply,
  resetTreeTableSearchApplyState,
  type TreeTableSearchElementsAccessor,
} from '@/common/utils/treeTableSearchSubmitted'
import {
  buildTabRoutedTreeTableSearchReloadPayload,
  ensureTabRoutedSearchClientFields,
  findTabRoutedSearchBranch,
  branchSetsTreeTableSearchFlag,
  getActionOnClickScript,
  isTabRoutedSearchAction,
  resolveTabRouteFieldFromContext,
  resolveTabRouteValue,
  resolveTreeTableNameForTabRouteBranch,
} from '@/services/action/tabRoutedSearchAction'
const en = ref<En>()
type EnConstructorOptions = ConstructorParameters<typeof En>[0]
type EnElements = EnConstructorOptions['elements']

export type { ActionContext } from './types'

/** Результат performPreAction; `continueAfterPreAction` = Swing boolean return (идти в doAction/окно). */
export type PerformPreActionResult = {
  ok: boolean
  continueAfterPreAction: boolean
  abortAction?: boolean
}

/**
 * Сервис выполнения actions платформы.
 * Обрабатывает onClick, chain/procedure, doAction, открытие форм, диалогов, документов.
 */
class ActionService {

  /** Извлекает значение из XML-подобного объекта (если есть _text) или возвращает исходное */
  private static extractTextValue(val: unknown): unknown {
    return extractActionTextValue(val)
  }

  /** Нормализует контекст: parentPageId, transfer, transferType, windowType */
  private normalizeContext(context: ActionContext, appStore: AppStore): void {
    const tabsStore = useDocumentTabsStore()
    const openTabIds = tabsStore.tabIdsOrdered
    const pagesData = (appStore?.pagesData ?? {}) as Record<string, unknown>

    if (
      !context.parentPageId &&
      appStore?.currentPageId &&
      appStore.currentPageId !== '' &&
      appStore.currentPageId !== 'home'
    ) {
      const cur = String(appStore.currentPageId)
      const stable = resolveStableParentPageSlotId(cur, pagesData, openTabIds)
      context.parentPageId =
        stable ??
        (isIsolatedPageSlotId(cur) ? tabsStore.rootTabId ?? undefined : cur)
    } else if (context.parentPageId) {
      const stable = resolveStableParentPageSlotId(
        context.parentPageId,
        pagesData,
        openTabIds,
      )
      if (stable) context.parentPageId = stable
    }
    const transferType = ActionService.extractTextValue(context.transferType)
    if (transferType !== context.transferType) {
      context.transferType = transferType as string
    }
    context.transfer = normalizeActionTransferValue(context.transfer, context.transferType)
    let windowType = ActionService.extractTextValue(context.windowType)
    if (typeof windowType === 'string') windowType = windowType.toUpperCase()
    if (windowType !== context.windowType) context.windowType = windowType as string
  }

  /** Возвращает ID WebSocket-соединения для вызова серверных скриптов или null. */
  private getSocketId(): string | null {
    return scriptService.getSocketId()
  }

  /** Извлекает тело скрипта chainCalled из контекста action (event.chainCalled). */
  private getChainCalledValue(context: ActionContext): string | undefined {
    return (
      this.getChainCalledData(context)?._text ||
      context.event?.chainCalled
    )
  }

  /** Возвращает метаданные chainCalled вне зависимости от плоской/вложенной структуры action. */
  private getChainCalledData(context: ActionContext): ScriptData | undefined {
    return (
      context.data?.event?.chainCalled ||
      context.data?.['event.chainCalled']
    ) as ScriptData | undefined
  }

  /** Подготовка actionItem для проверок needToExecuteProcedure, needToCallChain, needToCallDoAction, needToCloseModal. */
  private buildActionItem(context: ActionContext, normalizedTransferType: string): ActionItem {
    return {
      transferType: { _text: normalizedTransferType },
      confirm: context.confirm ? { _text: context.confirm } : undefined,
      'event.chainCalled': this.getChainCalledData(context) ?? this.getChainCalledValue(context),
    }
  }

  private resolveModalCloseTransferType(
    context: ActionContext,
    transferTypeValue?: unknown,
  ): string {
    const candidates = [
      transferTypeValue,
      context.transferType,
      extractActionTextValue(context.data?.transferType),
    ]
    for (const raw of candidates) {
      if (raw == null || String(raw).trim() === '') continue
      return String(raw).toLowerCase()
    }
    return ''
  }

  private shouldSkipDoActionResponseUiApply(
    context: ActionContext,
    dependencies?: ActionDependencies,
    transferTypeValue?: unknown,
  ): boolean {
    const actions = dependencies?.actions
    if (!actions?.needToCloseModal) return false
    const transferType = this.resolveModalCloseTransferType(context, transferTypeValue)
    if (!transferType) return false
    return actions.needToCloseModal(this.buildActionItem(context, transferType))
  }

  /**
   * Тип transfer «не задан»: нет платформенного doAction по transferType.
   */
  private isUnsetTransferType(transferType: unknown): boolean {
    return isUnsetActionTransferType(transferType)
  }

  private getActionLayoutMeta(context: ActionContext): { level: string; position: string } {
    const layout = context.data?.layout || {}
    const level = String(layout.level?._text || context.level || '').toLowerCase()
    const position = String(layout.position?._text || '').toLowerCase()
    return { level, position }
  }

  private shouldLimitToSelectedData(context: ActionContext): boolean {
    if ((context as any)?.limitToSelectedData && context.selectedData) {
      return true
    }
    const { level, position } = this.getActionLayoutMeta(context)
    return (
      level === 'element' &&
      (position === 'events' || position === 'inline') &&
      !!context.selectedData
    )
  }

  /** Inline/events: только selectedData + поля элемента (как Swing getInlineData). */
  private filterClientDataForLimitedSelection(
    clientDataFields: Record<string, any>,
    context: ActionContext,
    dependencies?: ActionDependencies,
  ): Record<string, any> {
    const selected = context.selectedData as Record<string, unknown> | undefined
    if (!selected || typeof selected !== 'object') {
      return clientDataFields
    }

    const allowed = new Set<string>()
    const selectedFields = selected.fields
    if (selectedFields && typeof selectedFields === 'object') {
      for (const key of Object.keys(selectedFields as object)) {
        allowed.add(key)
      }
    }
    for (const key of Object.keys(selected)) {
      if (key !== 'fields') {
        allowed.add(key)
      }
    }

    let elementFieldName = ''
    const elements = dependencies?.formProvider?.getElements?.()
    if (elements && context.elementId != null) {
      for (const [name, el] of Object.entries(elements)) {
        if (String((el as { elementId?: unknown }).elementId) === String(context.elementId)) {
          elementFieldName = name
          break
        }
      }
    }

    const filtered: Record<string, any> = {}
    for (const [key, val] of Object.entries(clientDataFields)) {
      if (allowed.has(key)) {
        filtered[key] = val
        continue
      }
      if (elementFieldName && isElementScopedFieldKey(key, elementFieldName)) {
        filtered[key] = val
      }
    }
    return filtered
  }

  /** Собирает данные родительского элемента по уровню (element/panel/form/page) для clientData. */
  private getParentElementData(
    context: ActionContext,
    dependencies?: ActionDependencies,
  ): Record<string, any> {
    const getForm = dependencies?.getForm || (() => ({}))
    let parentData: Record<string, any> = {}
    const contextFormId = context.formId ? parseInt(context.formId) : 0

    // Determine the actual level from action layout if available, otherwise use context level
    const layout = context.data?.layout || {}
    let actualLevel = layout.level?._text || context.level || 'form'
    const actionPosition = String(layout.position?._text || '').toLowerCase()

    // Normalize level to lowercase for comparison
    if (typeof actualLevel === 'string') {
      actualLevel = actualLevel.toLowerCase()
    }

    const layoutId = layout.id?._text || layout.id

    // For form-level actions, use layoutId (the form ID from action definition) as the target formId
    const targetFormId = actualLevel === 'form' && layoutId ? parseInt(layoutId) : contextFormId

    switch (actualLevel) {
      case 'element':
        if (context.elementId || layoutId) {
          const elementId = layoutId || context.elementId
          const elementResult = getForm({
            formId: targetFormId,
            panelId: context.panelId,
            elementId,
            flatten: false,
            tilesRowScope: context.tilesRowScope,
          })
          if (actionPosition === 'events') {
            parentData = elementResult || {}
          } else {
            // Скрипт на сервере ожидает поля по имени элемента (например en.getData().getFieldAsTable("outgoingReestrTable")).
            // Подмешиваем данные панели (в т.ч. таблицу под именем элемента), чтобы они точно попали в запрос.
            const panelData = getForm({
              formId: targetFormId,
              panelId: context.panelId,
              flatten: false,
              tilesRowScope: context.tilesRowScope,
            }) || {}
            parentData = { ...panelData, ...(elementResult || {}) }
          }
        }
        break
      case 'panel':
        if (layoutId || context.panelId) {
          const panelId = layoutId || context.panelId
          const panelResult = getForm({
            formId: targetFormId,
            panelId,
            flatten: false,
            tilesRowScope: context.tilesRowScope,
          })
          parentData = panelResult || {}
        } else {
          const panelFallbackResult = getForm({ formId: targetFormId, flatten: false })
          parentData = panelFallbackResult || {}
        }
        break
      case 'form':
        const formResult = getForm({ formId: targetFormId, flatten: false })
        parentData = formResult || {}
        break
      case 'page':
        // For page-level actions, use getPageData to collect data only from panels on the specific page
        const getPageData = dependencies?.getPageData || (() => ({}))
        const pageId = layoutId || context.pageId
        if (pageId) {
          const pageResult = getPageData({ formId: targetFormId, pageId, flatten: false })
          parentData = pageResult || {}
        } else {
          // Fallback to form data if pageId not available
          const pageFallbackResult = getForm({ formId: targetFormId, flatten: false })
          parentData = pageFallbackResult || {}
        }
        break
      default:
        const defaultResult = getForm({ formId: targetFormId, flatten: false })
        parentData = defaultResult || {}
    }

    // Для isContext="none": удаляем document identity из parentData только для ADD режима.
    // Проблема: различить ADD (создание новой записи) и EDIT (редактирование существующей).
    // - ADD: selectedData отсутствует, parentData._id = ID родительского документа (наследуется от родителя)
    // - EDIT: selectedData отсутствует (при вызове chain), но parentData._id = ID редактируемой записи
    //
    // Решение: проверяем $wasOpenedWithSelection в pageData диалога.
    // При открытии диалога через EDIT, attachBoundDocumentIdentityToPagePayload устанавливает этот флаг.
    // См. /dev/contracts/isContext-and-document-identity.md
    const pages = dependencies?.appStore?.pagesData as Record<string, any> | undefined
    const pageData = dependencies?.pageData || (context?.formId && pages
      ? pages[String(context.formId)] ?? pages[Number(context.formId)]
      : undefined)
    const wasOpenedWithSelection = (pageData as any)?.$wasOpenedWithSelection === true

    const hasSelection = Boolean(
      context.selectedData ||
      context.tilesRowScope?.getRowData?.() ||
      wasOpenedWithSelection
    )

    if (!isDocumentContextAction(context) && !hasSelection) {
      delete parentData._id
      delete parentData._typeId
      delete parentData._versionId
    }

    return parentData
  }

  /** True, если selectedData пришёл из контекстного меню таблицы (есть поля *__rowNum / *__column). */
  private isTableContextMenuSelection(selectedData: unknown): boolean {
    if (!selectedData || typeof selectedData !== 'object') return false
    const keys = Object.keys(selectedData as Record<string, unknown>)
    return keys.some(
      (k) => k.endsWith('__rowNum') || k.endsWith('__column'),
    )
  }

  /** Извлекает обычное значение из Vue ref, чтобы в clientData не попадали реактивные объекты. */
  private unwrapRef(val: unknown): unknown {
    if (val != null && typeof val === 'object' && (val as any).__v_isRef === true) {
      return this.unwrapRef((val as any).value)
    }
    return val
  }

  /** Возвращает безопасное значение по умолчанию для typed-полей без value. */
  private getDefaultValueForFieldType(type?: unknown): unknown {
    const normalizedType = typeof type === 'string' ? type : String(type ?? '')
    switch (normalizedType) {
      case 'UniDataTable':
      case 'UniDataTableFields':
        return { rows: [] }
      case 'Boolean':
        return false
      case 'Long':
      case 'Double':
      case 'BigDecimal':
      case 'Integer':
        return 0
      default:
        return ''
    }
  }

  /** Нормализует значение до примитива: если объект с полем value — берёт value (чтобы в payload не уходило value: { type: "String" }). */
  private toPrimitiveValue(val: unknown): unknown {
    const v = this.unwrapRef(val)
    if (v != null && typeof v === 'object') {
      const typedValue = v as any
      if ('value' in typedValue) {
        if (typeof typedValue.value === 'undefined') {
          return this.getDefaultValueForFieldType(typedValue.type)
        }
        return this.toPrimitiveValue(typedValue.value)
      }
      if ('type' in typedValue) {
        return this.getDefaultValueForFieldType(typedValue.type)
      }
    }
    return v
  }

  private resolveFreshPageDataForAction(
    dependencies?: ActionDependencies,
    formId?: string | number | null,
  ): Record<string, unknown> | null | undefined {
    const pages = dependencies?.appStore?.pagesData as Record<string, any> | undefined
    const resolvedFormId = formId ?? dependencies?.pageRegistryFormId ?? dependencies?.pageData?.formId
    if (pages && (dependencies?.pageData || resolvedFormId)) {
      const pageStoreKey = resolvePagesDataStoreKey(pages, {
        pageDataSlot: dependencies?.pageDataSlot,
        pageDataRef: dependencies?.pageData,
        registryFormId: dependencies?.pageRegistryFormId ?? resolvedFormId,
        formId: resolvedFormId,
        currentPageId: dependencies?.appStore?.currentPageId,
        topDialogId: dependencies?.appStore?.dialogIds?.length
          ? String(dependencies.appStore.dialogIds[dependencies.appStore.dialogIds.length - 1] ?? '')
          : '',
        windowInstanceId: dependencies?.pageWindowInstanceId,
      })
      if (pageStoreKey && pages[pageStoreKey]) {
        return pages[pageStoreKey]
      }
    }
    return dependencies?.pageData as Record<string, unknown> | null | undefined
  }

  private getPageFieldsForTreeTableSearch(
    dependencies?: ActionDependencies,
    formId?: string | number | null,
  ): Record<string, unknown> {
    const pageData = this.resolveFreshPageDataForAction(dependencies, formId)
    if (!pageData) return {}
    return overlayAuthoritativeFieldKeys(
      mergePageDataFieldSnapshot(pageData),
      resolveJsResultChangedDataFieldMap((pageData as { jsResult?: { changed_data?: unknown } }).jsResult?.changed_data),
    )
  }

  private getMergedFieldsForDoLoadSearch(
    context: ActionContext,
    dependencies?: ActionDependencies,
  ): Record<string, unknown> {
    const pageFields = this.getPageFieldsForTreeTableSearch(dependencies, context.formId)
    const fullFormData = dependencies?.getForm?.({ formId: context.formId })
    if (fullFormData && typeof fullFormData === 'object' && Object.keys(fullFormData).length > 0) {
      return mergeFieldMaps(pageFields, fullFormData)
    }
    return pageFields
  }

  private getLiveSearchString(
    formId: string | number,
    dependencies?: ActionDependencies,
    context?: ActionContext,
  ): unknown {
    const pageFields = this.getPageFieldsForTreeTableSearch(dependencies, formId)
    const tableName = resolveTreeTableFieldNameFromPageFields(pageFields)
    const live = dependencies?.formProvider?.collectLiveFieldOverrides?.(formId)
    if (context?.middleScriptUniData && tableName) {
      const snapshot = resolveClientDataActualFields(context.middleScriptUniData)
      for (const key of Object.keys(snapshot)) {
        if (!fieldParticipatesInTreeTableSearchCriteria(key, pageFields, tableName, { liveOverrides: live })) {
          continue
        }
        const field = snapshot[key]
        if (field && typeof field === 'object' && 'value' in field) {
          return (field as { value?: unknown }).value
        }
      }
    }
    const hostEn = context?.nestedHostEn
    if (hostEn) {
      if (!tableName) {
        const pageFieldsForEn = this.getPageFieldsForTreeTableSearch(dependencies, formId)
        const tableNameForEn = resolveTreeTableFieldNameFromPageFields(pageFieldsForEn)
        if (tableNameForEn) {
          for (const key of Object.keys(hostEn.getData()._getAllFields())) {
            if (!fieldParticipatesInTreeTableSearchCriteria(key, pageFieldsForEn, tableNameForEn, { liveOverrides: live })) {
              continue
            }
            return hostEn.getData().getField(key)?.value
          }
        }
      } else {
        for (const key of Object.keys(hostEn.getData()._getAllFields())) {
          if (!fieldParticipatesInTreeTableSearchCriteria(key, pageFields, tableName, { liveOverrides: live })) {
            continue
          }
          return hostEn.getData().getField(key)?.value
        }
      }
    }
    if (live) {
      for (const [key, field] of Object.entries(live)) {
        if (!field || typeof field !== 'object') continue
        if (key.includes('__')) continue
        const value = (field as { value?: unknown }).value
        const text = String(value ?? '').trim()
        if (text !== '') return value
      }
    }
    return undefined
  }

  private getTreeTableSearchElementsAccessor(
    dependencies?: ActionDependencies,
  ): TreeTableSearchElementsAccessor | undefined {
    const getElements = dependencies?.formProvider?.getElements
    if (typeof getElements !== 'function') return undefined
    return getElements as TreeTableSearchElementsAccessor
  }

  private markTreeTableSearchIfNeeded(
    context: ActionContext,
    dependencies?: ActionDependencies,
  ): void {
    if (context.formId == null) return
    const pageFields = this.getPageFieldsForTreeTableSearch(dependencies, context.formId)
    const mergedFields = this.getMergedFieldsForDoLoadSearch(context, dependencies)
    const searchString = this.getLiveSearchString(context.formId, dependencies, context)
    const getElements = this.getTreeTableSearchElementsAccessor(dependencies)
    const pageData = dependencies?.pageData as Record<string, unknown> | null | undefined
    if (
      !shouldSubmitTreeTableSearch(context, pageFields, {
        searchString,
        getElements,
        pageData,
        mergedFields,
        dependencies,
      })
    ) {
      return
    }
    const script = getActionOnClickScript(context)
    const routeValue = resolveTabRouteValue(pageFields, context, pageData, mergedFields, dependencies)
    const liveOverrides = dependencies?.formProvider?.collectLiveFieldOverrides?.(context.formId)
    markTreeTableSearchActionSubmitted(context.formId, pageFields, liveOverrides, getElements, {
      actionScript: script,
      routeValue,
      getElementsForRoute: getElements,
    })
  }

  private async flushPendingTreeTableSearchIfNeeded(
    context: ActionContext,
    dependencies?: ActionDependencies,
  ): Promise<void> {
    if (context.formId == null) return
    const pageFields = this.getPageFieldsForTreeTableSearch(dependencies, context.formId)
    const mergedFields = this.getMergedFieldsForDoLoadSearch(context, dependencies)
    const pageData = dependencies?.pageData as Record<string, unknown> | null | undefined
    if (
      !shouldSubmitTreeTableSearch(context, pageFields, {
        searchString: this.getLiveSearchString(context.formId, dependencies, context),
        getElements: this.getTreeTableSearchElementsAccessor(dependencies),
        pageData,
        mergedFields,
      })
    ) {
      return
    }
    await nextTick()
    await nextTick()
    await dependencies?.formProvider?.applyPendingTreeTableSearch?.(context.formId)
  }

  private resolveActionDisplayName(context: ActionContext): string {
    const fromDesc = context.data?.description?._text ?? context.data?.description
    if (typeof fromDesc === 'string' && fromDesc.trim()) return fromDesc.trim()
    if (context.name?.trim()) return context.name.trim()
    return 'No name'
  }


  private async reloadTabRoutedTreeTableSearchIfNeeded(
    context: ActionContext,
    dependencies: ActionDependencies | undefined,
    clientDataRef: { value: ExchangeData },
  ): Promise<void> {
    if (context.formId == null) return

    const pageFields = this.getPageFieldsForTreeTableSearch(dependencies, context.formId)
    const mergedFields = this.getMergedFieldsForDoLoadSearch(context, dependencies)
    const searchString = this.getLiveSearchString(context.formId, dependencies, context)
    const getElements = this.getTreeTableSearchElementsAccessor(dependencies)
    const pageData = dependencies?.pageData as Record<string, unknown> | null | undefined

    if (
      !shouldSubmitTreeTableSearch(context, pageFields, {
        searchString,
        getElements,
        pageData,
        mergedFields,
        dependencies,
      })
    ) {
      return
    }

    const script = getActionOnClickScript(context)
    if (!isTabRoutedSearchAction(script)) return

    const routeValue = resolveTabRouteValue(
      pageFields,
      context,
      pageData,
      mergedFields,
      dependencies,
    )
    const branch = findTabRoutedSearchBranch(script, routeValue)
    if (!branch || !branchSetsTreeTableSearchFlag(branch)) return

    const tableName = resolveTreeTableNameForTabRouteBranch(script, routeValue, dependencies)
    if (!tableName) return

    const reloadElement = dependencies?.reloadElement
    if (typeof reloadElement !== 'function') return

    const routeField = resolveTabRouteFieldFromContext(context)
    const payload = buildTabRoutedTreeTableSearchReloadPayload(
      branch,
      tableName,
      searchString,
      dependencies,
      routeField ?? undefined,
      routeValue ?? undefined,
    )

    await reloadElement(tableName, payload, {
      formId: context.formId,
      panelId: context.panelId,
      skipPageFocusGuard: true,
      source: 'tabRoutedTreeTableSearch',
    })
  }

  /**
   * Middle возвращает в `changed_data.fields.fields.reload__elements` таблицу
   * `[{level:'element'|'panel'|'page', id}]`. Без этого действия в webui state `is_refill`
   * применяется на устаревшие данные (например, фильтр вернул 0 строк,
   * но клиент оставляет старые 30 rows в таблице).
   */
  private async processReloadElementsInstructions(
    context: ActionContext,
    dependencies: ActionDependencies | undefined,
  ): Promise<void> {
    if (context.formId == null) return
    const reload = dependencies?.formProvider?.reloadElement
    if (typeof reload !== 'function') return
    const slotId = actionScript.resolvePagesDataSlotId(context, dependencies)
    if (!slotId) return
    const pageData = dependencies?.appStore?.pagesData?.[slotId] as
      | { jsResult?: { changed_data?: unknown } }
      | undefined
    const instructions = extractReloadElementsInstructions(pageData?.jsResult?.changed_data)
    if (instructions.length === 0) return
    const getElements = dependencies?.formProvider?.getElements
    const elementsById = new Map<number, string>()
    if (typeof getElements === 'function') {
      for (const [name, el] of Object.entries(getElements() ?? {})) {
        const idRaw = (el as { elementId?: unknown })?.elementId
        const idNum = Number(idRaw)
        if (Number.isFinite(idNum)) elementsById.set(idNum, name)
      }
    }
    for (const instr of instructions) {
      if (instr.level !== 'element') continue
      const name = elementsById.get(instr.id)
      if (!name) continue
      try {
        await reload(name, {}, {
          formId: context.formId,
          source: 'reload__elements',
        })
      } catch (err) {
        console.warn('[reload__elements] failed for element', name, err)
      }
    }
  }

  private async finalizeTreeTableSearchAfterOnClick(
    context: ActionContext,
    dependencies: ActionDependencies | undefined,
    clientDataRef: { value: ExchangeData },
    onClickRanOnClient: boolean,
  ): Promise<void> {
    await this.processReloadElementsInstructions(context, dependencies)
    // states применяются в actionScript.processOnClickServerResponse -> commitExchangeResult
    // Middle onClick (4367 и др.) уже вызывает en.reloadElement на сервере — второй client reload ломает поиск и expand.
    if (onClickRanOnClient) {
      await this.reloadTabRoutedTreeTableSearchIfNeeded(context, dependencies, clientDataRef)
    }
    await this.flushPendingTreeTableSearchIfNeeded(context, dependencies)
    if (
      !onClickRanOnClient &&
      context.formId != null &&
      hasPendingTreeTableSearchApply(context.formId)
    ) {
      await this.reloadTabRoutedTreeTableSearchIfNeeded(context, dependencies, clientDataRef)
      await this.flushPendingTreeTableSearchIfNeeded(context, dependencies)
    }
  }

  /**
   * Создаёт clientData (UniData) из parentData и selectedData для отправки на сервер.
   *
   * Контракт Swing (`Action.doAction` → `parent.getData`): только layout-контейнер.
   * Всю форму не берем! (см. dev/contracts/action.md).
   */
  private createClientDataUniData(
    context: ActionContext,
    dependencies?: ActionDependencies,
  ): ExchangeData {
    let parentData = this.getParentElementData(context, dependencies)
    const selectionPayloadFieldNames = new Set<string>()
    const limitToSelectedData = this.shouldLimitToSelectedData(context)
    const { level: layoutLevel } = this.getActionLayoutMeta(context)
    const isTilesCloneAction = Boolean(context.tilesRowScope)
    // Для isContext="none" page snapshot не включается — такие действия работают с subordinate data,
    // а не с document context. Соответствует Swing: getData(action) собирает только layout container.
    // См. /dev/contracts/isContext-and-document-identity.md
    const mayIncludePageSnapshot =
      !limitToSelectedData &&
      !isTilesCloneAction &&
      (layoutLevel === 'form' || layoutLevel === 'panel' || layoutLevel === 'page') &&
      isDocumentContextAction(context)

    if (limitToSelectedData) {
      parentData = {}
    }

    const pages = dependencies?.appStore?.pagesData as Record<string, any> | undefined
    const pageStoreKey =
      pages && (dependencies?.pageData || context?.formId)
        ? resolvePagesDataStoreKey(pages, {
            pageDataSlot: dependencies?.pageDataSlot,
            pageDataRef: dependencies?.pageData,
            registryFormId: dependencies?.pageRegistryFormId ?? context?.formId,
            formId: context?.formId,
            currentPageId: dependencies?.appStore?.currentPageId,
            topDialogId: dependencies?.appStore?.dialogIds?.length
              ? String(dependencies.appStore.dialogIds[dependencies.appStore.dialogIds.length - 1] ?? '')
              : '',
            windowInstanceId: dependencies?.pageWindowInstanceId,
          })
        : ''
    const pageData =
      dependencies?.pageData ||
      (pageStoreKey && pages ? pages[pageStoreKey] : undefined) ||
      (context?.formId && pages
        ? pages[String(context.formId)] ?? pages[Number(context.formId)]
        : undefined)
    const formRoot = (pageData as Record<string, unknown> | undefined)?.form
    const pageFields = (() => {
      if (!pageData || !mayIncludePageSnapshot) {
        return {} as Record<string, unknown>
      }
      const allScoped = resolveLayoutScopedClientFields(pageData, formRoot)
      if (layoutLevel === 'form') {
        return allScoped
      }
      if (layoutLevel === 'panel') {
        const panelId =
          resolveActionLayoutTextId(context.data?.layout) || String(context.panelId ?? '')
        if (!panelId) {
          return {} as Record<string, unknown>
        }
        return filterPanelGetDataSnapshotFields(allScoped, formRoot, new Set([panelId]), {
          elements:
            typeof dependencies?.formProvider?.getElements === 'function'
              ? dependencies.formProvider.getElements()
              : {},
          shellVisibleByElementId: dependencies?.formProvider?.elementShellVisibleByElementId,
        })
      }
      if (layoutLevel === 'page') {
        const pageId =
          resolveActionLayoutTextId(context.data?.layout) || String(context.pageId ?? '')
        if (!pageId) {
          return {} as Record<string, unknown>
        }
        return filterLayoutScopedFieldsToPanels(
          allScoped,
          formRoot,
          collectPagePanelIds(formRoot, pageId),
        )
      }
      return {} as Record<string, unknown>
    })()

    if (mayIncludePageSnapshot) {
      if (pageFields && typeof pageFields === 'object' && Object.keys(pageFields).length > 0) {
        parentData = mergeFieldMaps(pageFields, parentData)
      }
      if (layoutLevel === 'form') {
        const fullFormData = dependencies?.getForm?.({ formId: context.formId })
        if (fullFormData && typeof fullFormData === 'object' && Object.keys(fullFormData).length > 0) {
          parentData = mergeFieldMaps(parentData, fullFormData)
        }
        const elements =
          typeof dependencies?.formProvider?.getElements === 'function'
            ? dependencies.formProvider.getElements()
            : {}
        applyFormIdentificatorAndListboxGetDataToFields(parentData, elements, {
          formId: context.formId,
          formRoot,
          extraMaps: [
            typeof dependencies?.formProvider?.getElementsByKey === 'function'
              ? dependencies.formProvider.getElementsByKey()
              : undefined,
          ],
        })
      }
      if (layoutLevel === 'panel') {
        const panelId =
          resolveActionLayoutTextId(context.data?.layout) || String(context.panelId ?? '')
        if (panelId) {
          const elements =
            typeof dependencies?.formProvider?.getElements === 'function'
              ? dependencies.formProvider.getElements()
              : {}
          const panelIds = new Set([panelId])
          const identificatorNames = collectPanelIdentificatorFieldNames(
            formRoot,
            panelIds,
            elements,
          )
          if (identificatorNames.size === 0) {
            identificatorNames.add('_id')
            identificatorNames.add('_versionId')
          }
          parentData = mergeFieldMaps(
            parentData,
            pickIdentificatorFieldsFromSources(identificatorNames, [
              collectPanelIdentificatorGetDataFields(elements, { panelId }),
              mergePageDataFieldSnapshot(pageData),
              readPageBoundIdentityFieldMap(pageData),
            ]),
          )
          if (formRoot) {
            parentData = filterPanelGetDataSnapshotFields(
              parentData,
              formRoot,
              new Set([panelId]),
              {
                elements,
                shellVisibleByElementId: dependencies?.formProvider?.elementShellVisibleByElementId,
              },
            )
          }
        }
      }
    }

    // Leaf getData: checkbox (TABLE/EDITABLE/GROUPTABLE) → checked; EDITABLE без cb → all; TABLE без cb → omit; TREETABLE → checked.
    // Не применять, если action на этой таблице и position≠top (INLINE / full — как shouldApplyCheckbox…).
    if (!limitToSelectedData && !isTilesCloneAction && context.formId != null) {
      const tableSelections = getKnownTableSelections(context, dependencies)
      const elementsRegistry =
        typeof dependencies?.formProvider?.getElements === 'function'
          ? dependencies.formProvider.getElements()
          : {}
      const extraMaps = [
        typeof dependencies?.formProvider?.getElementsByKey === 'function'
          ? dependencies.formProvider.getElementsByKey()
          : undefined,
      ]
      const tableMetas = resolveTableLeafMetasFromElements(
        elementsRegistry as Record<string, unknown>,
        context.formId,
        extraMaps,
      )
      for (const fieldName of collectCheckboxSelectionPayloadFieldNames(tableMetas, tableSelections)) {
        const fromMeta = tableMetas.find((meta) => meta.fieldName === fieldName)
        const tableElementId =
          fromMeta?.elementId ?? resolveTableElementIdForFieldName(fieldName, context, dependencies)
        if (!shouldApplyCheckboxTableSelectionForTableField(context, tableElementId)) continue
        selectionPayloadFieldNames.add(fieldName)
      }
      parentData = applySynthActionTableLeafCollect(parentData, {
        elements: elementsRegistry as Record<string, unknown>,
        extraMaps,
        formId: context.formId,
        selections: tableSelections,
        shouldRewriteField: (meta) =>
          shouldApplyCheckboxTableSelectionForTableField(context, meta.elementId),
      })
    }

    ;(context as any).__selectionPayloadFieldNames = Array.from(selectionPayloadFieldNames)

    // Контекстное меню таблицы: в clientData только данные выбранной строки + tableName__rowNum, tableName__column
    const isTableContextMenu = this.isTableContextMenuSelection(context.selectedData)

    // Создаем структуру clientData с использованием createSafeClientData
    const clientDataFields: Record<string, any> = {}

    if (!isTableContextMenu) {
      // Преобразуем данные родительского элемента в формат для clientData
      Object.keys(parentData).forEach((key) => {
        const value = parentData[key]
        if (value && typeof value === 'object' && 'value' in value && 'type' in value) {
          clientDataFields[key] = {
            value: this.toPrimitiveValue((value as any).value),
            type: (value as any).type || 'String',
          }
        } else {
          // Если значение не в формате {value, type}, создаем его
          clientDataFields[key] = {
            value: value,
            type:
              typeof value === 'string'
                ? 'String'
                : typeof value === 'number'
                  ? 'Long'
                  : typeof value === 'boolean'
                    ? 'Boolean'
                    : 'String',
          }
        }
      })
    }

    // Overlay selectedData поверх layout-среза.
    // Tiles клон панели (tilesRowScope): Swing — только panel.getData клона после fill; PK строки.
    // Tiles INLINE на элементе (без tilesRowScope): полные скаляры строки UniDataTable + rowNum.
    // Form/panel/page: колонки строки не копировать в корень (WUI-507). Flatten — только getInlineData
    // (limitToSelectedData: inline / context menu / events).
    if (context.selectedData && typeof context.selectedData === 'object') {
      const selectedFieldsSource = (context.selectedData as any).fields
      const overlaySelectedRowCells = !isTilesCloneAction && limitToSelectedData
      if (overlaySelectedRowCells) {
        let addedFromSelectedFields = false
        if (selectedFieldsSource && typeof selectedFieldsSource === 'object') {
          Object.keys(selectedFieldsSource).forEach((key) => {
            const field = selectedFieldsSource[key]
            const rawValue = field?.value !== undefined ? field.value : field
            const value = this.toPrimitiveValue(rawValue)
            const type =
              field && typeof field === 'object' && 'type' in field
                ? (field as any).type
                : typeof value === 'string'
                  ? 'String'
                  : typeof value === 'number'
                    ? 'Long'
                    : typeof value === 'boolean'
                      ? 'Boolean'
                      : 'String'
            clientDataFields[key] = { value, type }
            addedFromSelectedFields = true
          })
        }

        if (!addedFromSelectedFields) {
          const converted = this.convertRowDataToFields(context.selectedData)
          Object.assign(clientDataFields, converted)
        }
      }
      // Собираем _id, _typeId, _versionId (только примитивы, чтобы в payload не уходило value: { type: "String" })
      const row = context.selectedData as any
      const addField = (k: string) => {
        if (k in clientDataFields) return
        const src = row[k] ?? selectedFieldsSource?.[k]
        if (src === undefined || src === null) return
        const v = this.toPrimitiveValue(src?.value !== undefined ? src.value : src)
        clientDataFields[k] = {
          value: v,
          type: typeof v === 'string' ? 'String' : typeof v === 'number' ? 'Long' : 'String'
        }
      }
      if (overlaySelectedRowCells || isTilesCloneAction) {
        addField('_id')
        if (!limitToSelectedData) {
          addField('_typeId')
          addField('_versionId')
          const currentTypeId = (clientDataFields._typeId?.value ?? this.toPrimitiveValue(row?._typeId) ?? row?.typeId)?.toString?.() ?? ''
          if (DEFAULT_TYPEID_FALLBACK.placeholderValues.includes(currentTypeId)) {
            const resolved = resolveDocumentTypeId(row, DEFAULT_TYPEID_FALLBACK)
            if (resolved && resolved !== currentTypeId) {
              clientDataFields._typeId = { value: resolved, type: 'String' }
            }
          }
        }
      }

      if (overlaySelectedRowCells) {
        Object.keys(context.selectedData as Record<string, unknown>).forEach((key) => {
          if (key === 'fields' || key in clientDataFields) return
          const v = this.toPrimitiveValue((context.selectedData as any)[key])
          if (v !== undefined && v !== null) {
            const isPrimitive =
              typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
            if (isPrimitive) {
              clientDataFields[key] = {
                value: v,
                type:
                  typeof v === 'string' ? 'String' : typeof v === 'number' ? 'Long' : 'Boolean',
              }
            }
          }
        })
        const fromRow = convertRowDataToFieldsForScript(context.selectedData)
        for (const [key, field] of Object.entries(fromRow)) {
          if (!(key in clientDataFields)) {
            clientDataFields[key] = field
          }
        }
      }
    }

    if (isTilesCloneAction) {
      Object.assign(
        clientDataFields,
        overlayTilesRowIdentityOnFieldMap({}, [
          context.selectedData,
          context.tilesRowScope?.getRowData?.(),
        ]),
      )
    }

    const documentViewFromAction = (context.data as any)?.documentView?._text ?? (context.data as any)?.documentView
    const isDocTransferFromList = context.doActionForAdjacentWindow && this.isDocumentTransferType((context.transferType as any) ?? '')
    const documentView = typeof documentViewFromAction === 'string' && documentViewFromAction
      ? documentViewFromAction
      : (isDocTransferFromList ? 'main' : null)
    const extraParams = documentView ? { documentView } : {}
    if (documentView && clientDataFields) {
      (clientDataFields as Record<string, unknown>)._documentView = { value: documentView, type: 'String' }
    }

    // TODO: Устаревшее псевдо-поле (раньше properties были в changed_data); не отправляем на middle.
    delete (clientDataFields as Record<string, unknown>).__js_result_properties

    if (!limitToSelectedData && !isTilesCloneAction) {
      applyComboBoxSwingToClientDataFieldsFromRegistry(
        clientDataFields,
        parentData,
        typeof dependencies?.formProvider?.getElements === 'function'
          ? dependencies.formProvider.getElements()
          : undefined,
      )
      applyListboxWireTypesFromListboxFields(clientDataFields)
      applyMultiSelectToClientDataFieldsFromRegistry(
        clientDataFields,
        parentData,
        typeof dependencies?.formProvider?.getElements === 'function'
          ? dependencies.formProvider.getElements()
          : undefined,
      )
      // FILEATTACHMENTS в clientData action: всегда UniDataTable; formRoot покрывает скрытые FA.
      applyFileAttachmentsToClientDataFieldsFromRegistry(
        clientDataFields,
        parentData,
        typeof dependencies?.formProvider?.getElements === 'function'
          ? dependencies.formProvider.getElements()
          : undefined,
        { formRoot: (pageData as { form?: unknown } | undefined)?.form },
      )
      applyCheckboxToClientDataFieldsFromRegistry(
        clientDataFields,
        parentData,
        typeof dependencies?.formProvider?.getElements === 'function'
          ? dependencies.formProvider.getElements()
          : undefined,
        dependencies?.getForm,
      )
      // DateRange → __first/__second для скриптов/reload (как SwingDateRange.getData).
      applyDateRangeToClientDataFieldsFromRegistry(
        clientDataFields,
        parentData,
        typeof dependencies?.formProvider?.getElements === 'function'
          ? dependencies.formProvider.getElements()
          : undefined,
        dependencies?.getForm,
      )
    }

    const collectedLiveOverrides = dependencies?.formProvider?.collectLiveFieldOverrides?.(
      context.formId,
      {
        pageDataSlot:
          dependencies?.pageDataSlot ??
          actionScript.resolvePagesDataSlotId(context, dependencies),
      },
    )
    // EdiTable cell onChange uses limitToSelectedData — still merge BOOLEAN/CHECKBOX live
    // clicks that race with cell blur.
    let liveOverrides = limitToSelectedData
      ? pickBooleanLiveFieldOverrides(collectedLiveOverrides)
      : collectedLiveOverrides
    if (isTilesCloneAction && liveOverrides && context.tilesRowScope) {
      liveOverrides = filterLiveOverridesToCurrentTilesRowPrefix(
        liveOverrides as Record<string, unknown>,
        context.tilesRowScope,
      ) as typeof liveOverrides
    }
    if (liveOverrides && typeof liveOverrides === 'object') {
      for (const [key, field] of Object.entries(liveOverrides)) {
        if (!field || typeof field !== 'object') continue
        clientDataFields[key] = {
          value: this.toPrimitiveValue((field as { value?: unknown }).value),
          type: (field as { type?: string }).type || 'String',
        }
      }
      const liveSearch = liveOverrides.searchString
      const liveSearchValue =
        liveSearch && typeof liveSearch === 'object'
          ? (liveSearch as { value?: unknown }).value
          : undefined
      if (liveSearchValue === '' || liveSearchValue == null) {
        clientDataFields.searchString = { value: '', type: 'String' }
        const clearSearchFlag = (fieldKey: string) => {
          clientDataFields[fieldKey] = { value: false, type: 'Boolean' }
        }
        for (const key of Object.keys(clientDataFields)) {
          if (key.endsWith('__search')) {
            clearSearchFlag(key)
          }
        }
        for (const key of Object.keys(parentData)) {
          if (key.endsWith('__search') && !(key in clientDataFields)) {
            clearSearchFlag(key)
          }
        }
      }
    }

    if (!limitToSelectedData && !isTilesCloneAction && context.formId != null) {
      const elementsRegistryForEditable =
        typeof dependencies?.formProvider?.getElements === 'function'
          ? dependencies.formProvider.getElements()
          : {}
      const getFormSnapshot =
        typeof dependencies?.getForm === 'function'
          ? (dependencies.getForm({ formId: context.formId }) as Record<string, unknown> | undefined)
          : undefined
      const editableLive = collectLiveEditableTableOverrides(
        context.formId,
        elementsRegistryForEditable ?? {},
        {
          pageDataSlot:
            dependencies?.pageDataSlot ??
            actionScript.resolvePagesDataSlotId(context, dependencies),
          getFormField: (fieldName) => getFormSnapshot?.[fieldName],
        },
      )
      for (const [key, field] of Object.entries(editableLive)) {
        if (!field || typeof field !== 'object') continue
        clientDataFields[key] = {
          type: field.type || 'UniDataTable',
          value: this.toPrimitiveValue(field.value),
        }
      }
    }

    if (context.nestedHostEn) {
      const fromHostEn = this.exchangeDataFromEnForNestedCall(context.nestedHostEn, context)
      const enFields = (fromHostEn.fields?.fields ?? fromHostEn.fields ?? {}) as Record<
        string,
        { value: unknown; type: string }
      >
      for (const [key, field] of Object.entries(enFields)) {
        if (field && typeof field === 'object') {
          clientDataFields[key] = field
        }
      }
    }

    const fieldsForPayload = limitToSelectedData
      ? this.filterClientDataForLimitedSelection(clientDataFields, context, dependencies)
      : clientDataFields

    if (!limitToSelectedData && !isTilesCloneAction) {
      ensureTabRoutedSearchClientFields(
        fieldsForPayload as Record<string, { value: unknown; type: string }>,
        context,
        dependencies,
        pageFields,
        pageData as Record<string, unknown> | null | undefined,
      )
    }

    overlayMiddleScriptUniDataOnClientDataFields(
      fieldsForPayload as Record<string, { value: unknown; type: string }>,
      context.middleScriptUniData,
      (value) => this.toPrimitiveValue(value),
    )

    applyListboxWireTypesFromListboxFields(fieldsForPayload)

    for (const [key, field] of Object.entries(fieldsForPayload)) {
      const coerced = coerceExchangeTypedFieldForPayload(field)
      if (coerced) {
        fieldsForPayload[key] = coerced
      }
    }

    if (!limitToSelectedData && context.formId) {
      const elementsRegistry =
        typeof dependencies?.formProvider?.getElements === 'function'
          ? dependencies.formProvider.getElements()
          : undefined
      const enrichedTables = enrichEditableTableUrlFieldsInClientDataFields(
        fieldsForPayload as Record<string, unknown>,
        elementsRegistry ?? {},
        context.formId,
      )
      const refillElement = dependencies?.formProvider?.refillElement
      if (enrichedTables.length > 0 && typeof refillElement === 'function') {
        for (const tableName of enrichedTables) {
          const entry = fieldsForPayload[tableName]
          if (entry) {
            refillElement(tableName, entry, String(context.formId))
          }
        }
      }

      const formRoot =
        (pageData as { form?: unknown } | undefined)?.form ??
        (pageData as { form?: { form?: unknown } } | undefined)?.form
      const layoutNames = collectLayoutFieldNames(formRoot)
      // Только этот срез (начальный parent.getData / recreate overlay), не накопленный currentData (WUI-507).
      const sanitized = sanitizeOutboundActionClientDataFields(fieldsForPayload, {
        formRoot,
        layoutElementNames: layoutNames,
      })
      for (const key of Object.keys(fieldsForPayload)) {
        if (!(key in sanitized)) {
          delete fieldsForPayload[key]
        }
      }
      fillEmptyUrlCellsFromSelfStringLists(fieldsForPayload)
    }

    return this.applyBoundDocumentIdentityOverlayToCurrentData(
      createSafeClientData({
        fields: fieldsForPayload,
        clientData: {
          params: { ...extraParams },
        },
      }),
      context,
      dependencies,
    )
  }

  /** Пустой url в таблицах ответа ← self-list из request currentData (websiteUrl TypedArray). */
  private fillEmptyUrlsInResponseFromRequest(
    response: Record<string, unknown> | null | undefined,
    currentData: ExchangeData | null | undefined,
  ): void {
    if (!response || typeof response !== 'object') return
    const source = currentData?.fields?.fields as Record<string, unknown> | undefined
    const jsResult = response.jsResult as { changed_data?: { fields?: { fields?: Record<string, unknown> } } } | undefined
    const targets: Array<Record<string, unknown> | undefined> = [
      jsResult?.changed_data?.fields?.fields,
      (response.currentChainData as { fields?: { fields?: Record<string, unknown> } } | undefined)?.fields
        ?.fields,
      (response.uniData as { fields?: { fields?: Record<string, unknown> } } | undefined)?.fields?.fields,
      (response.fields as { fields?: Record<string, unknown> } | undefined)?.fields,
    ]
    for (const target of targets) {
      if (target && typeof target === 'object') {
        fillEmptyUrlCellsFromSelfStringLists(target, source)
      }
    }
  }

  /** Очистка UniData (ru.esoft.swingfatclient.newlogic.Action.ActionCall#cleanupData) */
  private cleanupClientData(): any {
    return createSafeClientData({
      fields: {},
    })
  }

  /** Применяет changed_data к полям формы через En: setField и refillElement для каждого поля. */
  private applyChangedDataToForm(
    changedData: any,
    states: any[] = [],
    context: ActionContext,
    dependencies?: ActionDependencies,
  ): void {
    if (!changedData) {
      console.warn('[ActionService] No changed_data to apply')
      return
    }
    const formProvider = dependencies?.formProvider
    const getForm = dependencies?.getForm || (() => ({}))
    const appStore = dependencies?.appStore
    const slotId = actionScript.resolvePagesDataSlotId(context, dependencies)
    if (slotId) {
      applyFormErrorFromPayload(slotId, changedData)
    }
    const changedDataWithoutInitialPayload = stripInitialActionPayloadFields(changedData, context)
    const sanitizedChangedData = sanitizeCheckboxSelectionChangedData(
      changedDataWithoutInitialPayload,
      context,
      dependencies,
    )
    const tableSelections = getKnownTableSelections(context, dependencies)
    // Создаем En контекст для работы с данными формы
    en.value = actionScript.createEnContext(
      context,
      appStore,
      this.invokeNestedElementActionFromClientScript,
      dependencies?.reloadElement,
      formProvider?.refillElement,
      dependencies?.formProvider?.getElements,
      dependencies,
      formProvider?.empty,
    )

    // Входящая UniData не должна теряться: перед применением changed_data заполняем En полными
    const pageData = slotId ? appStore?.pagesData?.[slotId] : undefined
    const existingFields = pageData?.uniData?.fields?.fields || {}
    const existingFromJsResult = pageData?.jsResult?.changed_data?.fields?.fields || {}
    const changedFields = resolveClientDataActualFields(sanitizedChangedData)
    if (changedFields && typeof changedFields === 'object') {
      fillEmptyUrlCellsFromSelfStringLists(
        changedFields as Record<string, unknown>,
        {
          ...(existingFields as Record<string, unknown>),
          ...(existingFromJsResult as Record<string, unknown>),
        },
      )
    }
    const fullFields = mergeFieldMaps(existingFields, existingFromJsResult, changedFields)
    if (Object.keys(fullFields).length > 0) {
      en.value!._resetData(fullFields)
    }

    const fields = changedFields
    if (!fields || typeof fields !== 'object' || Array.isArray(fields)) {
      console.warn('[ActionService] changed_data.fields.fields is not an object or is empty')
      return
    }

    // Применяем измененные данные
    Object.keys(fields).forEach((fieldName) => {
      const fieldData = fields[fieldName]

      if (!fieldData || typeof fieldData !== 'object') {
        console.warn(`[ActionService] Invalid field data for field ${fieldName}:`, fieldData)
        return
      }

      const fieldValue = fieldData.value
      const fieldType = fieldData.type || 'String'
      const typedValue = {
        value: fieldValue,
        type: fieldType,
      }

      try {
        const existingField = en.value!.getData().getField(fieldName)
        const existingValue = existingField?.value
        const existingIsTable =
          existingField?.type === 'UniDataTable' ||
          Array.isArray(existingValue?.rows) ||
          typeof existingValue?.getRow === 'function'
        const incomingIsScalarString =
          fieldType === 'String' &&
          !Array.isArray(fieldValue?.rows) &&
          typeof fieldValue?.getRow !== 'function'

        if (
          !fieldName.endsWith('__action') &&
          existingIsTable &&
          incomingIsScalarString
        ) {
          return
        }

        if (
          !fieldName.endsWith('__action') &&
          shouldPreserveFieldEntryOnEmptyIncomingForStates(
            fieldName,
            existingField,
            fieldData,
            states,
          )
        ) {
          return
        }

        if (
          fieldType === 'UniDataTable' &&
          tableSelections.some(
            (selection) => selection.fieldName === fieldName && sameRows(fieldValue?.rows, selection.rows),
          )
        ) {
          return
        }

        en.value!.getData().setField(fieldName, typedValue)
        // Вызываем refillElement для обновления элемента формы
        en.value!.refillElement(fieldName, typedValue)
        if (
          fieldHasRefillOrEmptyState(states, fieldName) &&
          fieldEntryLooksEmptyIncoming(fieldData) &&
          context.formId != null
        ) {
          resetTreeTableSearchApplyState(context.formId)
        }
      } catch (error) {
        console.warn(`[ActionService] Failed to set field ${fieldName} via En:`, error)
      }
    })
  }

  /**
   * Как applyChangedDataToForm, но для переданного экземпляра En (скрипт web_client_exec), без подмены глобального en.
   */
  private applyChangedDataToEnInstance(
    targetEn: En,
    changedData: any,
    context: ActionContext,
    dependencies?: ActionDependencies,
    states: any[] = [],
  ): void {
    if (!changedData) {
      return
    }
    const appStore = dependencies?.appStore
    const formId = context.formId ? parseInt(context.formId) : 0
    const changedDataWithoutInitialPayload = stripInitialActionPayloadFields(changedData, context)
    const sanitizedChangedData = sanitizeCheckboxSelectionChangedData(
      changedDataWithoutInitialPayload,
      context,
      dependencies,
    )
    const tableSelections = getKnownTableSelections(context, dependencies)

    const pageData = appStore?.pagesData?.[formId]
    const existingFields = pageData?.uniData?.fields?.fields || {}
    const existingFromJsResult = pageData?.jsResult?.changed_data?.fields?.fields || {}
    const changedFields = resolveClientDataActualFields(sanitizedChangedData)
    if (changedFields && typeof changedFields === 'object') {
      fillEmptyUrlCellsFromSelfStringLists(
        changedFields as Record<string, unknown>,
        {
          ...(existingFields as Record<string, unknown>),
          ...(existingFromJsResult as Record<string, unknown>),
        },
      )
    }
    const fullFields = mergeFieldMaps(existingFields, existingFromJsResult, changedFields)
    if (Object.keys(fullFields).length > 0) {
      targetEn._resetData(fullFields)
    }

    const fields = changedFields
    if (!fields || typeof fields !== 'object' || Array.isArray(fields)) {
      return
    }

    Object.keys(fields).forEach((fieldName) => {
      const fieldData = fields[fieldName]
      if (!fieldData || typeof fieldData !== 'object') {
        return
      }
      const fieldValue = fieldData.value
      const fieldType = fieldData.type || 'String'
      const typedValue = { value: fieldValue, type: fieldType }
      try {
        const existingField = targetEn.getData().getField(fieldName)
        const existingValue = existingField?.value
        const existingIsTable =
          existingField?.type === 'UniDataTable' ||
          Array.isArray(existingValue?.rows) ||
          typeof existingValue?.getRow === 'function'
        const incomingIsScalarString =
          fieldType === 'String' &&
          !Array.isArray(fieldValue?.rows) &&
          typeof fieldValue?.getRow !== 'function'
        if (!fieldName.endsWith('__action') && existingIsTable && incomingIsScalarString) {
          return
        }
        if (
          !fieldName.endsWith('__action') &&
          shouldPreserveFieldEntryOnEmptyIncomingForStates(
            fieldName,
            existingField,
            fieldData,
            states,
          )
        ) {
          return
        }
        if (
          fieldType === 'UniDataTable' &&
          tableSelections.some(
            (selection) => selection.fieldName === fieldName && sameRows(fieldValue?.rows, selection.rows),
          )
        ) {
          return
        }
        targetEn.getData().setField(fieldName, typedValue)
        targetEn.refillElement(fieldName, typedValue)
        if (
          fieldHasRefillOrEmptyState(states, fieldName) &&
          fieldEntryLooksEmptyIncoming(fieldData) &&
          context.formId != null
        ) {
          resetTreeTableSearchApplyState(context.formId)
        }
      } catch (error) {
        console.warn(`[ActionService] Failed to set field ${fieldName} on client En:`, error)
      }
    })
  }

  /**
   * Вложенный doAction вызываемый из скриптов
   * TODO: требует проработки
   */
  invokeNestedElementActionFromClientScript = async (params: {
    hostContext: ActionContext
    actionId: string | number
    en: En
    appStore: AppStore
    dependencies?: ActionDependencies
  }): Promise<ExchangeData | null> => {
    const { hostContext, actionId, en: enInst, appStore, dependencies } = params
    const formId = hostContext.formId
    if (formId == null) {
      console.warn('[ActionService] invokeNestedElementActionFromClientScript: missing formId')
      return null
    }
    const slotId = actionScript.resolvePagesDataSlotId(hostContext, dependencies)
    const pageData =
      (slotId ? appStore?.pagesData?.[slotId] : undefined) ??
      appStore?.pagesData?.[formId] ??
      appStore?.pagesData?.[String(formId)]
    const actionBlob = this.findFormActionById(pageData, actionId)
    if (!actionBlob) {
      console.warn('[ActionService] No action in form layout for id', actionId)
      return null
    }

    const nestedContext = this.buildNestedActionContextFromFormBlob(hostContext, actionBlob)
    nestedContext.nestedHostEn = enInst
    this.normalizeContext(nestedContext, appStore)

    const onClickScriptData = nestedContext.data?.onClick as ScriptData | undefined
    if (shouldExecuteActionScriptOnClient(nestedContext, onClickScriptData)) {
      await this.executeAction(nestedContext, dependencies)
      return this.exchangeDataFromEnForNestedCall(enInst, hostContext)
    }

    const transfer = nestedContext.transfer
    const transferType = nestedContext.transferType
    if (!transfer || !transferType) {
      console.warn('[ActionService] Nested action missing transfer or transferType', actionId)
      return null
    }

    const normalizedTransferType =
      typeof transferType === 'string' ? transferType.toLowerCase() : String(transferType)
    const actionItem = this.buildActionItem(nestedContext, normalizedTransferType)
    const { needToCallDoAction } = useActions()
    if (!needToCallDoAction(actionItem)) {
      console.warn(
        '[ActionService] Nested action transferType does not use elementAction:',
        normalizedTransferType,
      )
      return null
    }

    if (nestedContext.confirm) {
      const confirmed = await confirmPlatformAction(nestedContext.confirm)
      if (!confirmed) return null
    }

    const currentData = this.exchangeDataFromEnForNestedCall(enInst, hostContext)
    const changedData = await this.callDoAction(transfer, nestedContext, currentData, dependencies)
    if (changedData && enInst) {
      this.applyChangedDataToEnInstance(enInst, changedData, hostContext, dependencies)
    }
    return changedData ?? null
  }

  private findFormActionById(pageData: any, actionId: string | number): any | null {
    if (!pageData?.form?.actions?.action) {
      return null
    }
    const raw = pageData.form.actions.action
    const list = Array.isArray(raw) ? raw : [raw]
    const want = String(actionId)
    for (const a of list) {
      const id = a?._attributes?.id
      if (id != null && String(id) === want) {
        return a
      }
    }
    return null
  }

  private buildNestedActionContextFromFormBlob(host: ActionContext, action: any): ActionContext {
    const layout = action?.layout || {}
    const layoutLevel = layout.level?._text || host.level
    const layoutId = layout.id?._text ?? layout.id
    const pageId = layoutLevel === 'page' && layoutId ? String(layoutId) : host.pageId

    const isTruthy = (v: unknown) =>
      v === true ||
      v === 1 ||
      (typeof v === 'string' && ['true', '1', 'yes', 'y'].includes(v.trim().toLowerCase()))

    return {
      id: String(action?._attributes?.id ?? ''),
      name: String(action?.name?._text ?? action?._attributes?.name ?? ''),
      elementId:
        layoutLevel === 'element' && layoutId
          ? String(layoutId)
          : host.elementId != null
            ? String(host.elementId)
            : undefined,
      panelId: host.panelId != null ? String(host.panelId) : undefined,
      formId: host.formId != null ? String(host.formId) : undefined,
      pageId: pageId != null ? String(pageId) : undefined,
      level: (layoutLevel || host.level || 'form') as ActionContext['level'],
      data: action,
      platform: action?.platform?._text || host.platform,
      windowType: (() => {
        const w = action?.window__type?._text ?? action?.windowType
        return typeof w === 'string' ? w.toUpperCase() : w
      })(),
      onClick: action?.onClick?._text,
      confirm: action?.confirm?._text,
      transfer: action?.transfer?._text as string | undefined,
      transferType: action?.transferType?._text as string | undefined,
      event: {
        dialogClosed: action?.event?.dialogClosed?._text,
        chainCalled: action?.event?.chainCalled?._text,
      },
      flow: action?.flow?._text,
      selectedData: host.selectedData,
      doActionForAdjacentWindow: isTruthy(
        action?.doActionForAdjacentWindow?._text ?? action?.doActionForAdjacentWindow,
      ),
      parentPageId: host.parentPageId,
    }
  }

  private exchangeDataFromEnForNestedCall(enInst: En, hostContext: ActionContext): ExchangeData {
    const allFields = enInst.getData()._getAllFields()
    const clientDataFields: Record<string, any> = {}
    for (const key of Object.keys(allFields)) {
      if (enInst.getElement(String(key))?.doNotSerialize) {
        continue
      }
      const rawField = serializeTypedFieldForExchange(allFields[key])
      if (rawField && typeof rawField === 'object' && 'value' in rawField && 'type' in rawField) {
        clientDataFields[key] = {
          value: this.toPrimitiveValue((rawField as any).value),
          type: (rawField as any).type || 'String',
        }
      } else {
        clientDataFields[key] = {
          value: this.toPrimitiveValue(rawField),
          type: 'String',
        }
      }
    }
    if (hostContext.selectedData && typeof hostContext.selectedData === 'object') {
      const converted = this.convertRowDataToFields(hostContext.selectedData)
      for (const [k, v] of Object.entries(converted)) {
        if (clientDataFields[k] === undefined) {
          clientDataFields[k] = v
        }
      }
    }
    delete (clientDataFields as Record<string, unknown>).__js_result_properties

    const documentViewFromAction =
      (hostContext.data as any)?.documentView?._text ?? (hostContext.data as any)?.documentView
    const extraParams =
      typeof documentViewFromAction === 'string' && documentViewFromAction
        ? { documentView: documentViewFromAction }
        : {}
    if (extraParams.documentView) {
      clientDataFields._documentView = { value: extraParams.documentView, type: 'String' }
    }

    return createSafeClientData({
      fields: clientDataFields,
      clientData: { params: { ...extraParams } },
    })
  }

  /** Возвращает sessionId текущей страницы(формы, coreId) из appStore. */
  private getCurrentSessionId(appStore: AppStore): string | null {
    const currentPageId = appStore.currentPageId || Object.keys(appStore.pagesData)[0]
    const sessionId = appStore.pagesData[currentPageId]?.sessionId

    if (sessionId != null) {
      return String(sessionId)
    }
    return null
  }

  /** Возвращает данные полей формы текущей страницы (uniData.fields.fields). */
  private getCurrentFormData(appStore: AppStore): any {
    const currentPageId = Object.keys(appStore.pagesData)[0]
    return appStore.pagesData[currentPageId]?.uniData?.fields?.fields || {}
  }

  /** Преобразует selectedData/rowData в формат fields (TypedValue) для отправки на сервер. */
  private convertRowDataToFields(rowData: any): Record<string, any> {
    const fields: Record<string, any> = {}

    if (rowData?.fields && typeof rowData.fields === 'object') {
      Object.keys(rowData.fields).forEach((key) => {
        const field = rowData.fields[key]
        if (field && typeof field === 'object' && 'type' in field) {
          const plainValue = this.toPrimitiveValue('value' in field ? field.value : field)
          fields[key] = { value: plainValue, type: (field as any).type || 'String' }
        }
      })
      if (Object.keys(fields).length > 0) {
        return fields
      }
    }

    Object.keys(rowData).forEach((key) => {
      const value = this.unwrapRef(rowData[key])

      if (key === 'attributes' || key === 'errors') {
        return
      }
      // Identity и служебные поля строки (_id / _typeId / _versionId) нужны call_chain;
      // прочие ключи с «_» (например внутренние UI) пропускаем.
      if (
        key.startsWith('_') &&
        key !== '_id' &&
        key !== '_typeId' &&
        key !== '_versionId' &&
        key !== '_countChildren'
      ) {
        return
      }

      if (value !== null && value !== undefined) {
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          fields[key] = {
            value: value,
            type:
              typeof value === 'string' ? 'String' : typeof value === 'number' ? 'Long' : 'Boolean',
          }
        }
      }
    })

    return fields
  }

  /** Извлекает _id, _typeId, _versionId из selectedData для открытия документа. */
  private extractRowDataFromContext(context: ActionContext): any {
    const selectedData = context.selectedData || {}

    if (!selectedData._id || !selectedData._typeId) {
      console.warn(
        `[ActionService] No selected row data found in context. SelectedData:`,
        selectedData,
      )
      console.warn(`[ActionService] Available keys:`, Object.keys(selectedData))

      const extracted: any = {}

      if (selectedData.fields && typeof selectedData.fields === 'object') {
        Object.keys(selectedData.fields).forEach((key: string) => {
          const fieldData = selectedData.fields[key]
          extracted[key] = fieldData?.value !== undefined ? fieldData.value : fieldData
        })
      }

      Object.keys(selectedData).forEach((key: string) => {
        if (key !== 'fields' && !extracted.hasOwnProperty(key)) {
          extracted[key] = selectedData[key]
        }
      })

      if (extracted._id && extracted._typeId) {
        return extracted
      }

      // If still not found, return dummy data
      return {
        _id: -1,
        _typeId: '',
        _versionId: null,
      }
    }

    return selectedData
  }

  private isDocumentOpenTransferType(transferType?: string): boolean {
    return String(transferType ?? '').trim().toLowerCase() === 'document'
  }

  private resolveIdentityForDocumentOpen(
    context: ActionContext,
    sources?: {
      fromExchangeFields?: Record<string, unknown>
      pagePayload?: Record<string, unknown>
    },
  ): BoundDocumentIdentity | null {
    const fromSelected = buildBoundDocumentIdentityFromRow(
      context.selectedData as Record<string, unknown> | undefined,
    )
    if (fromSelected) return fromSelected

    const tilesRow = context.tilesRowScope?.getRowData?.()
    const fromTiles = buildBoundDocumentIdentityFromRow(
      tilesRow as Record<string, unknown> | undefined,
    )
    if (fromTiles) return fromTiles

    if (sources?.fromExchangeFields) {
      const fromFields = readExchangeDocumentIdentity(sources.fromExchangeFields)
      if (fromFields) return fromFields
    }

    if (sources?.pagePayload) {
      return resolveDocumentIdentityFromPageSnapshot(sources.pagePayload)
    }

    return null
  }

  private resolveActionPageData(
    context: ActionContext,
    dependencies?: ActionDependencies,
  ): Record<string, unknown> | undefined {
    const slotId = actionScript.resolvePagesDataSlotId(context, dependencies)
    return (
      (slotId ? dependencies?.appStore?.pagesData?.[slotId] : undefined) ??
      dependencies?.pageData
    )
  }

  private resolveBoundDocumentIdentityForContext(
    context: ActionContext,
    dependencies?: ActionDependencies,
  ): BoundDocumentIdentity | null {
    return resolveBoundDocumentIdentityFromPage(this.resolveActionPageData(context, dependencies))
  }

  private boundIdentityPreserveContext(
    context: ActionContext,
    dependencies?: ActionDependencies,
  ): PreservePayloadDocumentIdentityContext {
    return {
      tilesRowScope: context.tilesRowScope,
      selectedData: context.selectedData,
      layoutDocumentIdentity: resolveDocumentIdentityFromPageSnapshot(
        this.resolveActionPageData(context, dependencies),
      ),
    }
  }

  private stripClientOnlyPageDataForActionContext(
    pageData: Record<string, unknown> | undefined | null,
  ): Record<string, unknown> {
    if (!pageData || typeof pageData !== 'object') return {}
    const {
      $boundDocumentIdentity: _bound,
      $isDialog: _dialog,
      $isAdjacentWindow: _adjacent,
      $isMainFrame: _mainFrame,
      ...rest
    } = pageData
    return rest
  }

  private attachBoundDocumentIdentityToPagePayload(
    pagePayload: Record<string, unknown>,
    context: ActionContext,
    options?: {
      fromExchangeFields?: Record<string, unknown>
      inheritFromPageData?: unknown
      openingCurrentData?: ExchangeData | Record<string, unknown> | null
      refreshBoundFromPayload?: boolean
    },
  ): Record<string, unknown> {
    const existingOnPayload =
      resolveBoundDocumentIdentityFromPage(pagePayload) ??
      resolveDocumentIdentityFromPageSnapshot(pagePayload)
    const inherited =
      resolveBoundDocumentIdentityFromPage(options?.inheritFromPageData) ??
      resolveDocumentIdentityFromPageSnapshot(options?.inheritFromPageData)
    const payloadIdentity = options?.refreshBoundFromPayload
      ? readExchangeDocumentIdentity(mergePageDataFieldSnapshot(pagePayload))
      : null

    if (!this.isDocumentOpenTransferType(String(context.transferType ?? ''))) {
      const keep = payloadIdentity ?? existingOnPayload ?? inherited
      return keep ? { ...pagePayload, $boundDocumentIdentity: keep } : pagePayload
    }

    const openingFields = (() => {
      const raw = options?.openingCurrentData
      if (!raw || typeof raw !== 'object') return undefined
      const exchange = raw as ExchangeData
      if (exchange.fields?.fields && typeof exchange.fields.fields === 'object') {
        return exchange.fields.fields as Record<string, unknown>
      }
      return raw as Record<string, unknown>
    })()

    const identity =
      this.resolveIdentityForDocumentOpen(context, {
        fromExchangeFields: openingFields ?? options?.fromExchangeFields,
        pagePayload,
      }) ??
      existingOnPayload ??
      inherited

    if (!identity) return pagePayload
    return { ...pagePayload, $boundDocumentIdentity: identity }
  }

  private applyBoundDocumentIdentityOverlayToCurrentData(
    clientData: ExchangeData,
    context: ActionContext,
    dependencies?: ActionDependencies,
  ): ExchangeData {
    if (!isDocumentContextAction(context)) return clientData
    const bound = this.resolveBoundDocumentIdentityForContext(context, dependencies)
    if (!bound) return clientData
    const fields = { ...(clientData.fields?.fields ?? {}) } as Record<string, unknown>
    overlayBoundDocumentIdentityOnFieldsUnlessPreserved(
      fields,
      bound,
      this.boundIdentityPreserveContext(context, dependencies),
    )
    return {
      ...clientData,
      fields: {
        ...(clientData.fields ?? {}),
        fields,
      },
    }
  }

  private applyBoundDocumentIdentityAfterProcedureMerge(
    clientData: ExchangeData,
    context: ActionContext,
    dependencies?: ActionDependencies,
    beforeProcedure?: ExchangeData,
    intermediateResult?: ExchangeData | Record<string, unknown> | null,
  ): ExchangeData {
    const mergedFields = { ...((clientData.fields?.fields ?? {}) as Record<string, unknown>) }
    const intermediateFields =
      (intermediateResult as ExchangeData | undefined)?.fields?.fields ??
      (intermediateResult as { fields?: Record<string, unknown> } | undefined)?.fields ??
      {}
    const withProcedureIdentity = replaceDocumentIdentityFieldsFromSource(
      mergedFields,
      intermediateFields as Record<string, unknown>,
    )
    const procedureIdentity = readExchangeDocumentIdentity(
      intermediateFields as Record<string, unknown>,
    )

    const withIdentity: ExchangeData = {
      ...clientData,
      fields: {
        ...(clientData.fields ?? {}),
        fields: withProcedureIdentity,
      },
    }

    if (!isDocumentContextAction(context)) return withIdentity
    const bound = this.resolveBoundDocumentIdentityForContext(context, dependencies)
    if (!bound) return withIdentity
    const beforeFields = (beforeProcedure?.fields?.fields ?? {}) as Record<string, unknown>
    const allowCrossTypeRestore =
      !procedureIdentity && !this.isDocumentTransferType(context.transferType)
    const restored = restoreDocumentIdentityFieldsAfterProcedureMerge(
      withProcedureIdentity,
      bound,
      beforeFields,
      { allowCrossTypeRestore },
    )
    return {
      ...clientData,
      fields: {
        ...(clientData.fields ?? {}),
        fields: restored,
      },
    }
  }

  /** Проверяет, является ли transferType типом открытия документа (document, call_chain_change_doc и т.д.). */
  private isDocumentTransferType(transferType?: string): boolean {
    if (!transferType) return false
    const normalized = transferType.toLowerCase()
    return (DOCUMENT_TRANSFER_TYPES as readonly string[]).includes(normalized)
  }

  private syncPageDataToRegistryForAction(
    context: ActionContext,
    dependencies?: ActionDependencies,
    extra?: { forceFieldNames?: string[] },
  ): void {
    if (!context.formId) return
    const pageDataSlot =
      dependencies?.pageDataSlot ??
      actionScript.resolvePagesDataSlotId(context, dependencies)
    dependencies?.formProvider?.syncPageDataToRegistry?.(
      context.formId,
      pageDataSlot,
      overlayRegistrySyncOptions(context.pageId, extra),
    )
  }

  // ###############################################################################################

  /**
   * Главный метод: выполняет action целиком.
   * Последовательность:
   * 1) подтверждение (confirm)
   * 1b) verify полей формы (verify_re); при ошибке — прерывание
   * 2) onClick (клиент/сервер); при isContext в результате — ранний выход без transfer
   * 3) цепочка: executeProcedure, затем chainCalled (в цикле при retry__operation)
   * 4) платформенное действие: doAction / elementAction по transfer + transferType (handleTransferWithExistingLogic → callDoAction)
   * 5) навигация по окну (handleWindowType)
   * 6) applyChangedData (в том числе changed_data из doAction)
   *
   * Важно: пункты 3 (procedure + chainCalled) выполняются до пункта 4, а не после transfer.
   *
   * @param context - контекст action (id, formId, transfer, transferType, onClick и т.д.)
   * @param dependencies - зависимости (appStore, router, actions, getForm, formProvider и др.)
   */
  async executeAction(
    context: ActionContext,
    dependencies?: ActionDependencies,
  ): Promise<void> {
    const endMeasure = startWorkflowMeasure('ActionService.executeAction', {
      actionId: context.id,
      formId: context.formId,
      level: context.level,
      transferType: context.transferType,
      windowType: context.windowType,
    })

    this.normalizeContext(context, dependencies?.appStore)
    pushBreadcrumb('action', context?.id != null ? String(context.id) : undefined)

    const appStore = dependencies?.appStore
    const actionName = this.resolveActionDisplayName(context)

    try {

      // Шаг 1: диалог
      if (context.confirm) {
        const confirmed = await confirmPlatformAction(context.confirm)
        if (!confirmed) {
          if (appStore?.setLoading) {
            appStore.setLoading(false)
          }
          return
        }
      }

      const verifyFormId = String(
        context.formId ?? dependencies?.pageRegistryFormId ?? '',
      ).trim()
      const pageDataSlot =
        dependencies?.pageDataSlot ??
        actionScript.resolvePagesDataSlotId(context, dependencies)

      if (context.formId) {
        this.syncPageDataToRegistryForAction(context, dependencies)
        await nextTick()
      }

      const elementRegistry = dependencies?.formProvider?.getElements?.()
      if (verifyFormId && elementRegistry && isActionXmlVerifyEnabled(context.data)) {
        const layoutScope = resolveActionLayoutVerifyScope({
          level: context.level,
          layout: context.data?.layout,
          formId: verifyFormId,
          panelId: context.panelId,
          elementId: context.elementId,
          pageId: context.pageId,
        })
        const formRoot = dependencies?.pageData?.form
        const formIdAliases = getPageIdentityAliases(
          dependencies?.pageData as Record<string, unknown>,
          {
            slotId: pageDataSlot,
            registryFormId: verifyFormId,
          },
        )
        const { valid, firstInvalidName } = verifyFormElements(elementRegistry, verifyFormId, {
          layoutScope,
          formRoot,
          formIdAliases,
        })
        if (!valid) {
          if (firstInvalidName) {
            dependencies?.formProvider?.requestFocus?.(firstInvalidName)
          }
          if (appStore?.setLoading) {
            appStore.setLoading(false)
          }
          return
        }
      }

      // Show loading indicator
      if (appStore?.setLoading) appStore.setLoading(true, `${actionName}...`)

      // Обновляем платформу если указана по контексту
      if (context.platform && dependencies?.appStore?.updateCurrentPlatform) {
        appStore.updateCurrentPlatform(context.platform)
      }

      const onClickScriptData = context.data?.onClick as ScriptData | undefined
      const willRunOnClick =
        context.onClick && !isEmptyActionScript(onClickScriptData, context.onClick)

      this.markTreeTableSearchIfNeeded(context, dependencies)

      if (willRunOnClick) {
        await dependencies?.formProvider?.flushPendingEditableTableEdits?.(context.formId)
        this.syncPageDataToRegistryForAction(context, dependencies)
        await nextTick()
        await dependencies?.formProvider?.retryEditableTableOnChangeIfNeeded?.(context.formId)
        this.syncPageDataToRegistryForAction(context, dependencies)
        await nextTick()
      }

      const currentDataRef: { value: ExchangeData } = {
        value: this.createClientDataUniData(context, dependencies),
      };
      (context as any).__initialClientData = createSafeClientData(currentDataRef.value)

      // const hasResultDataChangesRef = { value: false } // признак наличия изменений в полученном результате
      const statesRef: { value: StateData[] } = { value: [] } // states - данные об изменениях элементов после выполнения скрипта на middle
      const jsResultPropertiesRef: { value: JsResultProperties } = {
        value: { ...INITIAL_JS_RESULT_PROPERTIES },
      } // данные, управляющие процессом action
      // Шаг 2: Выполнение onClick (если есть)

      let onClickRanOnClient = false
      if (willRunOnClick) {
        const onClickScript = String(context.onClick ?? '')
        const putFullDataFieldNames = extractScriptRegistryElementNames(onClickScript)
        if (putFullDataFieldNames.length > 0 && context.formId) {
          this.syncPageDataToRegistryForAction(context, dependencies, {
            forceFieldNames: putFullDataFieldNames,
          })
          await nextTick()
        }
        const onClickImportPreflight = {
          clientImportNames: Object.keys(appStore?.imports ?? {}),
          serverOnlyImportNames: appStore?.serverOnlyImportNames ?? [],
        }

        const runOnClickOnMiddle = async () => {
          if (putFullDataFieldNames.length > 0) {
            const elementsRegistry =
              typeof dependencies?.formProvider?.getElements === 'function'
                ? dependencies.formProvider.getElements()
                : typeof dependencies?.getElements === 'function'
                  ? dependencies.getElements()
                  : undefined
            const elementsByKey =
              typeof dependencies?.formProvider?.getElementsByKey === 'function'
                ? dependencies.formProvider.getElementsByKey()
                : undefined
            currentDataRef.value = await overlayFileAttachmentsPutFullDataOnExchangeData(
              currentDataRef.value,
              putFullDataFieldNames,
              elementsRegistry,
              elementsByKey,
            )
          }
          await actionScript.executeMiddleCode(
            ScriptName.ON_CLICK,
            onClickScript,
            context,
            currentDataRef,
            jsResultPropertiesRef,
            appStore,
            this.invokeNestedElementActionFromClientScript,
            dependencies,
            statesRef,
          )
        }

        try {
          if (shouldExecuteOnClickOnClient(context, onClickScriptData, onClickImportPreflight)) {
            onClickRanOnClient = true
            try {
              // Messages.* await user OK — must not sit under LoadingScreen.
              if (appStore?.setLoading) appStore.setLoading(false)
              const updatedChangedData = await actionScript.executeClientCode(
                onClickScript,
                context,
                appStore,
                currentDataRef.value,
                this.invokeNestedElementActionFromClientScript,
                dependencies,
              )

              if (updatedChangedData) {
                currentDataRef.value = updatedChangedData

                jsResultPropertiesRef.value = actionScript.extractJsResultProperties(updatedChangedData)

                // Swing: setContext aborts transfer; putFormData only fills JSContext UniData.
                // Do not refill widgets from that collect (empty TREETABLE checked-slice, etc.).
                if (!jsResultPropertiesRef.value.isContext) {
                  actionScript.applyChangedDataToAppStore(
                    updatedChangedData,
                    context,
                    appStore,
                    dependencies,
                  )
                  this.syncPageDataToRegistryForAction(context, dependencies)
                }
              }
            } catch (clientError) {
              if (clientError instanceof MiddleExecutionRequiredError) {
                if (import.meta.env.DEV) {
                  console.info(
                    `[action.service] onClick routed to middle after client execution failed (import: ${clientError.importName})`,
                  )
                }
                onClickRanOnClient = false
                await runOnClickOnMiddle()
              } else {
                throw clientError
              }
            }
          } else {
            await runOnClickOnMiddle()
          }
        } catch (error: any) {
          if (error instanceof Error) {
            throw error
          } else {
            throw new Error(error)
          }
        }
      }

      actionScript.applyDispatchedVisibilitiesFromProperties(
        jsResultPropertiesRef.value.dispatched_visibilities,
        context,
        dependencies,
      )

      const ranOnClick =
        context.onClick && !isEmptyActionScript(onClickScriptData, context.onClick)
      if (ranOnClick && !jsResultPropertiesRef.value.isContext) {
        await this.finalizeTreeTableSearchAfterOnClick(
          context,
          dependencies,
          currentDataRef,
          onClickRanOnClient,
        )
      }

      if (jsResultPropertiesRef.value.isContext) {
        // Прерывание, в случае если isContext=true
        if (appStore?.setLoading) appStore.setLoading(false)
        return
      }

      // Client onClick cleared the loader for Messages.*; restore only if procedure/doAction follows.
      const willRunProcedureOrTransfer =
        shouldExecuteProcedureLoop(context) || !isNoPlatformTransferAction(context)
      if (onClickRanOnClient && willRunProcedureOrTransfer && appStore?.setLoading) {
        appStore.setLoading(true, `${actionName}...`)
      }

      const stage2CloseResult = await this.handleDialogCloseFromProperties(
        context,
        currentDataRef,
        jsResultPropertiesRef,
        dependencies,
      )
      if (stage2CloseResult.abortAction) {
        if (appStore?.setLoading) appStore.setLoading(false)
        return
      }

      // После onClick registry может содержать url/refill из element onChange; transfer берёт currentDataRef.
      // putFormData() расширяет контекст до всей формы — не затирать снимок onClick полной пересборкой layout-среза.
      // WUI-507: накопленный UniData (setField / putFormData tables) не layout-фильтровать и не bleed-ить.
      // Recreate (layout store) может вернуть все строки EdiTable; checkbox-срез — из onClick, не из recreate.
      if (ranOnClick && context.formId) {
        this.syncPageDataToRegistryForAction(context, dependencies)
        const clientDataAfterOnClick = currentDataRef.value
        const recreatedClientData = this.createClientDataUniData(context, dependencies)
        const selectionPayloadFieldNames = Array.isArray((context as any).__selectionPayloadFieldNames)
          ? (context as any).__selectionPayloadFieldNames
          : []
        const mergedCurrentData = actionScript.mergeIntoCurrentData(
          clientDataAfterOnClick,
          recreatedClientData,
        )
        const pageDataAfterOnClick = this.resolveFreshPageDataForAction(dependencies, context.formId)
        const faFieldNames = collectFileAttachmentFieldNames(
          (pageDataAfterOnClick as { form?: unknown } | undefined)?.form,
          typeof dependencies?.formProvider?.getElements === 'function'
            ? dependencies.formProvider.getElements()
            : undefined,
        )
        currentDataRef.value = preserveUploadedFileAttachmentsOnExchangeData(
          preserveOnClickDocumentIdentity(
            preserveOnClickTableFieldsNotInSelectionPayload(
              overlaySelectionPayloadFieldsOnExchangeData(
                mergedCurrentData,
                clientDataAfterOnClick,
                selectionPayloadFieldNames.length ? selectionPayloadFieldNames : undefined,
              ),
              clientDataAfterOnClick,
              selectionPayloadFieldNames,
            ),
            clientDataAfterOnClick,
          ),
          clientDataAfterOnClick,
          faFieldNames,
        )
      }

      // Шаг 3: Выполнение performPreAction

      const endPerformPreActionMeasure = startWorkflowMeasure('ActionService.performPreAction', {
        actionId: context.id,
        formId: context.formId,
        transfer: context.transfer,
        transferType: context.transferType,
      })
      let resultPerform: PerformPreActionResult
      try {
        resultPerform = await this.performPreAction(
          context,
          currentDataRef,
          jsResultPropertiesRef,
          dependencies,
        )
      } finally {
        endPerformPreActionMeasure()
      }

      actionScript.applyDispatchedVisibilitiesFromProperties(
        jsResultPropertiesRef.value.dispatched_visibilities,
        context,
        dependencies,
      )

      if (
        this.isDocumentTransferType(context.transferType) &&
        jsResultPropertiesRef.value.isContext &&
        documentIdentityChangedAfterPreAction(
          (context as any).__initialClientData as ExchangeData,
          currentDataRef.value,
        )
      ) {
        // chainCalled мог вызвать setContext() до проверки srvCnt (result guard),
        // но procedure уже сменил документ (_id dummy) — нужен conditional change_doc.
        jsResultPropertiesRef.value = {
          ...jsResultPropertiesRef.value,
          isContext: false,
        }
      }

      if (!resultPerform.ok || resultPerform.abortAction || jsResultPropertiesRef.value.isContext) {
        // call&return + en.setContext() in chainCalled: abort before doAction, but still close dialog
        // (transferType *return* / XML call&return — Swing parity for 2577 add_btn).
        if (
          resultPerform.ok &&
          !resultPerform.abortAction &&
          jsResultPropertiesRef.value.isContext &&
          (context as unknown as Record<string, unknown>).__closedViaProperties !== true
        ) {
          const xmlIsContext = String(
            extractActionTextValue(context.data?.isContext) ?? '',
          ).trim()
          const shouldCloseModal =
            shouldCloseDialogDespiteSetContextAbort(xmlIsContext, context.transferType) ||
            (dependencies?.actions?.needToCloseModal?.(
              this.buildActionItem(context, context.transferType ?? ''),
            ) ??
              false)
          if (shouldCloseModal) {
            try {
              await this.closeCurrentContext(context, dependencies)
            } catch (closeError) {
              console.warn(
                '[ActionService] closeCurrentContext after setContext abort failed',
                closeError,
              )
            }
          }
        }
        if (appStore?.setLoading) appStore.setLoading(false)
        return
      }

      // Swing: performPreAction returned false (XML isContext=call) — no doAction / window open.
      if (!resultPerform.continueAfterPreAction) {
        if (appStore?.setLoading) appStore.setLoading(false)
        return
      }

      // doAction / platformAction — handleTransferWithExistingLogic.
      const closeRootsSourceSlotId =
        String(dependencies?.pageDataSlot ?? '').trim() ||
        actionScript.resolvePagesDataSlotId(context, dependencies)
      const transferResult =
        context.transfer &&
        context.transferType &&
        !isNoPlatformTransferAction(context)
          ? await this.handleTransferWithExistingLogic(context, currentDataRef.value, dependencies)
          : { didCallDoAction: false, changedData: null }

      const transferChangedData = transferResult.changedData

      // TODO: уточнить действия ниже!
      if (
        !context.doActionForAdjacentWindow &&
        context.windowType &&
        !this.isDocumentTransferType(context.transferType)
      ) {
        // callDoAction уже открыл форму
        const shouldSkipWindowOpen =
          transferResult.didCallDoAction &&
          (context.windowType === 'NEW_DIALOG' ||
            context.windowType === 'NEW_WINDOW' ||
            context.windowType === 'SAME_WINDOW' ||
            context.windowType === 'MAIN_FRAME')
        if (!shouldSkipWindowOpen) {
          const endHandleWindowTypeMeasure = startWorkflowMeasure('ActionService.handleWindowType', {
            actionId: context.id,
            formId: context.formId,
            windowType: context.windowType,
            transfer: context.transfer,
          })
          try {
            await this.handleWindowType(
              context,
              dependencies?.router,
              dependencies?.appStore,
              dependencies,
            )
          } finally {
            endHandleWindowTypeMeasure()
          }
        }
      }

      // Swing ActionCall.closeRoots after NEW_WINDOW/NEW_DIALOG (isNewThread):
      // en.setReturn() in chainCalled with call&explicit must close the dialog after open.
      if ((context as unknown as Record<string, unknown>).__closedViaProperties !== true) {
        try {
          await invokeCloseRootsFromProperties({
            context,
            props: jsResultPropertiesRef.value,
            windowType: context.windowType,
            dependencies,
            sourceSlotId: closeRootsSourceSlotId,
            openedSlotId: readCloseRootsOpenedSlotId(context),
            resolvePagesDataSlotId: actionScript.resolvePagesDataSlotId.bind(actionScript),
            closeDialogSlot: (closingSlotId, ctx, deps, listeners, dialogPageSnapshot) =>
              this.closeDialogSlot(
                closingSlotId,
                ctx,
                deps,
                listeners,
                dialogPageSnapshot,
                extractIncomingDataFieldMap(currentDataRef.value),
              ),
            mergedCloseFields: extractIncomingDataFieldMap(currentDataRef.value),
          })
        } catch (closeRootsError) {
          console.warn('[ActionService] closeRoots after new thread failed', closeRootsError)
        }
      }

      const shouldSkipParentChangedDataApply =
        context.windowType === 'NEW_DIALOG' && transferResult.didCallDoAction

      const shouldSkipPostDoActionChangedDataApply =
        transferResult.didCallDoAction &&
        (shouldSkipParentChangedDataApply ||
          (context.windowType === 'SAME_WINDOW' &&
            this.isDocumentTransferType(context.transferType)))

      const closedViaProperties =
        (context as unknown as Record<string, unknown>).__closedViaProperties === true
      const modalCloseTransferType = this.resolveModalCloseTransferType(context, context.transferType)
      const isModalCloseAction =
        !closedViaProperties &&
        (dependencies?.actions?.needToCloseModal?.(
          this.buildActionItem(context, modalCloseTransferType),
        ) ??
          false)

      // Применение данных changed_data в итоге
      if (
        transferChangedData &&
        !context.doActionForAdjacentWindow &&
        !shouldSkipPostDoActionChangedDataApply &&
        !isModalCloseAction
      ) {
        const endApplyChangedDataMeasure = startWorkflowMeasure('ActionService.applyChangedData', {
          actionId: context.id,
          formId: context.formId,
          transferType: context.transferType,
        })
        try {
          this.applyChangedDataToForm(
            transferChangedData,
            statesRef.value || [],
            context,
            dependencies,
          )
          // TODO: Собственно, только тут и должно вызываться, по идее. Проверить, зачем в онклик дергается
          actionScript.applyChangedDataToAppStore(
            transferChangedData,
            context,
            appStore,
            dependencies,
            statesRef.value || [],
          )
        } finally {
          endApplyChangedDataMeasure()
        }
      }

      if (
        context.formId != null &&
        !ranOnClick &&
        shouldSubmitTreeTableSearch(
          context,
          this.getPageFieldsForTreeTableSearch(dependencies, context.formId),
          {
            searchString: this.getLiveSearchString(context.formId, dependencies, context),
            getElements: this.getTreeTableSearchElementsAccessor(dependencies),
            pageData: dependencies?.pageData as Record<string, unknown> | null | undefined,
            mergedFields: this.getMergedFieldsForDoLoadSearch(context, dependencies),
            dependencies,
          },
        )
      ) {
        await this.flushPendingTreeTableSearchIfNeeded(context, dependencies)
      }
    } catch (error: any) {
      console.error(`Error executing action ${context.name}:`, error)
      reportError(error, {
        source: 'action.service',
        actionId: context?.id != null ? String(context.id) : '',
        actionName: context?.name != null ? String(context.name) : '',
        formId: context?.formId != null ? String(context.formId) : '',
      })
      // throw (error instanceof Error) ? error : new Error(error)
      throw error
    } finally {
      // Hide loading indicator
      if (appStore?.setLoading) {
        appStore.setLoading(false)
      }
      purgeOrphanBodyDialogMasks()
      endMeasure()
    }
  }

  /** Применяет delta procedure/chainCalled к UI и appStore после performPreAction merge. */
  private applyProcedureMergeDeltaToForm(
    snapshotBeforeProcedure: ExchangeData,
    mergedCurrentData: ExchangeData,
    context: ActionContext,
    dependencies?: ActionDependencies,
    /** Поля, уже обновлённые через en.reloadElement в client chainCalled — не затирать. */
    omitFieldNames?: ReadonlySet<string>,
  ): void {
    const delta = computeExchangeFieldDelta(snapshotBeforeProcedure, mergedCurrentData)
    const rawFields = delta.fields?.fields ?? {}
    if (!rawFields || Object.keys(rawFields).length === 0) {
      return
    }

    let deltaFields = rawFields
    if (omitFieldNames && omitFieldNames.size > 0) {
      deltaFields = Object.fromEntries(
        Object.entries(rawFields).filter(([key]) => {
          // Поля после en.reloadElement: UI уже из onReloaded; не откатывать delete 0→1.
          return !omitFieldNames.has(key)
        }),
      )
      if (Object.keys(deltaFields).length === 0) {
        return
      }
    }

    const appStore = dependencies?.appStore
    if (!appStore) {
      return
    }

    const filteredDelta: ExchangeData = {
      ...delta,
      fields: {
        ...(delta.fields && typeof delta.fields === 'object' ? delta.fields : {}),
        fields: deltaFields,
      },
    }

    actionScript.applyChangedDataToAppStore(filteredDelta, context, appStore, dependencies)
    this.applyChangedDataToForm(filteredDelta, [], context, dependencies)
    this.syncPageDataToRegistryForAction(context, dependencies)
  }

  /**
   * Выполняет pre-action: executeProcedure (chain) и chainCalled (управляющий скрипт).
   * Поддерживает retry при retry__operation.
   */
  private async performPreAction(
    context: ActionContext,
    currentDataRef: { value: ExchangeData },
    jsResultPropertiesRef: { value: JsResultProperties },
    dependencies?: ActionDependencies,
  ): Promise<PerformPreActionResult> {
    const appStore = dependencies?.appStore

    const { transfer, transferType } = context

    if (!transferType) {
      console.warn('[ActionService] Transfer or transferType is missing')
      return { ok: false, continueAfterPreAction: true }
    }

    const procedureLoop = shouldExecuteProcedureLoop(context)
    // Swing: XML isContext=call → performPreAction returns false (no doAction/window).
    const continueAfterPreAction = shouldContinueAfterPerformPreAction(context)

    // `transfer=0` is valid (context_call route via execute_procedure). `!0` must not skip the loop.
    if (procedureLoop && !hasNormalizedActionTransfer(transfer, transferType)) {
      console.warn('[ActionService] Transfer or transferType is missing')
      return { ok: false, continueAfterPreAction: true }
    }

    if (!procedureLoop) {
      return { ok: true, continueAfterPreAction: true }
    }

    // TODO: переименовать в doRetry
    let retry__operation = false

    try {
      do {
        retry__operation = false

        // Снимок currentData до procedure — база для финального merge (этап 3).
        const snapshotBeforeProcedure = currentDataRef.value
        let intermediateResult: ExchangeData | Record<string, unknown> | null =
          await this.executeProcedure(currentDataRef, context, dependencies)
        if (intermediateResult) {
          intermediateResult = normalizeProcedureExchangeData(intermediateResult)
        }

        const chainCalledScript = this.getChainCalledValue(context)
        const chainCalledData = this.getChainCalledData(context)
        const hasChainCalled = Boolean(
          chainCalledScript && !isEmptyActionScript(chainCalledData, chainCalledScript),
        )

        if (intermediateResult && !hasChainCalled) {
          const procedureProps =
            (intermediateResult as Record<string, unknown>).properties ??
            (intermediateResult as { jsResult?: { properties?: unknown } }).jsResult?.properties
          if (procedureProps != null) {
            jsResultPropertiesRef.value = actionScript.extractJsResultProperties(
              undefined,
              procedureProps,
            )
          }
        }

        /** Таблицы, уже перезагруженные client chainCalled — не затирать delta procedure. */
        let clientChainReloadedFields: ReadonlySet<string> | undefined
        let skipProcedureMergeDelta = false

        if (chainCalledScript && hasChainCalled) {
          if (shouldExecuteChainCalledOnClient(context, chainCalledData)) {
            clientChainReloadedFields = new Set(
              extractReloadElementNamesFromScript(chainCalledScript),
            )
            skipProcedureMergeDelta = scriptReloadsEntireForm(chainCalledScript)

            // chainCalled often await Messages.showMessage — clear loader first.
            if (appStore?.setLoading) appStore.setLoading(false)
            const updatedCurrentData = await actionScript.executeClientCode(
              chainCalledScript,
              context,
              appStore,
              (intermediateResult ?? snapshotBeforeProcedure) as ExchangeData,
              this.invokeNestedElementActionFromClientScript,
              dependencies,
            )
            if (updatedCurrentData) {
              intermediateResult = updatedCurrentData
              jsResultPropertiesRef.value =
                actionScript.extractJsResultProperties(updatedCurrentData)
              retry__operation = jsResultPropertiesRef.value.retry__operation ?? false
            }
          } else {
            const chainDataRef: { value: ExchangeData } = {
              value: (intermediateResult ?? snapshotBeforeProcedure) as ExchangeData,
            }
            await actionScript.executeMiddleCode(
              ScriptName.EVENT_CHAIN_CALLED,
              chainCalledScript,
              context,
              chainDataRef,
              jsResultPropertiesRef,
              appStore,
              this.invokeNestedElementActionFromClientScript,
              dependencies,
            )
            intermediateResult = chainDataRef.value
            retry__operation = jsResultPropertiesRef.value.retry__operation ?? false
          }
        }

        // Промежуточный UniData (procedure ± chainCalled) накладывается поверх currentData:
        // новые ключи добавляются, совпадающие — заменяются значениями из intermediate.
        currentDataRef.value = this.applyBoundDocumentIdentityAfterProcedureMerge(
          actionScript.mergeIntoCurrentData(snapshotBeforeProcedure, intermediateResult),
          context,
          dependencies,
          snapshotBeforeProcedure,
          intermediateResult,
        )

        if (!(hasChainCalled && jsResultPropertiesRef.value.cleanup_data) && !skipProcedureMergeDelta) {
          // Reload уже в UI; delta всё же нужна, если storeLookup оставил procedure
          // (не тот слот / reload no-op) — omit только имена reload, не все UniDataTable.
          this.applyProcedureMergeDeltaToForm(
            snapshotBeforeProcedure,
            currentDataRef.value,
            context,
            dependencies,
            clientChainReloadedFields,
          )
        }

        if (hasChainCalled && jsResultPropertiesRef.value.cleanup_data) {
          currentDataRef.value = this.cleanupClientData()
          context.params.clientData = currentDataRef.value
        }

        const stage3CloseResult = await this.handleDialogCloseFromProperties(
          context,
          currentDataRef,
          jsResultPropertiesRef,
          dependencies,
        )
        if (stage3CloseResult.abortAction) {
          return { ok: true, continueAfterPreAction: false, abortAction: true }
        }

        if (hasChainCalled && jsResultPropertiesRef.value.isContext) {
          break
        }
      } while (retry__operation)

      return { ok: true, continueAfterPreAction }
    } catch (error) {
      throw error
    }
  }

  /**
   * Выполнение action. Обрабатывает transfer: callDoAction, closeModal, handleSpecificTransferTypes (print, document, view).
   * Возвращает флаг вызова doAction и (опционально) changed_data, полученный из ответа elementAction.
   * TODO: проверить передачу в запрос currentData!
   */
  private async handleTransferWithExistingLogic(
    context: ActionContext,
    currentData: any,
    dependencies?: ActionDependencies,
  ): Promise<{ didCallDoAction: boolean; changedData: ExchangeData | null }> {
    const endMeasure = startWorkflowMeasure('ActionService.handleTransferWithExistingLogic', {
      actionId: context.id,
      formId: context.formId,
      transfer: context.transfer,
      transferType: context.transferType,
      windowType: context.windowType,
    })
    const { transfer, transferType } = context
    const appStore = dependencies?.appStore

    const actions = dependencies?.actions

    const currentFields =
      currentData?.fields?.fields ||
      currentData?.fields ||
      currentData?.clientData?.fields?.fields ||
      currentData?.clientData?.fields ||
      {}
    const currentActionValueRaw = currentFields?.action
    const currentActionValue =
      currentActionValueRaw && typeof currentActionValueRaw === 'object' && 'value' in currentActionValueRaw
        ? String((currentActionValueRaw as { value: unknown }).value ?? '')
        : String(currentActionValueRaw ?? '')
    const hasManagedProgressBar = currentFields?.prId != null

    if (!transfer || !transferType) {
      console.warn('[ActionService] Transfer or transferType is missing')
      return { didCallDoAction: false, changedData: null }
    }

    let didCallDoAction = false
    let changedDataFromDoAction: ExchangeData | null = null

    try {
      // Normalize transferType to string - it might come as number (e.g., 9) or string (e.g., "9")
      const normalizedTransferType =
        typeof transferType === 'string' ? transferType.toLowerCase() : String(transferType)

      const actionItem = this.buildActionItem(context, normalizedTransferType)

      if (actions && actions.needToCallDoAction(actionItem)) {
        const callDoActionStartedAt = performance.now()
        changedDataFromDoAction = await this.callDoAction(transfer, context, currentData, dependencies)
        startWorkflowMeasure('ActionService.afterCallDoActionResume', {
          actionId: context.id,
          formId: context.formId,
          transfer: context.transfer,
          transferType: context.transferType,
          elapsedSinceCallStartMs: Math.round((performance.now() - callDoActionStartedAt) * 10) / 10,
        })()
        didCallDoAction = true
        if (
          changedDataFromDoAction &&
          actions?.needToCloseModal?.(actionItem) &&
          appStore
        ) {
          const changedFields = changedDataFromDoAction.fields?.fields || {}
          if (changedFields && Object.keys(changedFields).length > 0) {
            actionScript.applyChangedDataToAppStore(
              changedDataFromDoAction,
              context,
              appStore,
              dependencies,
            )
            this.applyChangedDataToForm(
              changedDataFromDoAction,
              [],
              context,
              dependencies,
            )
          }
        }
        // Fallback: close only generic progress bars.
        // Print helper manages its own progress bar inside chainCalled, so closing it here breaks multi-file flow.
        try {
          const isPrintHelperLoopAction =
            currentActionValue === 'getDocFiles' || currentActionValue === 'getFileData'
          if (!hasManagedProgressBar && !isPrintHelperLoopAction) {
            const endProgressCleanupMeasure = startWorkflowMeasure('ActionService.progressCleanup', {
              actionId: context.id,
              formId: context.formId,
              transferType: context.transferType,
            })
            try {
              const last = progressBarService.getLastInstance()
              if (last?.visible) {
                progressBarService.close(last.id)
              }
            } finally {
              endProgressCleanupMeasure()
            }
          }
        } catch (_) {
          // ignore
        }
      }

      if (
        !((context as unknown as Record<string, unknown>).__closedViaProperties === true) &&
        actions &&
        actions.needToCloseModal(actionItem)
      ) {
        const endCloseContextMeasure = startWorkflowMeasure('ActionService.closeCurrentContext', {
          actionId: context.id,
          formId: context.formId,
          transferType: context.transferType,
        })
        try {
          await this.closeCurrentContext(context, dependencies)
        } finally {
          endCloseContextMeasure()
        }
      }

      if (!(didCallDoAction && this.isDocumentTransferType(normalizedTransferType))) {
        const endSpecificTransferMeasure = startWorkflowMeasure('ActionService.handleSpecificTransferTypes', {
          actionId: context.id,
          formId: context.formId,
          transferType: context.transferType,
          transfer,
        })
        try {
          await this.handleSpecificTransferTypes(context, dependencies)
        } finally {
          endSpecificTransferMeasure()
        }
      }
      return { didCallDoAction, changedData: changedDataFromDoAction }
    } finally {
      endMeasure()
    }
  }

  private resolveStableParentForChildOpen(
    parentPageId: string | null | undefined,
    dependencies?: ActionDependencies,
  ): string | undefined {
    const appStore = dependencies?.appStore
    if (!appStore) return parentPageId ?? undefined
    const tabsStore = useDocumentTabsStore()
    const candidate =
      parentPageId ??
      (appStore.currentPageId && appStore.currentPageId !== 'home'
        ? appStore.currentPageId
        : undefined)
    return (
      resolveStableParentPageSlotId(
        candidate,
        appStore.pagesData as Record<string, unknown>,
        tabsStore.tabIdsOrdered,
      ) ??
      tabsStore.rootTabId ??
      undefined
    )
  }

  /**
   * Вкладка + pending до router.push: иначе mainSlotTabIds без win-слота → пустой экран.
   */
  private prepareMainAreaDocumentNavigation(
    pageSlotId: string,
    pageData: Record<string, unknown> | null | undefined,
    parentPageId: string | null | undefined,
    dependencies?: ActionDependencies,
  ): void {
    if (!dependencies?.appStore) return
    const slotId = String(pageSlotId ?? '').trim()
    if (!slotId) return
    if (
      isDialogPageSlot(
        {
          dialogIds: dependencies.appStore.dialogIds ?? [],
          dialogPageSlotIds: dependencies.appStore.dialogPageSlotIds ?? [],
          pagesData: dependencies.appStore.pagesData ?? {},
        },
        slotId,
      )
    ) {
      return
    }
    const stableParent = this.resolveStableParentForChildOpen(parentPageId, dependencies)
    useDocumentTabsStore().openOrFocusDocumentTab(
      slotId,
      pageData ?? null,
      'document',
    )
    dependencies.appStore.setPendingPageNavigation?.(
      slotId,
      stableParent ?? undefined,
    )
    dependencies.appStore.updatePageId?.(slotId)
    dependencies.appStore.clearAdjacentForParent?.(slotId)
  }

  /**
   * SAME_WINDOW document replace: ensure :activate so root onLoad runs after remount (e.g. hide «Создать» when _id > 0).
   */
  private ensureSameWindowActivateEvent(
    slotId: string,
    dependencies?: ActionDependencies,
  ): void {
    const appStore = dependencies?.appStore
    if (!appStore) return
    const id = String(slotId ?? '').trim()
    if (!id) return
    const pageData = appStore.pagesData?.[id] as Record<string, any> | undefined
    if (!pageData) return
    const fields = pageData.uniData?.fields?.fields as Record<string, unknown> | undefined
    const eventVal =
      fields?._event &&
      typeof fields._event === 'object' &&
      'value' in (fields._event as Record<string, unknown>)
        ? (fields._event as { value: unknown }).value
        : fields?._event
    if (String(eventVal ?? '') === ':activate') {
      return
    }
    const nextFields = { ...(fields || {}), _event: { type: 'String', value: ':activate' } }
    appStore.setPageData(
      id,
      {
        ...pageData,
        uniData: {
          ...pageData.uniData,
          fields: {
            ...pageData.uniData?.fields,
            fields: nextFields,
          },
        },
      },
      undefined,
      undefined,
      'ActionService.ensureSameWindowActivateEvent',
    )
  }

  /** SAME_WINDOW в основной области: заменить вкладку/слот исходной формы, не открывать вторую вкладку. */
  private prepareMainAreaSameWindowReplace(
    sourceSlotId: string,
    pageData: Record<string, unknown> | null | undefined,
    parentPageId: string | null | undefined,
    dependencies?: ActionDependencies,
    options?: { openedFormId?: string | null; schemaUnchanged?: boolean },
  ): void {
    if (!dependencies?.appStore) return

    const slotId = String(sourceSlotId ?? '').trim()
    if (!slotId) return

    const openedFormId = String(options?.openedFormId ?? '').trim()
    if (openedFormId && openedFormId !== slotId) {
      const tabsStore = useDocumentTabsStore()
      tabsStore.removeTabEntry(openedFormId)
      // Orphan schema key (2673) after SAME_WINDOW replace into parent slot (2675).
      if (dependencies.appStore.pagesData?.[openedFormId]) {
        dependencies.appStore.removePage(openedFormId)
      }
    }

    const pageEntry = dependencies.appStore.pagesData?.[slotId] as
      | { $isAdjacentWindow?: boolean }
      | undefined
    const isAdjacentSlot = pageEntry?.$isAdjacentWindow === true
    const isDialogSlot = isDialogPageSlot(
      {
        dialogIds: dependencies.appStore.dialogIds ?? [],
        dialogPageSlotIds: dependencies.appStore.dialogPageSlotIds ?? [],
        pagesData: dependencies.appStore.pagesData ?? {},
      },
      slotId,
    )

    if (isDialogSlot && options?.schemaUnchanged) {
      this.ensureSameWindowActivateEvent(slotId, dependencies)
      return
    }

    const stableParent = this.resolveStableParentForChildOpen(parentPageId, dependencies)
    if (!isAdjacentSlot && !isDialogSlot) {
      const tabsStore = useDocumentTabsStore()
      tabsStore.openOrFocusDocumentTab(slotId, pageData ?? null, 'document')
      dependencies.appStore.setPendingPageNavigation?.(slotId, stableParent ?? undefined)
      dependencies.appStore.updatePageId?.(slotId)
    }
    this.ensureSameWindowActivateEvent(slotId, dependencies)
    dispatchSameWindowReplace(slotId)

    if (!isAdjacentSlot && dependencies.appStore.clearAdjacentForParent) {
      dependencies.appStore.clearAdjacentForParent(slotId)
    }
  }

  private scheduleSameWindowDocumentRoute(
    slotId: string,
    context: ActionContext,
    dependencies?: ActionDependencies,
  ): void {
    if (!dependencies?.router || !dependencies?.appStore) return
    const id = String(slotId ?? '').trim()
    if (!id) return
    if (
      shouldSkipDocumentNavigationForSlot(
        {
          dialogIds: dependencies.appStore.dialogIds ?? [],
          dialogPageSlotIds: dependencies.appStore.dialogPageSlotIds ?? [],
          pagesData: dependencies.appStore.pagesData ?? {},
        },
        id,
      )
    ) {
      return
    }

    const endScheduleNavigationMeasure = startWorkflowMeasure(
      'ActionService.callDoAction.scheduleRouterPush',
      {
        actionId: context.id,
        formId: id,
        routeName: 'document',
        windowType: 'SAME_WINDOW',
      },
    )
    setTimeout(() => {
      void (async () => {
        try {
          const endRouterPushMeasure = startWorkflowMeasure('ActionService.callDoAction.routerPush', {
            actionId: context.id,
            formId: id,
            routeName: 'document',
            windowType: 'SAME_WINDOW',
          })
          try {
            await dependencies.router!.push({ name: 'document', params: { id } })
          } finally {
            endRouterPushMeasure()
          }
        } catch (error) {
          console.warn(
            '[ActionService] [callDoAction] Failed to navigate after SAME_WINDOW replace:',
            error,
          )
        }
      })()
    }, 0)
    endScheduleNavigationMeasure()
  }

  /**
   * SAME_WINDOW + document transfer без response.form: полная загрузка формы по новому sessionId.
   */
  private async refetchSameWindowPageAfterDocumentTransfer(
    targetPageId: string,
    response: Record<string, unknown>,
    currentData: ExchangeData,
    context: ActionContext,
    dependencies?: ActionDependencies,
  ): Promise<Record<string, unknown> | null> {
    const appStore = dependencies?.appStore
    if (!appStore) return null

    const sessionId =
      response.sessionId != null && String(response.sessionId).trim() !== ''
        ? String(response.sessionId)
        : null
    if (!sessionId) return null

    const platform = context.platform || appStore.currentPlatform?.value || 'WF'
    const openedFormId =
      resolveSameWindowOpenedFormId(response) || String(context.formId ?? targetPageId)
    const fields = currentData?.fields?.fields ?? {}

    const payload = createPlatformActionParams({
      sessionId,
      isClone: false,
      type: platform,
      actionType: 1,
      actionId: openedFormId,
      clientData: createSafeClientData({ fields }),
    })

    const parentPage = appStore.pagesData?.[targetPageId] as Record<string, unknown> | undefined
    const fullPage = (await coreService.getWindowData(payload, {
      ...(parentPage ?? {}),
      sessionId,
    })) as Record<string, unknown> | null

    if (!fullPage?.form) {
      return null
    }

    normalizeDialogPageUniDataFieldAliases(fullPage)
    if (
      isDialogPageSlot(
        {
          dialogIds: appStore.dialogIds ?? [],
          dialogPageSlotIds: appStore.dialogPageSlotIds ?? [],
          pagesData: appStore.pagesData ?? {},
        },
        targetPageId,
      )
    ) {
      fullPage.$isDialog = true
    }

    const parentPageSlotForOpen =
      context.parentPageId != null && String(context.parentPageId).trim() !== ''
        ? String(context.parentPageId).trim()
        : ''
    let parentPageId =
      parentPageSlotForOpen && parentPageSlotForOpen !== String(targetPageId)
        ? parentPageSlotForOpen
        : undefined
    parentPageId = this.resolveStableParentForChildOpen(parentPageId, dependencies)

    appStore.setPageData(targetPageId, fullPage, parentPageId)
    this.prepareMainAreaSameWindowReplace(
      targetPageId,
      fullPage,
      parentPageId ?? context.parentPageId ?? null,
      dependencies,
      {
        openedFormId:
          fullPage.status != null && Number(fullPage.status) > 0
            ? String(fullPage.status)
            : openedFormId,
      },
    )
    this.scheduleSameWindowDocumentRoute(targetPageId, context, dependencies)
    return fullPage
  }

  /**
   * Работа с окном по windowType: NEW_DIALOG, NEW_WINDOW или SAME_WINDOW.
   * TODO: уточнить! same_window?
   */
  private async handleWindowType(
    context: ActionContext,
    router: any,
    appStore: AppStore,
    dependencies?: ActionDependencies,
  ): Promise<void> {
    const { windowType, transfer } = context

    if (!windowType || !transfer) {
      console.warn('[ActionService] Window type or transfer is missing')
      return
    }

    switch (windowType) {
      case 'NEW_DIALOG':
        await this.openNewDialog(transfer, context, appStore, dependencies)
        break

      case 'NEW_WINDOW':
        await this.openNewWindow(transfer, router)
        break

      case 'SAME_WINDOW':
        break

      case 'MAIN_FRAME':
        break

      default:
        console.warn(`[ActionService] Unknown window type: ${windowType}`)
        break
    }
  }

  /**
   * Вызывает coreService.elementAction — основной API для выполнения action на бэкенде.
   * Возвращает changed_data (если он присутствует в jsResult ответа), чтобы затем можно было
   * применить изменения к текущей форме (особенно при windowType === 'SAME_WINDOW').
   * TODO: currentData передается через context.params!
   * TODO: проверить merge
   */
  /**
   * Корневой onLoad с web_client_exec выполняется до setPageData (поля + states в ответе).
   */
  private async prepareOpenFormResponse(
    response: Record<string, unknown>,
    context: ActionContext,
    dependencies: ActionDependencies | undefined,
    openedFormId: string,
  ): Promise<Record<string, unknown>> {
    const appStore = dependencies?.appStore
    if (!response?.form || !appStore) {
      return response
    }

    const numericFormId = parseInt(openedFormId, 10)
    if (Number.isFinite(numericFormId)) {
      const { parseService } = await import('@/services/parse/parse.service')
      response = {
        ...response,
        form: parseService.getForm(response.form, numericFormId),
      }
    }

    return applyWebClientFormOnLoadBeforeBuild(
      response,
      { ...context, formId: openedFormId },
      appStore,
      dependencies,
      (params) => this.invokeNestedElementActionFromClientScript(params),
      openedFormId,
    )
  }

  private async ensureOpenFormPreBuild(
    pageData: Record<string, unknown>,
    openedFormId: string,
    dependencies: ActionDependencies | undefined,
    options?: { parseForm?: boolean },
  ): Promise<Record<string, unknown>> {
    const appStore = dependencies?.appStore
    if (!pageData?.form || !appStore) {
      return pageData
    }
    const { ensureWebClientOnLoadBeforeOpenForm } = await import(
      '@/services/action/webClientFormOnLoadBeforeBuild'
    )
    return ensureWebClientOnLoadBeforeOpenForm(pageData, {
      appStore,
      openedFormId,
      parseForm: options?.parseForm ?? false,
      parseActionId: openedFormId,
      dependencies,
      invokeNestedElementActionFromClientScript: (params) =>
        this.invokeNestedElementActionFromClientScript(params),
    })
  }

  private async callDoAction(
    transfer: string,
    context: ActionContext,
    currentData: any,
    dependencies?: ActionDependencies,
  ): Promise<ExchangeData | null> {
    const endMeasure = startWorkflowMeasure('ActionService.callDoAction', {
      actionId: context.id,
      formId: context.formId,
      transfer,
      transferType: context.transferType,
    })
    const appStore = dependencies?.appStore

    try {
      const platform = context.platform || dependencies?.appStore?.currentPlatform?.value || 'WF'

      // Resolve the slot that owns this action (dialog, adjacent, main tab — not always currentPageId).
      const resolvedSlotId =
        actionScript.resolvePagesDataSlotId(context, dependencies) ||
        appStore?.currentPageId ||
        Object.keys(appStore?.pagesData || {})[0]
      const pageDataFromStore = resolvedSlotId
        ? appStore?.pagesData?.[resolvedSlotId]
        : undefined
      const pageData =
        pageDataFromStore ||
        (dependencies?.pageData as Record<string, unknown> | undefined) ||
        {}
      const currentFormId =
        context.formId ||
        dependencies?.pageRegistryFormId ||
        appStore?.currentFormFid ||
        resolvedSlotId

      const isReturnAction =
        context.transferType &&
        (String(context.transferType).match(/^return_/i) ||
          (RETURN_TRANSFER_TYPE_CODES as readonly string[]).includes(String(context.transferType)))

      let effectiveSessionId: string | null = null
      if (isReturnAction) {
        const pageEntries = Object.entries(appStore?.pagesData || {})

        if (resolvedSlotId && pageData?.sessionId) {
          effectiveSessionId = String(pageData.sessionId)
        } else if (resolvedSlotId) {
          const slotPage = appStore?.pagesData?.[resolvedSlotId]
          if (slotPage?.sessionId) {
            effectiveSessionId = String(slotPage.sessionId)
          }
        }

        if (!effectiveSessionId) {
          let mostRecentPage: [string, any] | null = null
          let highestPageId = -1

          for (const [pageId, page] of pageEntries) {
            const numericPageId = parseInt(pageId, 10)
            if (!isNaN(numericPageId) && numericPageId > highestPageId) {
              highestPageId = numericPageId
              mostRecentPage = [pageId, page]
            }
          }

          if (mostRecentPage && mostRecentPage[1]?.sessionId) {
            effectiveSessionId = mostRecentPage[1].sessionId
          }

          // Fallback to getCurrentSessionId
          if (!effectiveSessionId) {
            effectiveSessionId = this.getCurrentSessionId(appStore)
          }
        }

        if (!effectiveSessionId && isReturnAction) {
          effectiveSessionId = null
        }
      } else {
        const depSession =
          dependencies?.pageData?.sessionId != null
            ? String(dependencies.pageData.sessionId)
            : ''
        effectiveSessionId =
          depSession ||
          (pageData?.sessionId != null ? String(pageData.sessionId) : null) ||
          this.getCurrentSessionId(appStore)
      }

      const windowTypeRaw = context.data?.window__type?._text || context.windowType || 'SAME_WINDOW'
      const windowType =
        typeof windowTypeRaw === 'string' ? windowTypeRaw.toUpperCase() : 'SAME_WINDOW'
      const actionId = context.data?._attributes?.id || context.id
      const intendedDialogFormId = String(transfer ?? '').trim()
      const hasFixedDialogSlot =
        windowType === 'NEW_DIALOG' &&
        intendedDialogFormId !== '' &&
        intendedDialogFormId !== '0'
      const dialogOpenEpochAtStart =
        hasFixedDialogSlot && appStore?.getDialogSlotEpoch
          ? appStore.getDialogSlotEpoch(intendedDialogFormId)
          : 0

      const previewSourceSlot =
        dependencies?.pageDataSlot ??
        resolvedSlotId ??
        appStore?.currentPageId ??
        ''
      const useAdjacentSplit = shouldUseAdjacentSplitPreview(
        context.doActionForAdjacentWindow,
        previewSourceSlot,
        (appStore?.pagesData ?? {}) as Record<string, unknown>,
      )

      let transferTypeValue = context.transferType
      if (transferTypeValue) {
        const normalizedType = String(transferTypeValue).toLowerCase()
        const enumToCodeMap: Record<string, string> = {
          return_none: '5',
          return_replace: '6',
          return_prev_none: '7',
          return_prev_replace: '8',
          return_prev_cur_data_none: '9',
          return_prev_cur_data_replace: '10',
          return_to_last_form: '11',
        }
        if (enumToCodeMap[normalizedType]) {
          transferTypeValue = enumToCodeMap[normalizedType]
        }
      }

      const selectedData = context.selectedData || {}
      const selectedFieldsData = this.convertRowDataToFields(selectedData)

      // Merge form data with selected data and script-updated currentData
      const formDataForAction = {
        ...selectedFieldsData,
        ...currentData.fields?.fields,
      }

      const actionContext = {
        actionItem: {
          ...context.data,
          _attributes: {
            ...context.data?._attributes,
            id: actionId,
          },
          transfer: { _text: transfer },
          transferType: transferTypeValue ? { _text: transferTypeValue } : undefined,
          window__type: { _text: windowType },
          selected: {
            fields: selectedFieldsData,
          },
        },
        form: formDataForAction,
        formId: context.formId,
        panelId: context.panelId,
        elementId: context.elementId,
        pageData: {
          ...this.stripClientOnlyPageDataForActionContext(
            pageData as Record<string, unknown> | undefined,
          ),
          sessionId: effectiveSessionId || pageData.sessionId,
        },
        platform: platform,
        params: {
          clientData: currentData,
          type: platform,
          actionType: 0,
          formState: {
            params: {
              ...(currentData?.clientData?.params?.documentView && {
                documentView: currentData.clientData.params.documentView
              })
            }
          },
          sessionId: effectiveSessionId,
          ...(useAdjacentSplit ? { isClone: true } : {}),
          currentFid: currentFormId,
        },
      }

      let response
      try {
        response = await coreService.elementAction(actionContext as any)
      } catch (error: any) {
        console.error('[callDoAction] Error calling elementAction:', error)
        // Retry with new core if core was cleared
        const isCoreError =
          error?.response?.data?.message?.includes('Core cannot be null') ||
          error?.response?.data?.message?.includes('core is null') ||
          error?.response?.data?.developerMessage?.includes('Core cannot be null') ||
          error?.response?.data?.developerMessage?.includes('core is null') ||
          error?.message?.includes('Core cannot be null') ||
          error?.message?.includes('core is null')

        if (isCoreError) {
          actionContext.params.sessionId = null
          actionContext.pageData.sessionId = null
          try {
            response = await coreService.elementAction(actionContext as any)
          } catch (retryError: any) {
            throw retryError
          }
        } else {
          throw error
        }
      }

      this.fillEmptyUrlsInResponseFromRequest(response, currentData)

      // Handle response based on window type
      const isSameWindow = windowType === 'SAME_WINDOW'
      const transferTypeNorm = String(transferTypeValue ?? context.transferType ?? '')
        .trim()
        .toLowerCase()
      const applySlotId =
        actionScript.resolvePagesDataSlotId(context, dependencies) ||
        dependencies?.appStore?.currentPageId ||
        ''
      const isInPlaceDialogSave =
        appStore != null &&
        isSameWindow &&
        isDialogPageSlot(
          {
            dialogIds: appStore.dialogIds ?? [],
            dialogPageSlotIds: appStore.dialogPageSlotIds ?? [],
            pagesData: appStore.pagesData ?? {},
          },
          applySlotId,
        ) &&
        (transferTypeNorm === 'context_call' || transferTypeNorm === 'context_call_change_doc')

      // TODO: вынести и описать логику ниже
      if (isReturnAction && response?.stack && response.stack.length > 0) {
        const stackFormId = String(response.stack[0])
        if (dependencies?.appStore?.focusFormByFid) {
          dependencies.appStore.focusFormByFid(stackFormId)
        } else if (dependencies?.router) {
          await dependencies.router.push({ name: 'platform', params: { id: stackFormId } })
        }
        return null
      }

      if (
        isReturnAction &&
        response &&
        !response.form &&
        response.stack &&
        response.stack.length > 0
      ) {
        const stackFormId = String(response.stack[0])
        if (dependencies?.appStore?.focusFormByFid) {
          dependencies.appStore.focusFormByFid(stackFormId)
        } else if (dependencies?.router) {
          await dependencies.router.push({ name: 'platform', params: { id: stackFormId } })
        }
        return null
      }

      // Swing parity: для context_call_and_return без stack переходим к предыдущей форме или home
      const isContextCallAndReturn =
        transferTypeNorm === 'context_call_and_return' ||
        transferTypeNorm === 'call_chain_and_return' ||
        transferTypeNorm.includes('_and_return')

      if (
        isContextCallAndReturn &&
        response &&
        !response.form &&
        (!response.stack || response.stack.length === 0) &&
        response.status != null &&
        Number(response.status) === 0
      ) {
       // Помечаем страницу как закрывающуюся перед навигацией
        const currentPageId = resolvedSlotId || appStore?.currentPageId
        if (currentPageId && appStore?.pagesData?.[currentPageId]) {
          const pageData = appStore.pagesData[currentPageId] as Record<string, unknown>
          pageData.$isClosing = true
        }

        // Пытаемся найти предыдущую форму в истории
        const allPages = Object.keys(appStore?.pagesData || {})
        const otherPages = allPages.filter(
          id => id !== currentPageId &&
                id !== 'mainFrame' &&
                id !== '__main_frame__' &&
                !id.startsWith('__')
        )

        if (otherPages.length > 0 && dependencies?.appStore?.focusFormByFid) {
          // Есть другие открытые формы - переключаемся на последнюю
          const targetFormId = otherPages[otherPages.length - 1]
          dependencies.appStore.focusFormByFid(targetFormId)
          return null
        } else if (dependencies?.router) {
          // Нет других форм - идём на home
          await dependencies.router.push({ name: 'home' })
          return null
        }
      }

      if (this.shouldSkipDoActionResponseUiApply(context, dependencies, transferTypeValue)) {
        const changedData = sanitizeCheckboxSelectionChangedData(
          response?.jsResult?.changed_data,
          context,
          dependencies,
        ) as ExchangeData | undefined
        return changedData ?? null
      }

      if (response?.form && dependencies?.appStore && !isInPlaceDialogSave) {
        let formId = resolveSameWindowAuthoritativeFormId(response as Record<string, unknown>)
        if (!formId) {
          formId = response.form?._attributes?.id
        }
        if (!formId && response.status) {
          const statusNum = parseInt(String(response.status), 10)
          if (!isNaN(statusNum)) {
            formId = response.status.toString()
          }
        }

        if (!formId && actionId) {
          const actionIdNum = parseInt(String(actionId), 10)
          if (!isNaN(actionIdNum)) {
            formId = String(actionId)
          }
        }

        if (!formId && response.stack && response.stack.length > 0) {
          formId = String(response.stack[0])
        }

        if (!formId) {
          formId = response.form?.originalBranchId?._text
        }

        if (!formId) {
          formId = String(actionId)
        }

        response = (await this.prepareOpenFormResponse(
          response as Record<string, unknown>,
          context,
          dependencies,
          String(formId),
        )) as typeof response

        if (windowType === 'MAIN_FRAME') {
          const processedData = await buildMainFramePageDataFromActionResponse(
            response as Record<string, unknown>,
            {
              formIdHint: formId != null ? String(formId) : undefined,
              platform,
              appStore: dependencies.appStore,
              dependencies,
              invokeNestedElementActionFromClientScript: (params) =>
                this.invokeNestedElementActionFromClientScript(params),
            },
          )
          dependencies.appStore.setPageData(MAIN_FRAME_SLOT_ID, processedData, null)
          if (dependencies?.router && dependencies.router.currentRoute.value.name !== 'home') {
            await dependencies.router.push({ name: 'home' })
          }
          const changedData = sanitizeCheckboxSelectionChangedData(
            response?.jsResult?.changed_data,
            context,
            dependencies,
          ) as ExchangeData | undefined
          return changedData ?? null
        }

        if (useAdjacentSplit) {
          const adjToken =
            response?.sessionId != null
              ? String(response.sessionId)
              : `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
          formId = `${formId}_adj_${adjToken}`
        }

        const parentPageSlotForOpen =
          context.parentPageId != null && String(context.parentPageId).trim() !== ''
            ? String(context.parentPageId).trim()
            : ''
        const isMainAreaSameWindow = !useAdjacentSplit && windowType === 'SAME_WINDOW'
        const pageSlotId = useAdjacentSplit
          ? formId
          : isMainAreaSameWindow
            ? resolveSameWindowPageSlotId({
                sourcePageSlotId:
                  dependencies?.pageDataSlot ??
                  dependencies.appStore?.currentPageId ??
                  parentPageSlotForOpen,
                openedFormId: formId,
              })
            : resolveNewWindowPageSlotId(formId, {
                parentPageSlotId: parentPageSlotForOpen,
                sessionId: response.sessionId,
                windowType,
              })
        if (windowType === 'NEW_WINDOW' || windowType === 'NEW_DIALOG') {
          stampCloseRootsOpenedSlotId(context, windowType === 'NEW_DIALOG' ? formId : pageSlotId)
        }

        const closeCfgFromResponse =
          response.clientDialogCloseConfig && typeof response.clientDialogCloseConfig === 'object'
            ? (response.clientDialogCloseConfig as Record<string, unknown>)
            : {}

        if (windowType === 'NEW_DIALOG') {
          const parentSlotAtOpen = resolveParentSlotAtDialogOpen(
            dependencies.appStore,
            dependencies?.pageDataSlot,
            context.parentPageId,
            {
              windowInstanceId: dependencies?.pageWindowInstanceId,
              registryFormId: dependencies?.pageRegistryFormId ?? context.formId,
            },
          )
          const dialogClosedScriptText =
            context.event?.dialogClosed ??
            getDialogClosedScriptText(context.data as Record<string, unknown>) ??
            closeCfgFromResponse.onCloseDialogEvent
          const dialogCloseConfig = augmentDialogCloseConfigMergeScope(
            buildDialogCloseConfigForStore(closeCfgFromResponse, {
              onCloseDialogEvent: dialogClosedScriptText,
              onCloseDialogWebClientExec: resolveOnCloseDialogWebClientExec({
                actionItem: context.data as Record<string, unknown>,
                scriptText:
                  typeof dialogClosedScriptText === 'string' ? dialogClosedScriptText : null,
                responseFlag: closeCfgFromResponse.onCloseDialogWebClientExec,
              }),
              ...buildDialogClosedNReturnCloseOverrides({
                actionItem: context.data as Record<string, unknown>,
                responseCfg: closeCfgFromResponse,
              }),
              pageDataSlot: parentSlotAtOpen,
              onCloseDialogParentSlotId: parentSlotAtOpen,
            }),
            {
              scriptText: dialogClosedScriptText,
              actionContext: {
                data: context.data,
                actionItem: context.data,
                formId: context.formId,
                panelId: context.panelId,
                elementId: context.elementId,
                level: context.level,
              },
              dependencies,
            },
          )
          const endScheduleDialogMeasure = startWorkflowMeasure('ActionService.callDoAction.scheduleDialogOpen', {
            actionId: context.id,
            formId,
            sessionId: response.sessionId,
          })
          try {
            if (
              shouldDropStaleDialogOpenAfterClose(
                {
                  dialogIds: dependencies.appStore.dialogIds ?? [],
                  dialogSlotEpoch: dependencies.appStore.dialogSlotEpoch ?? {},
                },
                intendedDialogFormId,
                formId,
                dialogOpenEpochAtStart,
              )
            ) {
              const changedData = sanitizeCheckboxSelectionChangedData(
                response?.jsResult?.changed_data,
                context,
                dependencies,
              ) as ExchangeData | undefined
              return changedData ?? null
            }

            const endAddDialogMeasure = startWorkflowMeasure('ActionService.callDoAction.addDialog', {
              actionId: context.id,
              formId,
              sessionId: response.sessionId,
            })
            try {
              dependencies.appStore.addDialog({
                id: formId,
                sessionId: response.sessionId,
                config: dialogCloseConfig,
                data: response,
              })
            } finally {
              endAddDialogMeasure()
            }

            const endSetDialogPageDataMeasure = startWorkflowMeasure('ActionService.callDoAction.setDialogPageData', {
              actionId: context.id,
              formId,
              sessionId: response.sessionId,
            })
            try {
              normalizeDialogPageUniDataFieldAliases(response as Record<string, unknown>)
              ;(response as Record<string, unknown>).$isDialog = true
              // Флаг для различения ADD/EDIT режимов в subordinate forms
              if (context.selectedData || context.tilesRowScope?.getRowData?.()) {
                (response as Record<string, unknown>).$wasOpenedWithSelection = true
              }
              dependencies.appStore.setPageData(
                formId,
                this.attachBoundDocumentIdentityToPagePayload(
                  response as Record<string, unknown>,
                  context,
                  {
                    openingCurrentData: currentData,
                    inheritFromPageData: pageData,
                  },
                ),
              )
            } finally {
              endSetDialogPageDataMeasure()
            }
          } finally {
            endScheduleDialogMeasure()
          }
        } else {
          let parentPageId =
            parentPageSlotForOpen && parentPageSlotForOpen !== String(pageSlotId)
              ? parentPageSlotForOpen
              : context.parentPageId && String(context.parentPageId) !== String(formId)
                ? context.parentPageId
                : undefined
          parentPageId = this.resolveStableParentForChildOpen(parentPageId, dependencies)

          // SAME_WINDOW adjacent split: remove stale preview slot for same parent.
          if (useAdjacentSplit) {
            const windowTypeRaw =
              context.data?.window__type?._text || context.windowType || 'SAME_WINDOW'
            const windowTypeForAdj =
              typeof windowTypeRaw === 'string' ? windowTypeRaw.toUpperCase() : 'SAME_WINDOW'
            if (windowTypeForAdj === 'SAME_WINDOW' && parentPageId) {
              const lastByParent = (dependencies.appStore as any).lastAdjacentPageIdByParent
              const previousAdjacentId =
                lastByParent?.value?.[parentPageId] ?? lastByParent?.[parentPageId]
              if (previousAdjacentId && previousAdjacentId !== formId) {
                const previousData = dependencies.appStore.pagesData?.[previousAdjacentId]
                const isAdjacent = previousData?.$isAdjacentWindow === true
                const sameParent = previousData?.$parentPageId === parentPageId
                if (isAdjacent && sameParent) {
                  dependencies.appStore.removePage(previousAdjacentId)
                }
              }
            }
          }
          capturePageSlotSnapshotBeforeChildWindow(
            dependencies?.appStore,
            parentPageId,
            windowType,
          )

          const pageSnapshotBeforeSameWindow = isMainAreaSameWindow
            ? (dependencies.appStore.pagesData[pageSlotId] as Record<string, unknown> | undefined)
            : undefined

          const endSetPageDataMeasure = startWorkflowMeasure('ActionService.callDoAction.setPageData', {
            actionId: context.id,
            formId: pageSlotId,
            parentPageId,
            adjacent: useAdjacentSplit,
          })
          try {
            const refreshBoundFromPayload =
              isDistinctPlatformSessionChange(pageData?.sessionId, response?.sessionId) ||
              documentIdentityChangedOnPage(
                pageData as Record<string, unknown> | undefined,
                currentData,
              )
            const pagePayload = this.attachBoundDocumentIdentityToPagePayload(
              attachDialogCloseConfigToPageData(
                response as Record<string, unknown>,
                buildDialogCloseConfigForStore(
                  closeCfgFromResponse,
                  {
                    onCloseDialogEvent:
                      context.event?.dialogClosed ??
                      getDialogClosedScriptText(context.data as Record<string, unknown>) ??
                      closeCfgFromResponse.onCloseDialogEvent,
                    onCloseDialogWebClientExec: resolveOnCloseDialogWebClientExec({
                      actionItem: context.data as Record<string, unknown>,
                      scriptText:
                        context.event?.dialogClosed ??
                        getDialogClosedScriptText(context.data as Record<string, unknown>) ??
                        (closeCfgFromResponse.onCloseDialogEvent as string | undefined) ??
                        null,
                      responseFlag: closeCfgFromResponse.onCloseDialogWebClientExec,
                    }),
                    ...buildDialogClosedNReturnCloseOverrides({
                      actionItem: context.data as Record<string, unknown>,
                      responseCfg: closeCfgFromResponse,
                    }),
                    pageDataSlot: parentPageId ?? parentPageSlotForOpen,
                    onCloseDialogParentSlotId: parentPageId ?? parentPageSlotForOpen,
                  },
                ),
              ),
              context,
              {
                openingCurrentData: currentData,
                inheritFromPageData: pageData,
                refreshBoundFromPayload,
              },
            )
            if (isDialogPageSlot(
              {
                dialogIds: dependencies.appStore.dialogIds ?? [],
                dialogPageSlotIds: dependencies.appStore.dialogPageSlotIds ?? [],
                pagesData: dependencies.appStore.pagesData ?? {},
              },
              pageSlotId,
            )) {
              normalizeDialogPageUniDataFieldAliases(pagePayload)
              pagePayload.$isDialog = true
            }
            const targetDialogSlot = isDialogPageSlot(
              {
                dialogIds: dependencies.appStore.dialogIds ?? [],
                dialogPageSlotIds: dependencies.appStore.dialogPageSlotIds ?? [],
                pagesData: dependencies.appStore.pagesData ?? {},
              },
              pageSlotId,
            )
            if (
              isMainAreaSameWindow &&
              targetDialogSlot &&
              sameWindowResponseNeedsFullFormRefetch(
                pageSnapshotBeforeSameWindow,
                response as Record<string, unknown>,
                pageSlotId,
              )
            ) {
              const refetched = await this.refetchSameWindowPageAfterDocumentTransfer(
                pageSlotId,
                response as Record<string, unknown>,
                currentData,
                context,
                dependencies,
              )
              if (refetched) {
                const changedData = sanitizeCheckboxSelectionChangedData(
                  response?.jsResult?.changed_data,
                  context,
                  dependencies,
                ) as ExchangeData | undefined
                return changedData ?? null
              }
            }
            dependencies.appStore.setPageData(
              pageSlotId,
              pagePayload,
              parentPageId,
              useAdjacentSplit,
            )
          } finally {
            endSetPageDataMeasure()
          }

          const incomingStatesFullForm = normalizeExchangeStates(response)
          const schemaUnchangedFullForm = sameWindowSchemaUnchangedOnPage(
            pageSnapshotBeforeSameWindow,
            response as Record<string, unknown>,
            pageSlotId,
          )
          const targetDialogSlotFullForm = isDialogPageSlot(
            {
              dialogIds: dependencies.appStore.dialogIds ?? [],
              dialogPageSlotIds: dependencies.appStore.dialogPageSlotIds ?? [],
              pagesData: dependencies.appStore.pagesData ?? {},
            },
            pageSlotId,
          )

          if (isMainAreaSameWindow && targetDialogSlotFullForm) {
            let storedFullFormPage = dependencies.appStore.pagesData[pageSlotId] as
              | Record<string, unknown>
              | undefined
            const usesServerOnLoad =
              storedFullFormPage?.form != null &&
              !shouldRunRootFormOnLoadOnClientBeforeBuild(storedFullFormPage.form)

            if (schemaUnchangedFullForm && usesServerOnLoad && storedFullFormPage) {
              await runStartupServerRootOnLoadIfNeeded(
                dependencies.appStore,
                pageSlotId,
                storedFullFormPage,
              )
              storedFullFormPage = dependencies.appStore.pagesData[pageSlotId] as
                | Record<string, unknown>
                | undefined
            }

            const statesToApply = normalizeExchangeStates(storedFullFormPage ?? response)
            if (statesToApply.length > 0 && dependencies?.formStates?.processStates) {
              await applyIncomingExchangeStates({
                appStore: dependencies.appStore,
                slotId: pageSlotId,
                registryFormId:
                  dependencies?.pageRegistryFormId ??
                  resolveDialogSlotRegistryFormId(storedFullFormPage, pageSlotId),
                incomingStates: statesToApply,
                changedData: (storedFullFormPage?.jsResult as Record<string, unknown> | undefined)
                  ?.changed_data,
                processStates: dependencies.formStates.processStates,
                setPageDataSource: 'ActionService.callDoAction.sameWindowFullForm',
              })
            }
          } else if (
            isMainAreaSameWindow &&
            incomingStatesFullForm.length > 0 &&
            dependencies?.formStates?.processStates
          ) {
            const storedFullFormPage = dependencies.appStore.pagesData[pageSlotId] as
              | Record<string, unknown>
              | undefined
            await applyIncomingExchangeStates({
              appStore: dependencies.appStore,
              slotId: pageSlotId,
              registryFormId:
                dependencies?.pageRegistryFormId ??
                resolveDialogSlotRegistryFormId(storedFullFormPage, pageSlotId),
              incomingStates: incomingStatesFullForm,
              changedData: (storedFullFormPage?.jsResult as Record<string, unknown> | undefined)
                ?.changed_data,
              processStates: dependencies.formStates.processStates,
              setPageDataSource: 'ActionService.callDoAction.sameWindowFullForm',
            })
          }

          if (!useAdjacentSplit && windowType === 'NEW_WINDOW') {
            this.prepareMainAreaDocumentNavigation(
              pageSlotId,
              response as Record<string, unknown>,
              parentPageId ?? context.parentPageId ?? null,
              dependencies,
            )
            if (dependencies?.router) {
              const endScheduleNavigationMeasure = startWorkflowMeasure('ActionService.callDoAction.scheduleRouterPush', {
                actionId: context.id,
                formId: pageSlotId,
                routeName: 'document',
              })
              setTimeout(() => {
                void (async () => {
                  try {
                    const endRouterPushMeasure = startWorkflowMeasure('ActionService.callDoAction.routerPush', {
                      actionId: context.id,
                      formId: pageSlotId,
                      routeName: 'document',
                    })
                    try {
                      await dependencies.router.push({ name: 'document', params: { id: pageSlotId } })
                    } finally {
                      endRouterPushMeasure()
                    }
                  } catch (error) {
                    console.warn(
                      '[ActionService] [callDoAction] Failed to navigate to opened form:',
                      error,
                    )
                  }
                })()
              }, 0)
              endScheduleNavigationMeasure()
            }
          } else if (isMainAreaSameWindow) {
            const pageDataAfterSet = dependencies.appStore.pagesData[pageSlotId] as
              | Record<string, unknown>
              | undefined
            this.prepareMainAreaSameWindowReplace(
              pageSlotId,
              pageDataAfterSet ?? (response as Record<string, unknown>),
              parentPageId ?? context.parentPageId ?? null,
              dependencies,
              {
                openedFormId: formId,
                schemaUnchanged: sameWindowSchemaUnchangedOnPage(
                  pageSnapshotBeforeSameWindow,
                  response as Record<string, unknown>,
                  pageSlotId,
                ),
              },
            )
            this.scheduleSameWindowDocumentRoute(pageSlotId, context, dependencies)
          }
        }
      } else if (windowType === 'NEW_DIALOG' && response && !response.form) {
        // call_chain_*_explicit_view: view comes from chain/response.form. Opening by
        // transfer (often a chain id) creates a duplicate/empty dialog when the chain
        // already finished without a view (e.g. email already confirmed).
        const transferTypeLc = String(context.transferType ?? '').toLowerCase()
        const explicitViewWithoutForm =
          transferTypeLc === 'call_chain_and_explicit_view' ||
          transferTypeLc === 'context_call_and_explicit_view' ||
          transferTypeLc === 'context_call_and_conditional_explicit_view'
        if (!explicitViewWithoutForm) {
          const dialog = dependencies?.dialog || useDialog()
          await dialog.openDialog(transfer, {
            sessionId: null,
            form: formDataForAction,
            platform,
          })
        }
      } else if (isSameWindow && response && dependencies?.appStore && (!response.form || isInPlaceDialogSave)) {
        // Fallback for doActionForAdjacentWindow: backend may return status/stack/sessionId but form=null when status<1.
        // Try to get form from pagesData (e.g. from a previous open of same form via double-click) and open split anyway.
        // Skip when status is 0 - that means no form opened, formId "0" would use wrong structure.
        // TODO: проверить merge
        if (useAdjacentSplit && response?.sessionId && (response?.status != null || response?.stack?.length)) {
          const statusVal = response?.status != null ? Number(response.status) : NaN
          const stackFirst = response?.stack?.[0] != null ? Number(response.stack[0]) : NaN
          const formIdFromResponse = (!isNaN(statusVal) && statusVal > 0) ? String(statusVal)
            : (!isNaN(stackFirst) && stackFirst > 0) ? String(stackFirst) : ''
          if (!formIdFromResponse) { /* skip fallback - invalid form id */ } else {
            const pagesData = dependencies?.appStore?.pagesData || {}
            const foundPage = Object.values(pagesData).find((p: any) => {
              const fid = p?.status ?? p?.form?.form?._attributes?.id ?? p?.form?._attributes?.id ?? p?.form?._attributes?.fid
              return fid != null && String(fid) === formIdFromResponse
            }) as { form?: unknown } | undefined
            const cachedForm = foundPage?.form
            if (cachedForm && typeof cachedForm === 'object' && Object.keys(cachedForm).length > 0) {
              let responseWithForm = { ...response, form: cachedForm } as Record<string, unknown>
              const adjToken = String(response.sessionId)
              const formId = `${formIdFromResponse}_adj_${adjToken}`
              responseWithForm = await this.ensureOpenFormPreBuild(
                responseWithForm,
                formIdFromResponse,
                dependencies,
              )
              const parentPageId =
                context.parentPageId && String(context.parentPageId) !== formId ? context.parentPageId : undefined
              dependencies.appStore.setPageData(formId, responseWithForm, parentPageId, true)
            }
          }
        }
        // ###############################
        const targetPageId =
          actionScript.resolvePagesDataSlotId(context, dependencies) ||
          dependencies.appStore.currentPageId ||
          context.formId?.toString()
        if (targetPageId) {
          const currentPageData = dependencies.appStore.pagesData[targetPageId]
          if (currentPageData) {
            const sessionChanged = isDistinctPlatformSessionChange(
              currentPageData.sessionId,
              response.sessionId,
              { isAdjacentWindow: useAdjacentSplit },
            )
            const transferTypeNorm = String(transferTypeValue ?? context.transferType ?? '')
              .trim()
              .toLowerCase()
            const isDocTransfer = this.isDocumentTransferType(transferTypeNorm)
            const documentChanged =
              isDocTransfer && documentIdentityChangedOnPage(currentPageData, currentData)

            const formSchemaChanged = sameWindowResponseNeedsFullFormRefetch(
              currentPageData as Record<string, unknown>,
              response as Record<string, unknown>,
              targetPageId,
            )

            if (
              !response.form &&
              !isInPlaceDialogSave &&
              !useAdjacentSplit &&
              (formSchemaChanged || (isDocTransfer && documentChanged))
            ) {
              const refetched = await this.refetchSameWindowPageAfterDocumentTransfer(
                targetPageId,
                response as Record<string, unknown>,
                currentData,
                context,
                dependencies,
              )
              if (refetched) {
                const changedData = sanitizeCheckboxSelectionChangedData(
                  response?.jsResult?.changed_data,
                  context,
                  dependencies,
                ) as ExchangeData | undefined
                return changedData ?? null
              }
            }

            const sanitizedChangedData = sanitizeCheckboxSelectionChangedData(
              response.jsResult?.changed_data,
              context,
              dependencies,
            )
            const sanitizedUniData = sanitizeCheckboxSelectionChangedData(
              response.uniData,
              context,
              dependencies,
            )

            const replaceAuthoritative = sessionChanged || documentChanged
            const replaceStatesAuthoritative = replaceAuthoritative || formSchemaChanged
            const preserveEmpty = {
              shouldPreserveEmptyIncoming: () => !replaceAuthoritative,
            }
            const mergedUniFields = replaceAuthoritative
              ? sanitizedUniData?.fields?.fields || {}
              : sanitizedUniData?.fields?.fields
                ? mergePageFieldMaps(
                    preserveEmpty,
                    currentPageData.uniData?.fields?.fields || {},
                    sanitizedUniData.fields.fields,
                  )
                : currentPageData.uniData?.fields?.fields
            const mergedChangedFields = replaceAuthoritative
              ? sanitizedChangedData?.fields?.fields || {}
              : sanitizedChangedData?.fields?.fields
                ? mergePageFieldMaps(
                    preserveEmpty,
                    currentPageData.jsResult?.changed_data?.fields?.fields || {},
                    sanitizedChangedData.fields.fields,
                  )
                : currentPageData.jsResult?.changed_data?.fields?.fields

            const updatedPageData = this.attachBoundDocumentIdentityToPagePayload(
              {
                ...currentPageData,
                sessionId: response.sessionId || currentPageData.sessionId,
                status: response.status !== undefined ? response.status : currentPageData.status,
                form:
                  isInPlaceDialogSave && response.form
                    ? response.form
                    : currentPageData.form,
                jsResult: response.jsResult
                  ? {
                    ...currentPageData.jsResult,
                    ...response.jsResult,
                    states: replaceStatesAuthoritative
                      ? response.jsResult.states ?? []
                      : response.jsResult.states || currentPageData.jsResult?.states || [],
                    changed_data: sanitizedChangedData
                      ? {
                          ...(replaceAuthoritative ? {} : currentPageData.jsResult?.changed_data || {}),
                          fields: {
                            ...(replaceAuthoritative
                              ? {}
                              : currentPageData.jsResult?.changed_data?.fields || {}),
                            fields: mergedChangedFields,
                          },
                        }
                      : replaceAuthoritative
                        ? undefined
                        : currentPageData.jsResult?.changed_data,
                  }
                  : currentPageData.jsResult,
                uniData: sanitizedUniData
                  ? {
                      ...(replaceAuthoritative ? {} : currentPageData.uniData || {}),
                      ...sanitizedUniData,
                      fields: {
                        ...(replaceAuthoritative ? {} : currentPageData.uniData?.fields || {}),
                        fields: mergedUniFields,
                      },
                    }
                  : currentPageData.uniData,
                currentChainData: response.currentChainData || currentPageData.currentChainData,
              },
              context,
              {
                inheritFromPageData: currentPageData,
                refreshBoundFromPayload: replaceAuthoritative && documentChanged,
                ...(replaceAuthoritative && documentChanged
                  ? {
                      fromExchangeFields: (currentData?.fields?.fields ??
                        currentData?.fields ??
                        {}) as Record<string, unknown>,
                    }
                  : {}),
              },
            )
            dependencies.appStore.setPageData(targetPageId, updatedPageData)

            const incomingStates = normalizeExchangeStates(response)
            if (incomingStates.length > 0 && dependencies?.formStates?.processStates) {
              const storedPageData = dependencies.appStore.pagesData[targetPageId] as typeof currentPageData
              await applyIncomingExchangeStates({
                appStore: dependencies.appStore,
                slotId: targetPageId,
                registryFormId: context.formId,
                incomingStates,
                changedData: storedPageData?.jsResult?.changed_data,
                processStates: dependencies.formStates.processStates,
                setPageDataSource: 'ActionService.callDoAction.sameWindowPartial',
              })
            }

            const isMainAreaSameWindow =
              !useAdjacentSplit && windowType === 'SAME_WINDOW' && !isInPlaceDialogSave
            if (isMainAreaSameWindow) {
              const parentPageSlotForOpen =
                context.parentPageId != null && String(context.parentPageId).trim() !== ''
                  ? String(context.parentPageId).trim()
                  : ''
              let parentPageIdForReplace =
                parentPageSlotForOpen && parentPageSlotForOpen !== String(targetPageId)
                  ? parentPageSlotForOpen
                  : undefined
              parentPageIdForReplace = this.resolveStableParentForChildOpen(
                parentPageIdForReplace,
                dependencies,
              )
              const openedFormIdRaw =
                response.status != null && Number(response.status) > 0
                  ? String(response.status)
                  : response.stack?.[0] != null && Number(response.stack[0]) > 0
                    ? String(response.stack[0])
                    : ''
              const pageDataAfterSet = dependencies.appStore.pagesData[targetPageId] as
                | Record<string, unknown>
                | undefined
              this.prepareMainAreaSameWindowReplace(
                targetPageId,
                pageDataAfterSet ?? updatedPageData,
                parentPageIdForReplace ?? context.parentPageId ?? null,
                dependencies,
                {
                  openedFormId: openedFormIdRaw || undefined,
                  schemaUnchanged: sameWindowSchemaUnchangedOnPage(
                    currentPageData as Record<string, unknown>,
                    response as Record<string, unknown>,
                    targetPageId,
                  ),
                },
              )
              this.scheduleSameWindowDocumentRoute(targetPageId, context, dependencies)
            }
          }
        }
      } else if (response?.stack && response.stack.length > 0) {
        const stackFormId = String(response.stack[0])
        const stackIsDialog =
          dependencies?.appStore &&
          isDialogPageSlot(
            {
              dialogIds: dependencies.appStore.dialogIds ?? [],
              dialogPageSlotIds: dependencies.appStore.dialogPageSlotIds ?? [],
              pagesData: dependencies.appStore.pagesData ?? {},
            },
            stackFormId,
          )
        if (!stackIsDialog) {
          if (dependencies?.appStore?.focusFormByFid) {
            dependencies.appStore.focusFormByFid(stackFormId)
          } else if (dependencies?.router) {
            await dependencies.router.push({ name: 'platform', params: { id: stackFormId } })
          }
        }
      }

      const changedData = sanitizeCheckboxSelectionChangedData(
        response?.jsResult?.changed_data,
        context,
        dependencies,
      ) as ExchangeData | undefined
      return changedData ?? null
    } catch (error) {
      throw error
    } finally {
      endMeasure()
    }
  }

  /**
   * Выполняет procedure через coreService.executeProcedure. Результат записывается в currentDataRef.
   */
  private async executeProcedure(
    currentDataRef: { value: ExchangeData },
    context: ActionContext,
    dependencies?: ActionDependencies,
  ): Promise<any> {
    const endMeasure = startWorkflowMeasure('ActionService.executeProcedure', {
      actionId: context.id,
      formId: context.formId,
    })
    const appStore = dependencies?.appStore
    const platform = context.platform || dependencies?.appStore?.currentPlatform?.value || 'WF'
    const actionIdNum = Number(context.id)
    if (isNaN(actionIdNum) || actionIdNum <= 0) {
      throw new Error(`Invalid actionId for executeProcedure: ${context.id}`)
    }
    const sessionId = resolveActionPlatformSessionId(
      dependencies,
      (deps) => actionScript.resolvePagesDataSlotId(context, deps),
      () => this.getCurrentSessionId(appStore),
    )
    let currentData = currentDataRef.value || {}

    if (isDocumentContextAction(context)) {
      const bound = this.resolveBoundDocumentIdentityForContext(context, dependencies)
      if (bound) {
        const fields = { ...(currentData.fields?.fields ?? {}) } as Record<string, unknown>
        overlayBoundDocumentIdentityOnFieldsUnlessPreserved(
          fields,
          bound,
          this.boundIdentityPreserveContext(context, dependencies),
        )
        currentData = {
          ...currentData,
          fields: {
            ...(currentData.fields ?? {}),
            fields,
          },
        }
      }
    }

    // TODO: временное
    if (actionIdNum === 201) currentData = ensureGetFileDataPayload(currentData)

    let response
    try {
      response = await coreService.executeProcedure({
        type: platform,
        sessionId: sessionId ?? undefined,
        socketId: this.getSocketId(),
        isClone: false,
        currentData,
        actionId: actionIdNum,
        cloneOf: null,
      })
    } catch (error: any) {
      if (
        error?.response?.data?.message?.includes('Core cannot be null') ||
        error?.response?.data?.message?.includes('core is null') ||
        error?.response?.data?.message?.includes('NullPointerException') ||
        error?.message?.includes('Core cannot be null') ||
        error?.message?.includes('core is null') ||
        error?.message?.includes('Platform unavailable')
      ) {
        response = await coreService.executeProcedure({
          type: platform,
          sessionId: undefined,
          socketId: this.getSocketId(),
          isClone: false,
          currentData,
          actionId: actionIdNum,
          cloneOf: null,
        })
      } else {
        throw error
      }
    }

    // TODO: непонятно, переход на форму по роуту?
    // if (response?.form && appStore) {
    //   appStore.addForm(response)
    // } else if (response?.stack && response.stack.length > 0 && dependencies?.router) {
    //   await dependencies.router.push({
    //     name: 'platform',
    //     params: { id: String(response.stack[0]) },
    //   })
    // } else if (response?.status && appStore) {
    //   const formId = String(response.status)
    //   if (response.form) {
    //     appStore.addForm(response)
    //   } else if (dependencies?.router) {
    //     await dependencies.router.push({ name: 'platform', params: { id: formId } })
    //   }
    // }

    // TODO: Печать по данным из ответа procedure?
    // if (this.responseMayContainPrintData(response)) {
    //   try {
    //     const { extractPrintDataFromProcedureResponse, printFileFromBase64 } = await import('@/services/print/filePrint.service')
    //     const printData = extractPrintDataFromProcedureResponse(response)
    //     if (printData) {
    //       printFileFromBase64(printData.base64, printData.fileName)
    //     }
    //   } catch (_) {
    //     // ignore
    //   }
    // }

    endMeasure()
    return response
  }

  /** Обрабатывает специфичные transfer types: print, document, explicit_view и др. */
  private async handleSpecificTransferTypes(
    context: ActionContext,
    dependencies?: ActionDependencies,
  ): Promise<void> {
    const { transferType, transfer } = context
    // Normalize transferType to lowercase for consistent comparison
    const normalizedType = transferType?.toLowerCase()

    switch (normalizedType) {
      case 'explicit_print':
      case 'print_document':
      case 'call_chain_and_explicit_print':
        await this.showPrintForm(transfer!)
        break

      case 'document':
      case 'call_chain_change_doc':
      case 'call_chain_conditional_change_doc':
      case 'context_call_change_doc':
      case 'context_call_conditional_change_doc':
        await this.openDocumentForm(
          transfer!,
          context,
          dependencies?.appStore,
          dependencies?.router,
          dependencies,
        )
        break

      case 'call_chain':
        // For call_chain, transfer is the procedure/chain ID
        // The chain execution is handled by executeProcedure() which calls coreService.executeProcedure
        // No additional handling needed here - executeProcedure will execute the server-side chain code
        // and handle any form/document opening that results from the chain execution
        break

      case 'explicit_view':
      case 'call_chain_and_explicit_view':
      case 'context_call_and_explicit_view':
      case 'context_call_and_conditional_explicit_view':
        await this.openFormWithViewId(transfer!)
        break

      // Numeric return transfer types (5-11) for step navigation back
      // 5 = TT_RETURN_NONE
      // 6 = TT_RETURN_REPLACE
      // 7 = TT_RETURN_PREV_NONE
      // 8 = TT_RETURN_PREV_REPLACE
      // 9 = TT_RETURN_PREV_CUR_DATA_NONE
      // 10 = TT_RETURN_PREV_CUR_DATA_REPLACE
      // 11 = TT_RETURN_TO_LAST_FORM
      case '5':
      case '6':
      case '7':
      case '8':
      case '9':
      case '10':
      case '11':
        break
    }
  }

  /** Открывает диалог по actionId через useDialog(). */
  private async openNewDialog(
    actionId: string,
    context: ActionContext,
    appStore: AppStore,
    dependencies?: ActionDependencies,
  ): Promise<void> {
    const effectiveAppStore = dependencies?.appStore || appStore

    try {
      // Show loading indicator
      if (effectiveAppStore?.setLoading) {
        effectiveAppStore.setLoading(true, tGlobal('platform.openingDialog'))
      }

      const dialog = dependencies?.dialog || useDialog()
      const platform = context.platform || dependencies?.appStore?.currentPlatform?.value

      if (platform && dependencies?.appStore?.updateCurrentPlatform) {
        dependencies.appStore.updateCurrentPlatform(platform)
      }
      const result = await dialog.openDialog(actionId, {
        sessionId: null,
        form: this.getCurrentFormData(appStore),
        platform,
      })

      if (result.success) {
        if (context.event?.dialogClosed) {
          // Dialog closed event can be executed here if needed
        }
      } else {
        console.error(`Failed to open dialog: ${result.error}`)
      }
    } catch (error) {
      console.error('Error opening dialog:', error)
      throw error
    }
  }

  /**
   * Переходит на страницу platform с указанным actionId.
   * TODO: переход по actionId? branchId???
   */
  private async openNewWindow(actionId: string, router: any): Promise<void> {
    if (actionId === '0' || actionId === '') {
      return
    }

    await router.push({ name: 'platform', params: { id: actionId } })
  }

  /** Открывает форму документа по _id, _typeId, _versionId через platformAction API. */
  private async openDocumentForm(
    documentId: string,
    context: ActionContext,
    appStore: AppStore,
    router: any,
    dependencies?: ActionDependencies,
  ): Promise<void> {
    const effectiveAppStore = dependencies?.appStore || appStore

    try {
      // Show loading indicator
      if (effectiveAppStore?.setLoading) {
        effectiveAppStore.setLoading(true, tGlobal('platform.openingDocument'))
      }
      const identity = this.resolveIdentityForDocumentOpen(context)
      const clientData = identity
        ? {
            _id: identity._id,
            _typeId: identity._typeId,
            _versionId: identity._versionId ?? '',
          }
        : this.extractRowDataFromContext(context)
      const platform = context.platform || dependencies?.appStore?.currentPlatform?.value

      if (platform && dependencies?.appStore?.updateCurrentPlatform) {
        dependencies.appStore.updateCurrentPlatform(platform)
      }

      if (!clientData._id || !clientData._typeId) {
        console.error(
          `Missing required fields for document opening: _id=${clientData._id}, _typeId=${clientData._typeId}`,
        )
        return
      }

      // Build fields object with proper structure for createSafeClientData
      const fields: Record<string, any> = {}

      // Ensure _id is present and valid
      if (clientData._id !== '') {
        fields._id = {
          value: clientData._id,
          type: 'Long',
        }
      }

      // Ensure _typeId is present and valid
      if (
        clientData._typeId !== ''
      ) {
        fields._typeId = {
          value: clientData._typeId,
          type: 'String',
        }
      }

      // Only include _versionId if it has a valid value (not null, undefined, empty string, or empty object)
      const versionIdValue = clientData._versionId
      if (
        versionIdValue !== null &&
        versionIdValue !== undefined &&
        versionIdValue !== '' &&
        !(typeof versionIdValue === 'object' && Object.keys(versionIdValue).length === 0)
      ) {
        fields._versionId = {
          value: versionIdValue,
          type: 'Long',
        }
      }

      const safeFields: Record<string, any> = {}

      Object.keys(fields).forEach((key) => {
        const field = fields[key]

        if (field && typeof field === 'object' && 'value' in field && 'type' in field) {
          const fieldValue = field.value
          if (
            fieldValue === null ||
            fieldValue === undefined ||
            typeof fieldValue === 'string' ||
            typeof fieldValue === 'number' ||
            typeof fieldValue === 'boolean' ||
            (Array.isArray(fieldValue) && fieldValue.length === 0)
          ) {
            safeFields[key] = field
          } else if (typeof fieldValue === 'object' && !Array.isArray(fieldValue)) {
            const keys = Object.keys(fieldValue)
            if (keys.length <= 2 && (keys.includes('_text') || keys.includes('value'))) {
              safeFields[key] = field
            }
          } else {
            safeFields[key] = field
          }
        }
      })

      const clientDataStructure = createSafeClientData({
        fields: safeFields,
      })

      const payload = createPlatformActionParams({
        sessionId: this.getCurrentSessionId(appStore),
        actionId: context.id,
        actionType: 'FORM_ACTION',
        type: platform,
        clientData: clientDataStructure,
        openingParams: {
          newWindow: context.windowType === 'NEW_WINDOW',
          ignoreExistingStack: false,
          tempFid: null,
        },
      })

      // выполнение execute action
      const result = await apiService.platformAction(payload)
      const documentData = result.data || result

      const stack = Array.isArray(documentData?.stack) ? documentData.stack : []
      // Always request form from server, even if it was already opened

      if (context.windowType === 'NEW_DIALOG') {
        if (documentData && documentData.form) {
          if (dependencies?.appStore) {
            const formId = documentData.status?.toString() || context.id
            const store = dependencies.appStore
            const hasExistingDialog =
              store.dialogs?.[formId] != null || store.pagesData?.[formId] != null
            if (hasExistingDialog) {
              await store.removeDialog(formId)
            }

            store.addDialog({
              id: formId,
              sessionId: documentData.sessionId,
              config: {},
            })

            const processedData = { ...documentData }
            if (processedData.form) {
              const { parseService } = await import('@/services/parse/parse.service')
              processedData.form = parseService.getForm(processedData.form, parseInt(formId))
            }

            if (processedData.uniData) {
              if (!processedData.uniData.fields) {
                processedData.uniData = {
                  fields: {
                    fields: processedData.uniData,
                  },
                }
              } else if (!processedData.uniData.fields.fields) {
                processedData.uniData = {
                  fields: {
                    fields: processedData.uniData.fields,
                  },
                }
              }
            } else {
              processedData.uniData = {}
            }

            if (platform) {
              processedData.uniData = processedData.uniData || {}
              processedData.uniData.clientData = {
                ...(processedData.uniData.clientData || {}),
                platform,
              }
            }

            const preparedData = await this.ensureOpenFormPreBuild(
              processedData as Record<string, unknown>,
              formId,
              dependencies,
            )

            // Set page data in store with parent page ID for breadcrumbs
            const parentPageId =
              context.parentPageId && String(context.parentPageId) !== String(formId)
                ? context.parentPageId
                : null
            dependencies.appStore.setPageData(
              formId,
              this.attachBoundDocumentIdentityToPagePayload(preparedData, context, {
                fromExchangeFields: safeFields,
                inheritFromPageData: dependencies?.pageData,
              }),
              parentPageId,
            )
          }
        } else {
          console.warn(`[ActionService] No form returned for document opening`)
        }
      } else if (context.windowType === 'NEW_WINDOW' || context.windowType === 'SAME_WINDOW') {
        if (documentData && documentData.form) {
          if (dependencies?.appStore && dependencies?.router) {
            // Priority: status > form attributes > stack (extract numeric ID) > context.id
            let formId: string | null = null

            if (documentData.status) {
              formId = String(documentData.status)
            }

            if (!formId) {
              const formFid =
                documentData.form?.form?._attributes?.fid ||
                documentData.form?._attributes?.fid ||
                documentData.form?.fid
              if (formFid) {
                formId = String(formFid)
              }
            }

            if (!formId && Array.isArray(documentData.stack) && documentData.stack.length > 0) {
              const stackValue = String(documentData.stack[0])
              const idMatch = stackValue.match(/id:\s*['"]?(\d+)['"]?/g)
              if (idMatch && idMatch.length > 0) {
                const lastId = idMatch[idMatch.length - 1].match(/\d+/)?.[0]
                if (lastId) {
                  formId = lastId
                }
              }
            }

            if (!formId) {
              formId = context.id || String(Math.random())
            }

            const processedData = { ...documentData }
            if (processedData.form) {
              const { parseService } = await import('@/services/parse/parse.service')
              processedData.form = parseService.getForm(processedData.form, parseInt(formId))

              const setFormIdOnPanels = (panels: any) => {
                if (!panels) return
                if (Array.isArray(panels)) {
                  panels.forEach((panel) => {
                    if (panel && typeof panel === 'object') {
                      if (panel.panelId) {
                        panel.formId = formId
                      }
                      // Recursively check nested panels
                      if (panel.panelRows) {
                        setFormIdOnPanels(panel.panelRows)
                      }
                    }
                  })
                } else if (typeof panels === 'object') {
                  Object.keys(panels).forEach((key) => {
                    const panel = panels[key]
                    if (panel && typeof panel === 'object' && panel.panelId) {
                      panel.formId = formId
                    }
                  })
                }
              }

              // Set formId on all panels in form.panels
              if (processedData.form.panels) {
                Object.keys(processedData.form.panels).forEach((panelId) => {
                  if (processedData.form.panels[panelId]) {
                    processedData.form.panels[panelId].formId = formId
                  }
                })
              }

              // Set formId on panels in tabs (main, header, footer)
              setFormIdOnPanels(processedData.form.main)
              setFormIdOnPanels(processedData.form.header)
              setFormIdOnPanels(processedData.form.footer)

            }

            // Normalize uniData structure
            if (processedData.uniData) {
              if (!processedData.uniData.fields) {
                processedData.uniData = {
                  fields: {
                    fields: processedData.uniData,
                  },
                }
              } else if (!processedData.uniData.fields.fields) {
                processedData.uniData = {
                  fields: {
                    fields: processedData.uniData.fields,
                  },
                }
              }
            } else {
              console.warn('[ActionService] No uniData in response!')
              processedData.uniData = {}
            }

            if (platform) {
              processedData.uniData = processedData.uniData || {}
              processedData.uniData.clientData = {
                ...(processedData.uniData.clientData || {}),
                platform,
              }
            }

            processedData.formId = formId

            const preparedData = await this.ensureOpenFormPreBuild(
              processedData as Record<string, unknown>,
              formId!,
              dependencies,
            )

            const rawParentPageId =
              context.parentPageId && String(context.parentPageId) !== String(formId)
                ? context.parentPageId
                : null
            const parentPageId =
              this.resolveStableParentForChildOpen(rawParentPageId, dependencies) ?? null
            const isSameWindowOpen = context.windowType === 'SAME_WINDOW'
            const pageSlotId = isSameWindowOpen
              ? resolveSameWindowPageSlotId({
                  sourcePageSlotId:
                    dependencies?.pageDataSlot ??
                    dependencies.appStore?.currentPageId ??
                    parentPageId ??
                    context.parentPageId,
                  openedFormId: formId,
                })
              : resolveNewWindowPageSlotId(formId!, {
                  parentPageSlotId: parentPageId ?? context.parentPageId,
                  sessionId: processedData.sessionId,
                  windowType: context.windowType,
                })
            if (context.windowType === 'NEW_WINDOW') {
              stampCloseRootsOpenedSlotId(context, pageSlotId)
            }
            capturePageSlotSnapshotBeforeChildWindow(
              dependencies?.appStore,
              parentPageId,
              context.windowType,
            )
            dependencies.appStore.setPageData(
              pageSlotId,
              this.attachBoundDocumentIdentityToPagePayload(preparedData, context, {
                fromExchangeFields: safeFields,
                inheritFromPageData: dependencies?.pageData,
              }),
              parentPageId,
            )

            if (platform && dependencies.appStore.updateCurrentPlatform) {
              dependencies.appStore.updateCurrentPlatform(platform)
            }

            if (context.windowType === 'NEW_WINDOW') {
              this.prepareMainAreaDocumentNavigation(
                pageSlotId,
                processedData as Record<string, unknown>,
                parentPageId ?? context.parentPageId ?? null,
                dependencies,
              )
              await dependencies.router.push({ name: 'document', params: { id: pageSlotId } })
            } else if (isSameWindowOpen) {
              this.prepareMainAreaSameWindowReplace(
                pageSlotId,
                processedData as Record<string, unknown>,
                parentPageId ?? context.parentPageId ?? null,
                dependencies,
                { openedFormId: formId },
              )
              await dependencies.router.push({ name: 'document', params: { id: pageSlotId } })
            } else if (dependencies?.appStore?.setPendingPageNavigation) {
              dependencies.appStore.setPendingPageNavigation(pageSlotId, parentPageId ?? context.parentPageId)
              await dependencies.router.push({ name: 'platform', params: { id: pageSlotId } })
            }
          } else {
            console.warn('[ActionService] Missing appStore or router for navigation', {
              hasAppStore: !!dependencies?.appStore,
              hasRouter: !!dependencies?.router,
            })
          }
        } else {
          console.warn(
            `[ActionService] No form returned for document opening in ${context.windowType}`,
            {
              hasDocumentData: !!documentData,
              hasForm: !!documentData?.form,
              responseKeys: Object.keys(result || {}),
              documentDataKeys: documentData ? Object.keys(documentData) : [],
            },
          )
        }
      }
    } catch (error) {
      console.error(`Error opening document:`, error)
      throw error
    }
  }

  /** Открывает форму по viewId (explicit_view). Открытие выполняется через callDoAction + MAIN_FRAME/SAME_WINDOW/NEW_WINDOW. */
  private async openFormWithViewId(viewId: string): Promise<void> {}

  /** Показывает форму печати по printId. Временная реализация через alert. */
  private async showPrintForm(printId: string): Promise<void> {
    alert(`Print form: ${printId}`)
  }

  async executeConfiguredDialogCloseScripts(options: {
    enInstance?: En
    mergedCloseFields?: Record<string, unknown>
    cfg?: Record<string, unknown>
    context: ActionContext
    dependencies?: ActionDependencies
    scriptPhase?: 'dialogClosed' | 'dialogClosedNReturn' | 'all'
    [key: string]: unknown
  }): Promise<{ pending: DialogClosePendingFieldOnChange[]; properties: JsResultProperties }> {
    const appStore = options.dependencies?.appStore as AppStore | undefined
    if (!appStore) {
      return { pending: [], properties: emptyDialogCloseProperties() }
    }

    const cfg = options.cfg || {}
    const phase = options.scriptPhase ?? 'all'
    const scriptForMergeScope =
      phase === 'dialogClosedNReturn'
        ? cfg.onCloseDialogNReturnEvent
        : phase === 'dialogClosed'
          ? cfg.onCloseDialogEvent
          : cfg.onCloseDialogEvent

    const dialogConfig = augmentDialogCloseConfigMergeScope(
      {
        ...cfg,
        pageDataSlot:
          options.dependencies?.pageDataSlot ??
          cfg.pageDataSlot ??
          cfg.onCloseDialogParentSlotId,
      },
      {
        scriptText: scriptForMergeScope,
        actionContext: options.context
          ? {
              data: options.context.data,
              actionItem: options.context.data,
              formId: options.context.formId,
              panelId: options.context.panelId,
              elementId: options.context.elementId,
              level: options.context.level,
            }
          : undefined,
        dependencies: options.dependencies,
      },
    )
    const scripts = (
      phase === 'dialogClosed'
        ? [cfg.onCloseDialogEvent]
        : phase === 'dialogClosedNReturn'
          ? [cfg.onCloseDialogNReturnEvent]
          : [cfg.onCloseDialogEvent, cfg.onCloseDialogNReturnEvent]
    )
      .map((script) => (script != null ? String(script).trim() : ''))
      .filter(Boolean)

    const seedFieldOverlay =
      phase === 'dialogClosedNReturn'
        ? undefined
        : options.mergedCloseFields &&
            typeof options.mergedCloseFields === 'object' &&
            !Array.isArray(options.mergedCloseFields)
          ? (options.mergedCloseFields as Record<string, unknown>)
          : undefined
    const closingDialogId =
      options.dialogId != null ? String(options.dialogId) : String(appStore.topDialog?.id ?? '')
    const dialogPageSnapshot =
      options.dialogPageSnapshot &&
      typeof options.dialogPageSnapshot === 'object' &&
      !Array.isArray(options.dialogPageSnapshot)
        ? (options.dialogPageSnapshot as Record<string, unknown>)
        : undefined

    let pendingFieldOnChanges: DialogClosePendingFieldOnChange[] = []
    let properties = emptyDialogCloseProperties()
    for (const script of scripts) {
      const result = await this.executeDialogCloseScript(
        script,
        options.context,
        options.dependencies,
        dialogConfig,
        appStore,
        {
          seedFieldOverlay,
          closingDialogId: closingDialogId || undefined,
          dialogPageSnapshot,
          scriptPhase: phase === 'all' ? undefined : phase,
        },
      )
      pendingFieldOnChanges = pendingFieldOnChanges.concat(result.pending)
      properties = result.properties
    }
    return { pending: pendingFieldOnChanges, properties }
  }

  private async applyDialogCloseResultToParent(
    result: Record<string, unknown>,
    parentStoreKey: string,
    appStore: AppStore,
    dependencies?: ActionDependencies,
    dialogConfig?: Record<string, unknown>,
    options?: { deferFieldOnChange?: boolean },
  ): Promise<DialogClosePendingFieldOnChange[]> {
    if (!result || Object.keys(result).length === 0) return []
    if (!parentStoreKey) return []

    const pendingFieldOnChanges: DialogClosePendingFieldOnChange[] = []
    const parentContext = resolveDialogCloseParentContext(appStore, dialogConfig, {})
    const registryFormId = parentContext.registryFormId || parentStoreKey

    const formProvider = dependencies?.formProvider
    const elements = formProvider?.getElements?.() ?? dependencies?.getElements?.()
    const mergeFieldKeys = Array.isArray(dialogConfig?.onCloseDialogParentMergeFieldKeys)
      ? dialogConfig.onCloseDialogParentMergeFieldKeys.map((k) => String(k)).filter(Boolean)
      : Object.keys(result).filter((k) => !String(k).includes('__'))

    const targetPage = appStore.pagesData[parentStoreKey] as Record<string, any> | undefined
    const sideEffectFields = new Set(
      inferDialogCloseMergeFieldKeysFromScript(
        [
          readRootFormFunctions(targetPage),
          dialogConfig?.onCloseDialogEvent,
          dialogConfig?.onCloseDialogNReturnEvent,
        ]
          .filter(Boolean)
          .join('\n'),
      ),
    )

    const normalizedResult: Record<string, unknown> = {}
    for (const fieldName of mergeFieldKeys) {
      const fieldData = result[fieldName]
      if (fieldData === undefined) continue
      if (!fieldEntryHasPresentableValue(fieldData)) continue
      const element = findElementForDialogCloseParent(
        elements as Record<string, unknown> | undefined,
        fieldName,
        registryFormId,
        targetPage,
      )
      const elType = String(
        (element as { type?: { _text?: string }; elementType?: string })?.type?._text ??
          (element as { elementType?: string })?.elementType ??
          '',
      ).toUpperCase()
      normalizedResult[fieldName] =
        elType === 'MULTISELECT' ? toMultiSelectTypedValue(fieldData) : fieldData
    }

    const syncForceFieldNames = Object.keys(normalizedResult).filter((key) => !sideEffectFields.has(key))

    if (targetPage && Object.keys(normalizedResult).length > 0) {
      const preserveEmpty = { shouldPreserveEmptyIncoming: () => true }
      const existingUniFields = targetPage.uniData?.fields?.fields || {}
      const existingChangedFields = targetPage.jsResult?.changed_data?.fields?.fields || {}
      const mergedUniFields = mergePageFieldMaps(preserveEmpty, existingUniFields, normalizedResult)
      const mergedChangedFields = mergePageFieldMaps(preserveEmpty, existingChangedFields, normalizedResult)

      appStore.setPageData(
        parentStoreKey,
        {
          ...targetPage,
          uniData: {
            ...(targetPage.uniData || {}),
            fields: {
              ...(targetPage.uniData?.fields || {}),
              fields: mergedUniFields,
            },
          },
          jsResult: {
            ...(targetPage.jsResult || {}),
            changed_data: {
              ...(targetPage.jsResult?.changed_data || {}),
              fields: {
                ...(targetPage.jsResult?.changed_data?.fields || {}),
                fields: mergedChangedFields,
              },
            },
          },
        },
        undefined,
        undefined,
        'ActionService.applyDialogCloseResultToParent',
      )
    }

    if (syncForceFieldNames.length > 0) {
      formProvider?.syncPageDataToRegistry?.(registryFormId, parentStoreKey, {
        forceFieldNames: syncForceFieldNames,
      })
    }

    await nextTick()

    for (const fieldName of mergeFieldKeys) {
      const fieldData = normalizedResult[fieldName]
      if (fieldData === undefined) continue
      if (!fieldEntryHasPresentableValue(fieldData)) continue

      const element = findElementForDialogCloseParent(
        elements as Record<string, unknown> | undefined,
        fieldName,
        registryFormId,
        targetPage,
      )
      const refillFormId = registryFormId
      const panelId = (element as { panelId?: unknown })?.panelId
      const elementId = (element as { elementId?: unknown })?.elementId

      if (formProvider?.setValue && refillFormId && panelId != null && elementId != null) {
        formProvider.setValue({
          formId: refillFormId,
          panelId,
          elementId,
          value: { [fieldName]: fieldData },
        })
      }

      const registered = (elements as Record<string, { refill?: (data: unknown) => void; formId?: unknown }> | undefined)?.[
        fieldName
      ]
      const registeredMatches =
        registered &&
        String(registered.formId ?? '') === registryFormId &&
        (!element ||
          String(registered.formId ?? '') === String((element as { formId?: unknown }).formId ?? registryFormId))

      if (registeredMatches && typeof registered.refill === 'function') {
        registered.refill({ [fieldName]: fieldData })
      }
      formProvider?.refillElement?.(fieldName, fieldData, refillFormId)

      await nextTick()

      const parentPageData = appStore.pagesData[parentStoreKey] as Record<string, unknown> | undefined
      const onChangeContext = {
        fieldName,
        parentStoreKey,
        parentPageData,
        formId: refillFormId,
        panelId: panelId as string | number | undefined,
        elementId: elementId as string | number | undefined,
        element: element as Record<string, unknown> | undefined,
      }

      if (options?.deferFieldOnChange) {
        pendingFieldOnChanges.push({
          fieldName,
          fieldData,
          registered:
            registeredMatches && String(registered?.formId ?? '') === registryFormId
              ? (registered as Record<string, unknown>)
              : undefined,
          context: onChangeContext,
        })
        continue
      }

      await flushDialogClosePendingFieldOnChanges(
        [
          {
            fieldName,
            fieldData,
            registered:
              registeredMatches && String(registered?.formId ?? '') === registryFormId
                ? (registered as Record<string, unknown>)
                : undefined,
            context: onChangeContext,
          },
        ],
        formProvider,
        appStore,
      )
    }

    await nextTick()
    return pendingFieldOnChanges
  }

  private extractPageFieldMapForDialogClose(pageData: unknown): Record<string, unknown> {
    return extractDialogCloseSeedFieldMap(pageData)
  }

  private resolveRegistryFormIdForDialogClose(pageData: unknown, fallbackId: string): string {
    if (!pageData || typeof pageData !== 'object' || Array.isArray(pageData)) return fallbackId
    const page = pageData as Record<string, any>
    return String(
      page.jsResult?.form_id ??
        page.form?.form?._attributes?.id ??
        page.form?._attributes?.id ??
        page.formId ??
        fallbackId,
    )
  }

  private getRuntimeFormFieldsForDialogClose(
    dependencies: ActionDependencies | undefined,
    slotOrFormId: string,
    options?: { pageDataSlot?: string; registryFormId?: string },
  ): Record<string, unknown> {
    if (!slotOrFormId || typeof dependencies?.getForm !== 'function') return {}
    const pageDataSlot = String(options?.pageDataSlot ?? slotOrFormId).trim()
    const formId = String(options?.registryFormId ?? slotOrFormId).trim()
    const getForm = dependencies.getForm as (args: {
      formId?: string
      pageDataSlot?: string
      flatten?: boolean
    }) => Record<string, unknown>
    const data = getForm({ formId, pageDataSlot, flatten: true })
    return data && typeof data === 'object' && !Array.isArray(data) ? data : {}
  }

  private collectDialogCloseSeedData(
    parentSlot: string,
    parentPage: unknown,
    dialogId: string,
    dialogPage: unknown,
    dependencies?: ActionDependencies,
  ): Record<string, unknown> {
    const parentRegistryFormId = this.resolveRegistryFormIdForDialogClose(parentPage, parentSlot)
    const dialogRegistryFormId = this.resolveRegistryFormIdForDialogClose(dialogPage, dialogId)

    return isolateAllSharedTableFieldValues(
      mergeFieldMaps(
        this.extractPageFieldMapForDialogClose(parentPage),
        this.getRuntimeFormFieldsForDialogClose(dependencies, parentSlot, {
          pageDataSlot: parentSlot,
          registryFormId: parentRegistryFormId,
        }),
        this.extractPageFieldMapForDialogClose(dialogPage),
        this.getRuntimeFormFieldsForDialogClose(dependencies, dialogId, {
          pageDataSlot: dialogId,
          registryFormId: dialogRegistryFormId,
        }),
      ) as Record<string, unknown>,
    )
  }

  /** Side-effect fields (setField/refillElement in dialogClosed) must reach parent UI after script. */
  private async applyDialogCloseSideEffectFieldsToParent(
    enInstance: En,
    scriptText: string,
    parentStoreKey: string,
    parentRegistryFormId: string,
    appStore: AppStore,
    dependencies?: ActionDependencies,
  ): Promise<void> {
    const sideEffectFields = inferDialogCloseMergeFieldKeysFromScript(scriptText)
    if (!sideEffectFields.length || !parentStoreKey) return

    const serialized = enInstance._serialize() as Record<string, unknown>
    const payload: Record<string, unknown> = {}
    for (const fieldName of sideEffectFields) {
      const fieldData = serialized[fieldName]
      if (fieldData !== undefined && fieldEntryHasPresentableValue(fieldData)) {
        payload[fieldName] = fieldData
      }
    }
    if (Object.keys(payload).length === 0) return

    await this.applyDialogCloseResultToParent(
      payload,
      parentStoreKey,
      appStore,
      dependencies,
      { onCloseDialogParentMergeFieldKeys: sideEffectFields },
      { deferFieldOnChange: false },
    )
  }

  private async handleDialogCloseFromProperties(
    context: ActionContext,
    currentDataRef: { value: ExchangeData },
    jsResultPropertiesRef: { value: JsResultProperties },
    dependencies?: ActionDependencies,
  ): Promise<{ abortAction: boolean }> {
    const props = jsResultPropertiesRef.value
    const isContextXml = String(extractActionTextValue(context.data?.isContext) ?? '').trim()
    if (!shouldTriggerStage3DialogClose(props, isContextXml)) {
      return { abortAction: false }
    }

    const appStore = dependencies?.appStore
    if (appStore && currentDataRef.value) {
      actionScript.applyChangedDataToAppStore(
        currentDataRef.value,
        context,
        appStore,
        dependencies,
      )
    }

    const incomingSeed = extractIncomingDataFieldMap(currentDataRef.value)
    return invokeDialogCloseFromProperties({
      context,
      props,
      dependencies,
      mergedCloseFields: incomingSeed,
      resolvePagesDataSlotId: actionScript.resolvePagesDataSlotId.bind(actionScript),
      closeDialogSlot: (closingSlotId, ctx, deps, listeners, dialogPageSnapshot) =>
        this.closeDialogSlot(closingSlotId, ctx, deps, listeners, dialogPageSnapshot, incomingSeed),
    })
  }

  /** UI dismiss (крестик): только dialogClosedNReturn на родителе, затем removeDialog. */
  async dismissDialogFromUi(
    dialogId: string,
    dependencies?: ActionDependencies,
  ): Promise<void> {
    const minimalContext: ActionContext = {
      id: '',
      name: 'dialogDismiss',
      level: 'form',
      formId: dialogId,
      data: {},
    }
    await this.closeDialogSlot(
      dialogId,
      minimalContext,
      dependencies,
      ['dialogClosedNReturn'],
      dependencies?.appStore?.pagesData?.[dialogId] as Record<string, unknown> | undefined,
    )
  }

  private async removeClosingSlot(
    slotId: string,
    dialogConfig: Record<string, unknown>,
    dependencies?: ActionDependencies,
    context?: ActionContext,
  ): Promise<void> {
    const appStore = dependencies?.appStore
    if (!appStore) return

    const normalized = String(slotId ?? '').trim()
    if (!normalized) return

    const keepOpened = context ? readCloseRootsOpenedSlotId(context) : ''
    if (keepOpened && keepOpened === normalized) return

    const pageEntry = appStore.pagesData?.[normalized] as { $parentPageId?: string } | undefined
    const parentSlotHint = String(
      dialogConfig.pageDataSlot ??
        dialogConfig.onCloseDialogParentSlotId ??
        pageEntry?.$parentPageId ??
        '',
    ).trim()

    if (isDialogSlot(appStore, normalized) || appStore.dialogs?.[normalized]) {
      await appStore.removeDialog(normalized, {
        router: dependencies?.router,
        routePageId: dependencies?.router?.currentRoute?.value?.params?.id
          ? String(dependencies.router.currentRoute.value.params.id)
          : undefined,
      })
      return
    }

    if (typeof appStore.removePage === 'function') {
      const descendants = collectDescendantPageSlotIds(normalized, appStore.pagesData ?? {})
      for (const descId of descendants) {
        if (keepOpened && descId === keepOpened) continue
        appStore.removePage(descId)
      }
      appStore.removePage(normalized)
    } else if (typeof appStore.removePageAndDescendants === 'function') {
      appStore.removePageAndDescendants(normalized)
    }

    const router = dependencies?.router
    if (router && parentSlotHint) {
      try {
        await router.push({ name: 'document', params: { id: parentSlotHint } })
      } catch (error) {
        console.warn('[dialogClose] failed to navigate after tab close:', error)
      }
    }
  }

  private async closeDialogSlot(
    dialogId: string,
    context: ActionContext,
    dependencies: ActionDependencies | undefined,
    listeners: DialogCloseListener[],
    dialogPageSnapshot?: Record<string, unknown>,
    mergedCloseFields?: Record<string, unknown>,
  ): Promise<DialogCloseSlotResult> {
    const appStore = dependencies?.appStore
    if (!appStore || listeners.length === 0) {
      return { closed: false, postScriptProperties: emptyDialogCloseProperties() }
    }

    const dialogConfig = resolveDialogCloseConfigForSlot(appStore, dialogId)
    const snapshot =
      dialogPageSnapshot ??
      (appStore.pagesData[dialogId] as Record<string, unknown> | undefined)
    if (!snapshot && !appStore.dialogs?.[dialogId]) {
      return { closed: false, postScriptProperties: emptyDialogCloseProperties() }
    }

    const parentContext = resolveDialogCloseParentContext(appStore, dialogConfig, {
      closingDialogId: dialogId,
    })
    const parentSlotClose = parentContext.storeKey

    const closeDependencies = {
      ...dependencies,
      pageDataSlot: parentSlotClose || dependencies?.pageDataSlot,
      pageData: parentSlotClose
        ? (appStore.pagesData[parentSlotClose] as Record<string, unknown>)
        : dependencies?.pageData,
    }

    let pendingFieldOnChanges: DialogClosePendingFieldOnChange[] = []
    let postScriptProperties = emptyDialogCloseProperties()
    let dialogClosedProperties = emptyDialogCloseProperties()

    const runListenerPhase = async (phase: DialogCloseListener) => {
      const pending = await this.executeConfiguredDialogCloseScripts({
        cfg: dialogConfig,
        context,
        dependencies: closeDependencies,
        dialogId,
        dialogPageSnapshot: snapshot,
        scriptPhase: phase,
        mergedCloseFields: phase === 'dialogClosed' ? mergedCloseFields : undefined,
      })
      pendingFieldOnChanges = pendingFieldOnChanges.concat(pending.pending)
      postScriptProperties = pending.properties
    }

    for (const phase of planDialogClosePhases(listeners)) {
      if (phase === 'removeSlot') {
        await this.removeClosingSlot(dialogId, dialogConfig, dependencies, context)
        continue
      }
      if (phase === 'dialogClosed') {
        try {
          await runListenerPhase('dialogClosed')
          dialogClosedProperties = postScriptProperties
          if (pendingFieldOnChanges.length > 0) {
            await flushDialogClosePendingFieldOnChanges(
              pendingFieldOnChanges,
              dependencies?.formProvider,
              appStore,
            )
            pendingFieldOnChanges = []
          }
        } catch (listenerError) {
          console.warn('[dialogClose] dialogClosed listener failed after slot remove', listenerError)
        }
        continue
      }
      await runListenerPhase('dialogClosedNReturn')
      if (pendingFieldOnChanges.length > 0) {
        await flushDialogClosePendingFieldOnChanges(
          pendingFieldOnChanges,
          dependencies?.formProvider,
          appStore,
        )
      }
    }

    if (parentSlotClose && typeof dependencies?.formProvider?.resyncTabSlotFields === 'function') {
      dependencies.formProvider.resyncTabSlotFields(parentSlotClose, { skipSnapshotRestore: true })
    }

    const dispatched = pickDialogClosedDispatchedVisibilities(
      dialogClosedProperties,
      postScriptProperties,
    )
    // reloadForm/setVisible dialogClosed already updated parent labels.
    // Replaying pre-close jsResult.states would restore stale unconfirmed visibility.
    const skipVisibilityReplay =
      listeners.includes('dialogClosed') &&
      shouldSkipDialogCloseVisibilityReplay(dialogConfig?.onCloseDialogEvent)
    if (
      listeners.includes('dialogClosed') &&
      Array.isArray(dispatched) &&
      dispatched.length > 0
    ) {
      actionScript.applyDispatchedVisibilitiesFromProperties(
        dispatched,
        {
          ...context,
          formId: parentContext.registryFormId || parentSlotClose || context.formId,
        },
        closeDependencies,
      )
    }

    if (
      parentSlotClose &&
      !skipVisibilityReplay &&
      typeof dependencies?.formProvider?.replayPageVisibilityStatesForSlot === 'function'
    ) {
      dependencies.formProvider.replayPageVisibilityStatesForSlot(parentSlotClose)
    }

    return { closed: true, postScriptProperties }
  }

  /** Закрывает текущий контекст (модал) и выполняет скрипты dialogClosed / dialogClosedNReturn родителя. */
  private async closeCurrentContext(context: ActionContext, dependencies?: ActionDependencies): Promise<void> {
    const appStore = dependencies?.appStore
    if (!appStore?.topDialog?.id) return
    await this.closeDialogSlot(
      String(appStore.topDialog.id),
      context,
      dependencies,
      ['dialogClosed', 'dialogClosedNReturn'],
      appStore.pagesData[String(appStore.topDialog.id)] as Record<string, unknown> | undefined,
    )
  }

  private shouldRunDialogCloseScriptOnClient(
    dialogConfig: Record<string, unknown>,
    scriptPhase?: 'dialogClosed' | 'dialogClosedNReturn' | 'all',
    scriptText?: string,
  ): boolean {
    if (scriptPhase === 'dialogClosedNReturn') {
      return (
        dialogConfig.onCloseDialogNReturnWebClientExec === true ||
        isDialogCloseReloadOnlyScript(scriptText)
      )
    }
    if (scriptPhase === 'dialogClosed') {
      // Flag from open config, or reloadForm/reloadPanel-only script (2610 confirmEmail dialogClosed).
      return (
        dialogConfig.onCloseDialogWebClientExec === true ||
        isDialogCloseReloadOnlyScript(scriptText)
      )
    }
    return true
  }

  private collectDialogCloseSeedForPhase(
    scriptPhase: 'dialogClosed' | 'dialogClosedNReturn' | 'all' | undefined,
    parentStoreKey: string,
    parentPage: unknown,
    dialogId: string,
    dialogPage: unknown,
    dependencies?: ActionDependencies,
  ): Record<string, unknown> {
    if (scriptPhase === 'dialogClosedNReturn') {
      return {}
    }
    if (scriptPhase === 'dialogClosed') {
      const dialogRegistryFormId = this.resolveRegistryFormIdForDialogClose(dialogPage, dialogId)
      // Swing behavior: event.dialogClosed receives dialog snapshot, not merged runtime state
      // Runtime merge was causing addressAdd from parent form to be included in address formatting
      return isolateAllSharedTableFieldValues(
        this.extractPageFieldMapForDialogClose(dialogPage) as Record<string, unknown>,
      )
    }
    return this.collectDialogCloseSeedData(
      parentStoreKey,
      parentPage,
      dialogId,
      dialogPage,
      dependencies,
    )
  }

  /** Выполняет скрипт закрытия диалога и мержит результаты в родителя. */
  private async executeDialogCloseScript(
    scriptText: string,
    context: ActionContext,
    dependencies: ActionDependencies | undefined,
    dialogConfig: Record<string, unknown> | undefined,
    appStore: AppStore,
    options?: {
      seedFieldOverlay?: Record<string, unknown>
      closingDialogId?: string
      dialogPageSnapshot?: Record<string, unknown>
      scriptPhase?: 'dialogClosed' | 'dialogClosedNReturn'
    },
  ): Promise<{ pending: DialogClosePendingFieldOnChange[]; properties: JsResultProperties }> {
    const emptyResult = () => ({
      pending: [] as DialogClosePendingFieldOnChange[],
      properties: emptyDialogCloseProperties(),
    })
    try {
      const effectiveDialogConfig = augmentDialogCloseConfigMergeScope(
        { ...(dialogConfig ?? {}) },
        { scriptText, dependencies },
      )
      const dialogId =
        options?.closingDialogId != null && String(options.closingDialogId).trim() !== ''
          ? String(options.closingDialogId)
          : appStore.topDialog?.id != null
            ? String(appStore.topDialog.id)
            : undefined
      const dialogPage =
        options?.dialogPageSnapshot ??
        (dialogId ? (appStore.pagesData[String(dialogId)] as Record<string, unknown> | undefined) : undefined)
      const parentContext = resolveDialogCloseParentContext(appStore, effectiveDialogConfig, {
        closingDialogId: dialogId,
      })
      const parentStoreKey = parentContext.storeKey
      const parentPage = parentStoreKey ? appStore.pagesData[parentStoreKey] : undefined

      if (!dialogPage) {
        console.warn('[dialogClose] No dialog page found', { dialogId })
        return emptyResult()
      }
      if (!parentPage) {
        console.warn('[dialogClose] No parent page found', { parentStoreKey, dialogId })
        return emptyResult()
      }

      const scriptPhase = options?.scriptPhase
      if (!this.shouldRunDialogCloseScriptOnClient(effectiveDialogConfig, scriptPhase, scriptText)) {
        return await this.executeDialogCloseScriptOnMiddle(
          scriptText,
          context,
          dependencies,
          effectiveDialogConfig,
          appStore,
          {
            closingDialogId: dialogId,
            dialogPageSnapshot: dialogPage,
            scriptPhase,
            parentStoreKey,
            parentPage: parentPage as Record<string, unknown>,
          },
        )
      }

      let seedData = this.collectDialogCloseSeedForPhase(
        scriptPhase,
        parentStoreKey,
        parentPage,
        String(dialogId ?? ''),
        dialogPage,
        dependencies,
      )
      if (
        scriptPhase !== 'dialogClosedNReturn' &&
        options?.seedFieldOverlay &&
        Object.keys(options.seedFieldOverlay).length > 0
      ) {
        seedData = isolateAllSharedTableFieldValues(
          isolateSharedTableFieldAliases(
            mergeFieldMaps(seedData, options.seedFieldOverlay) as Record<string, unknown>,
          ),
        )
      }
      const resolveDialogCloseElements = (): EnElements =>
        (dependencies?.formProvider?.getElements?.() ??
          dependencies?.getElements?.() ??
          {}) as EnElements

      const pendingPanelReloads: Promise<unknown>[] = []

      const enInstance = new En({
        controls: {
          ...bindDialogCloseEnVisibilityControls(dependencies?.formProvider),
          getForm: ({ formId, panelId, elementId }: any) => {
            if (typeof dependencies?.getForm === 'function') {
              const runtimeData = dependencies.getForm({ formId, panelId, elementId, flatten: false })
              if (runtimeData && typeof runtimeData === 'object' && Object.keys(runtimeData).length > 0) {
                return runtimeData
              }
            }
            const pageData = appStore.pagesData[formId]
            if (!pageData) return {}
            if (formId && panelId && elementId) {
              return pageData.form?.[panelId]?.[elementId] || {}
            } else if (formId && panelId) {
              return pageData.form?.[panelId] || {}
            } else if (formId) {
              return pageData.form || {}
            }
            return {}
          },
          getPageStoreField: (fieldName: string, ctx?: { formId?: number | string; pageDataSlotId?: string }) => {
            const slot = String(ctx?.pageDataSlotId ?? parentStoreKey ?? '').trim()
            const pageData = slot ? appStore.pagesData[slot] : parentPage
            if (!pageData) return undefined
            const snapshot = mergePageDataFieldSnapshot(pageData) as Record<string, unknown>
            return snapshot[String(fieldName ?? '').trim()]
          },
          refreshPageDataSnapshot: (ctx?: { pageDataSlotId?: string }) => {
            const slot = String(ctx?.pageDataSlotId ?? parentStoreKey ?? '').trim()
            return slot ? appStore.pagesData[slot] ?? null : parentPage ?? null
          },
          reloadElement: async (elementName: string, out?: unknown, actionContext?: unknown) => {
            const reload =
              dependencies?.reloadElement ??
              (typeof dependencies?.formProvider?.reloadElement === 'function'
                ? dependencies.formProvider.reloadElement
                : undefined)
            if (typeof reload !== 'function') return
            await reload(elementName, out, {
              ...(actionContext && typeof actionContext === 'object' ? actionContext : {}),
              pageDataSlot: parentStoreKey,
              formId: parentContext.registryFormId,
              skipPageFocusGuard: true,
              source: 'dialogClose',
            })
          },
          reloadPanel: async (panelName: string | number, data?: unknown) => {
            const reloadPanel = dependencies?.formProvider?.reloadPanel
            if (typeof reloadPanel !== 'function') return
            const task = reloadPanel(panelName, data, {
              pageDataSlot: parentStoreKey,
              formId: parentContext.registryFormId,
            })
            pendingPanelReloads.push(Promise.resolve(task))
            await task
          },
          reloadForm: async (data?: unknown, ctx?: Record<string, unknown>) => {
            const parentSessionId =
              (parentPage as { sessionId?: unknown } | undefined)?.sessionId != null
                ? String((parentPage as { sessionId?: unknown }).sessionId)
                : undefined
            const stackCtx = {
              ...(ctx && typeof ctx === 'object' ? ctx : {}),
              pageDataSlot: parentStoreKey,
              formId: parentContext.registryFormId,
              ...(parentSessionId ? { sessionId: parentSessionId } : {}),
            }
            const reloadFormStack = dependencies?.formProvider?.reloadFormStack
            if (typeof reloadFormStack === 'function') {
              const task = reloadFormStack(stackCtx)
              pendingPanelReloads.push(Promise.resolve(task))
              return await task
            }
            const reloadForm = dependencies?.formProvider?.reloadForm
            if (typeof reloadForm !== 'function') return
            const task = reloadForm(resolveDialogCloseReloadFormOverlay(data), {
              ...stackCtx,
              omitPageFieldSnapshot: true,
            })
            pendingPanelReloads.push(Promise.resolve(task))
            return await task
          },
          refillElement: (elementName: string, fieldData?: unknown) => {
            const refill =
              dependencies?.refillElement ??
              (typeof dependencies?.formProvider?.refillElement === 'function'
                ? dependencies.formProvider.refillElement
                : undefined)
            refill?.(elementName, fieldData, parentContext.registryFormId)
          },
          empty: (name: string) => {
            dependencies?.formProvider?.empty?.(name)
          },
          Messages: { message: () => {}, confirm: () => Promise.resolve(true) },
        },
        elements: resolveDialogCloseElements(),
        getElements: resolveDialogCloseElements,
        actions: {},
        pageData: dialogPage,
      })
      enInstance._resetData(seedData)

      const closingRegistryFormId = this.resolveRegistryFormIdForDialogClose(
        dialogPage,
        String(dialogId ?? ''),
      )
      const sourceTableFields = inferDialogCloseSourceTableFieldsFromScript(scriptText)
      const sourceFieldOverlay =
        scriptPhase === 'dialogClosedNReturn'
          ? {}
          : resolveDialogCloseSourceFieldOverlay({
              elements: (dependencies?.formProvider?.getElements?.() ??
                dependencies?.getElements?.()) as Record<string, unknown> | undefined,
              registryFormId: closingRegistryFormId,
              dialogSlotId: String(dialogId ?? ''),
              fieldNames: sourceTableFields,
              getForm: dependencies?.getForm,
            })
      if (Object.keys(sourceFieldOverlay).length > 0) {
        enInstance._addData(sourceFieldOverlay)
      }

      const imports = appStore?.imports || {}
      Object.entries(imports).forEach(([name, item]) => {
        if (item && typeof (item as any).func === 'function') {
          enInstance.registerClientFunction(name, (item as any).func)
        }
      })

      enInstance._setPageData(buildEnPageDataWithParentFormFunctions(parentPage, dialogPage))
      enInstance._setPageDataSlotId(parentStoreKey)

      const registryFormId = parentContext.registryFormId
      if (/^\d+$/.test(registryFormId)) {
        enInstance._setFormId(Number.parseInt(registryFormId, 10))
        const additionalSendersEl = enInstance.getElement('additionalSenders') as { panelId?: number } | undefined
        if (additionalSendersEl?.panelId != null && Number.isFinite(additionalSendersEl.panelId)) {
          enInstance._setPanelId(Math.floor(additionalSendersEl.panelId))
        }
      } else if (registryFormId) {
        enInstance._setFormId(registryFormId)
      }

      dependencies?.formProvider?.pushPageDataSlotOverride?.(parentStoreKey)
      try {
        await enInstance.run(scriptText)
        actionScript.applyDispatchedVisibilitiesFromProperties(
          enInstance.getProperties().dispatchedVisibilities,
          {
            ...context,
            formId: parentContext.registryFormId || context.formId,
          },
          {
            ...dependencies,
            pageDataSlot: parentStoreKey,
            pageData: parentPage as Record<string, unknown>,
          },
        )
        await Promise.all(pendingPanelReloads)
        await this.applyDialogCloseSideEffectFieldsToParent(
          enInstance,
          scriptText,
          parentStoreKey,
          parentContext.registryFormId,
          appStore,
          dependencies,
        )
      } finally {
        dependencies?.formProvider?.popPageDataSlotOverride?.()
      }

      if (isDialogCloseReloadOnlyScript(scriptText)) {
        return {
          pending: [],
          properties: mapEnRuntimePropertiesToJsResult(enInstance.getProperties()),
        }
      }

      const rawResult = enInstance._serialize() as Record<string, unknown>
      const stripped = stripEnDataToDialogCloseMergeAllowlist(rawResult, effectiveDialogConfig)
      const result = stripDialogCloseSideEffectFieldsFromMergeResult(stripped, {
        scriptText,
        parentFunctions: readRootFormFunctions(parentPage),
      })
      const pending = await this.applyDialogCloseResultToParent(
        result,
        parentStoreKey,
        appStore,
        dependencies,
        effectiveDialogConfig,
        { deferFieldOnChange: true },
      )
      return {
        pending,
        properties: mapEnRuntimePropertiesToJsResult(enInstance.getProperties()),
      }
    } catch (error) {
      console.warn('[dialogClose] script execution failed:', error)
      return emptyResult()
    }
  }

  private async executeDialogCloseScriptOnMiddle(
    scriptText: string,
    context: ActionContext,
    dependencies: ActionDependencies | undefined,
    dialogConfig: Record<string, unknown>,
    appStore: AppStore,
    options: {
      closingDialogId?: string
      dialogPageSnapshot: Record<string, unknown>
      scriptPhase?: 'dialogClosed' | 'dialogClosedNReturn'
      parentStoreKey: string
      parentPage: Record<string, unknown>
    },
  ): Promise<{ pending: DialogClosePendingFieldOnChange[]; properties: JsResultProperties }> {
    const emptyResult = () => ({
      pending: [] as DialogClosePendingFieldOnChange[],
      properties: emptyDialogCloseProperties(),
    })

    const scriptName =
      options.scriptPhase === 'dialogClosedNReturn'
        ? ScriptName.EVENT_DIALOG_CLOSED_N_RETURN
        : ScriptName.EVENT_DIALOG_CLOSED

    const seedData = this.collectDialogCloseSeedForPhase(
      options.scriptPhase,
      options.parentStoreKey,
      options.parentPage,
      String(options.closingDialogId ?? ''),
      options.dialogPageSnapshot,
      dependencies,
    )

    const sessionId =
      dialogConfig.onCloseDialogScriptSessionId != null
        ? String(dialogConfig.onCloseDialogScriptSessionId)
        : dependencies?.pageData?.sessionId != null
          ? String(dependencies.pageData.sessionId)
          : (options.parentPage as { sessionId?: unknown }).sessionId != null
            ? String((options.parentPage as { sessionId?: unknown }).sessionId)
            : undefined

    const actionElementId = Number(dialogConfig.onCloseDialogActionElementId)
    const elementId = Number.isFinite(actionElementId) && actionElementId > 0 ? actionElementId : Number(context.id)

    try {
      const currentData = createSafeClientData({
        fields: { fields: seedData },
      } as ExchangeData)

      const scriptResponse = await scriptService.executeScript({
        sessionId: sessionId ?? '',
        currentData,
        elementId: Number.isFinite(elementId) ? elementId : 0,
        level: ScriptLevel.ACTION,
        scriptName,
        scriptBody: scriptText,
      })

      const responseData = scriptResponse
      if (!responseData) return emptyResult()

      const states = normalizeExchangeStates(responseData)
      if (responseData.changed_data && options.parentStoreKey) {
        applyExchangeDataToAppStoreSlot(
          appStore,
          options.parentStoreKey,
          responseData.changed_data,
          'executeDialogCloseScriptOnMiddle',
          dependencies?.formProvider?.getElements as (() => Record<string, unknown>) | undefined,
          states,
        )
      }

      const pending: DialogClosePendingFieldOnChange[] = []
      if (responseData.changed_data && options.parentStoreKey) {
        const changedFields =
          (responseData.changed_data as ExchangeData).fields?.fields ?? {}
        if (changedFields && Object.keys(changedFields).length > 0) {
          const mergedPending = await this.applyDialogCloseResultToParent(
            changedFields as Record<string, unknown>,
            options.parentStoreKey,
            appStore,
            dependencies,
            dialogConfig,
            { deferFieldOnChange: true },
          )
          pending.push(...mergedPending)
        }
      }

      return {
        pending,
        properties: actionScript.extractJsResultProperties(undefined, responseData.properties),
      }
    } catch (error) {
      console.warn('[dialogClose] middle script execution failed:', error)
      return emptyResult()
    }
  }
}

export default ActionService

export const actionService = new ActionService()

/** Текст `event.dialogClosed` из метаданных action (для ActionContext). */
export { getDialogClosedScriptText as getActionDialogClosedScript } from '@/common/utils/dialogClosedScriptMeta'


